import React, { useState, useEffect, useMemo, useCallback, useRef, Component } from "react";
import { LineChart, Line, AreaChart, Area, ComposedChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, ReferenceDot } from "recharts";
// ---------------------------------------------------------------------------
// Error Boundary — catches render crashes and shows the error instead of blank
// ---------------------------------------------------------------------------
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[TabErrorBoundary]", error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ color: "#ff6b6b", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Component crashed</div>
          <div style={{ color: "#718096", fontSize: 12, marginBottom: 16, fontFamily: "monospace", whiteSpace: "pre-wrap", maxWidth: 600, margin: "0 auto 16px", textAlign: "left", background: "#0d1117", padding: 16, borderRadius: 6, border: "1px solid #1a2030" }}>
            {String(this.state.error?.message || this.state.error)}
          </div>
          <button onClick={() => this.setState({ hasError: false, error: null })} style={{ background: "#1a2030", color: "#e2e8f0", border: "1px solid #2d3f52", padding: "8px 20px", borderRadius: 4, cursor: "pointer", fontFamily: "monospace", fontSize: 12 }}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wallet link hover styles (guarded against hot-reload duplication)
if (!document.getElementById("wallet-link-styles")) {
  const _s = document.createElement("style");
  _s.id = "wallet-link-styles";
  _s.textContent = `.wallet-link{color:#4da2ff;font-size:11px;text-decoration:none;cursor:pointer;display:inline-block;padding:4px 0;margin:-4px 0;transition:color .15s}.wallet-link:hover{color:#7bc4ff;text-decoration:underline}`;
  document.head.appendChild(_s);
}

const API = "https://api.hlrisksqueezedata.com";
const TOKEN_KEY = "hlrisk_token";

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
function clearToken() { localStorage.removeItem(TOKEN_KEY); }

function isTokenValid() {
  const token = getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp > Date.now() / 1000;
  } catch { return false; }
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authFetch(url, opts = {}) {
  return fetch(url, { ...opts, headers: { ...opts.headers, ...authHeaders() } });
}

// Guard against 401 reload loops — max 2 reloads within 30s
const _401_KEY = "hlrisk_401_ts";
function handle401() {
  const now = Date.now();
  const prev = parseInt(sessionStorage.getItem(_401_KEY) || "0", 10);
  if (now - prev < 30000) {
    console.error("401 loop detected — not reloading");
    return;
  }
  sessionStorage.setItem(_401_KEY, String(now));
  clearToken();
  window.location.reload();
}
const DEFAULT_ASSETS = ["BTC", "ETH", "HYPE", "SOL", "XRP", "DOGE", "AVAX", "ARB", "SUI", "APT"];
const ASSET_COLORS = { BTC: "#f7931a", ETH: "#627eea", HYPE: "#b44dff", SOL: "#14f195", XRP: "#23292f", DOGE: "#c2a633", AVAX: "#e84142", ARB: "#28a0f0", SUI: "#4da2ff", APT: "#00d4aa", OP: "#ff0420", BNB: "#f3ba2f" };
const SCENARIOS = [
  { label: "-3%", pct: -0.03 }, { label: "-5%", pct: -0.05 }, { label: "-10%", pct: -0.10 },
  { label: "-15%", pct: -0.15 }, { label: "-20%", pct: -0.20 }, { label: "-25%", pct: -0.25 },
  { label: "+3%", pct: 0.03 }, { label: "+5%", pct: 0.05 }, { label: "+10%", pct: 0.10 },
  { label: "+15%", pct: 0.15 }, { label: "+20%", pct: 0.20 }, { label: "+25%", pct: 0.25 },
];

// ═══ Responsive hook — shared across all components ═══
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [breakpoint]);
  return mobile;
}

function fmt(n) {
  if (!n || isNaN(n)) return "$0";
  if (Math.abs(n) >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (Math.abs(n) >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toFixed(0);
}

function fmtSignedMoney(n) {
  if (!n || isNaN(n)) return "$0";
  return `${n > 0 ? "+" : "-"}${fmt(Math.abs(n))}`;
}

function fmtPct(n, digits = 1) {
  if (n == null || isNaN(n)) return "—";
  return `${n > 0 ? "+" : ""}${Number(n).toFixed(digits)}%`;
}

function normalizeScenarioBand(b) {
  const shockPct = Number(b?.shock_pct ?? b?.pct_change ?? 0);
  const targetPrice = Number(b?.target_price ?? b?.price ?? 0);
  const longNotional = Number(b?.long_notional_liquidated ?? 0);
  const shortNotional = Number(b?.short_notional_liquidated ?? 0);
  const totalMarginLost = Number(b?.total_margin_lost ?? b?.margin_lost ?? 0);
  const totalUnrealizedLoss = b?.unrealized_loss != null
    ? Number(b.unrealized_loss)
    : Number(b?.unrealized_loss_long ?? 0) + Number(b?.unrealized_loss_short ?? 0);
  return {
    ...b,
    shock_pct: shockPct,
    pct_change: shockPct,
    target_price: targetPrice,
    notional_liquidated: Number(b?.notional_liquidated ?? (longNotional + shortNotional)),
    long_notional_liquidated: longNotional,
    short_notional_liquidated: shortNotional,
    total_margin_lost: totalMarginLost,
    total_unrealized_loss: totalUnrealizedLoss,
    cascade_risk: b?.cascade_risk ?? b?.risk ?? "LOW",
    net_forced_flow: Number(b?.net_forced_flow ?? 0),
    incremental_flow: Number(b?.incremental_flow ?? 0),
    incremental_liq_notional: Number(b?.incremental_liq_notional ?? 0),
    concentration_top10_pct: Number(b?.concentration_top10_pct ?? 0),
    unique_accounts: Number(b?.unique_accounts ?? 0),
    dominant_cohort: b?.dominant_cohort || "—",
  };
}

function fmtTooltipPrice(p) {
  if (p == null || isNaN(p)) return "$—";
  const abs = Math.abs(p);
  const sign = p < 0 ? "-" : "";
  if (abs >= 10000) return sign + "$" + abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 100)   return sign + "$" + abs.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (abs >= 1)     return sign + "$" + abs.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return sign + "$" + abs.toFixed(4);
}

function fmtTooltipTime(ts, interval) {
  const d = new Date(ts);
  const mo = d.getMonth() + 1;
  const da = d.getDate();
  const hr = d.getHours();
  const mn = String(d.getMinutes()).padStart(2, "0");
  if (interval === "1d" || interval === "1w") return `${mo}/${da}`;
  return `${mo}/${da} ${hr}:${mn}`;
}

const VALID_ETH_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

function WalletLink({ address, display }) {
  const isValid = address && VALID_ETH_ADDRESS.test(address);
  if (!isValid) {
    return <span style={{ color: "#4da2ff", fontSize: 11, display: "inline-block", padding: "4px 0" }}>{display || "\u2014"}</span>;
  }
  return (
    <a
      className="wallet-link"
      href={`https://hypurrscan.io/address/${address}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={address}
      aria-label={`View ${display || address} on Hypurrscan`}
    >
      {display || "\u2014"}
    </a>
  );
}

function findNearestLevel(sortedLevels, target) {
  if (!sortedLevels || sortedLevels.length === 0) return null;
  let lo = 0, hi = sortedLevels.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sortedLevels[mid].price < target) lo = mid + 1;
    else hi = mid;
  }
  const candidates = [sortedLevels[lo]];
  if (lo > 0) candidates.push(sortedLevels[lo - 1]);
  let best = candidates[0], bestDist = Math.abs(best.price - target);
  for (let i = 1; i < candidates.length; i++) {
    const d = Math.abs(candidates[i].price - target);
    if (d < bestDist) { bestDist = d; best = candidates[i]; }
  }
  return { level: best, dist: bestDist };
}

function useApi(url, interval = 15000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetch_ = useCallback(() => {
    if (!url) return;
    const controller = new AbortController();
    setError(null);
    authFetch(url, { signal: controller.signal })
      .then(r => {
        if (r.status === 401) { handle401(); return null; }
        return r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));
      })
      .then(d => { if (d) setData(d); setLoading(false); })
      .catch(e => { if (e.name !== 'AbortError') { setError(e); setLoading(false); } });
    return controller;
  }, [url]);
  useEffect(() => {
    const controller = fetch_();
    const t = setInterval(fetch_, interval);
    return () => { clearInterval(t); if (controller) controller.abort(); };
  }, [fetch_, interval]);
  return { data, loading, error };
}

function StatCard({ label, value, sub, accent = "#e2e8f0" }) {
  return (
    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px", flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent, fontFamily: "'Space Mono', monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function CascadeBadge({ risk }) {
  const colors = { EXTREME: "#ff0000", HIGH: "#ff4444", MODERATE: "#ff9900", LOW: "#4dff91" };
  return (
    <span style={{ padding: "4px 12px", borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 2, background: (colors[risk] || "#4dff91") + "20", color: colors[risk] || "#4dff91", border: `1px solid ${colors[risk] || "#4dff91"}40` }}>
      {risk || "LOW"}
    </span>
  );
}

// ═══ Cross-exchange summary sub-components ═══

function TinyStackedShareBar({ shares }) {
  const total = shares.reduce((s, x) => s + (x.value || 0), 0);
  if (total <= 0) return null;
  return (
    <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: "#21262d", marginTop: 6 }}>
      {shares.map((s, i) => (
        <div key={i} title={`${s.label}: ${((s.value / total) * 100).toFixed(1)}%`}
          style={{ width: `${(s.value / total) * 100}%`, background: s.color, minWidth: s.value > 0 ? 2 : 0 }} />
      ))}
    </div>
  );
}

function RatioBar({ value, label, color = "#f0883e" }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8b949e", marginBottom: 2 }}>
        <span>{label}</span>
        <span>{pct}% L / {100 - pct}% S</span>
      </div>
      <div style={{ height: 10, background: "#21262d", borderRadius: 5, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 5 }} />
      </div>
    </div>
  );
}

function MirroredLiqBar({ leftValue, rightValue, maxValue, label, height = 14 }) {
  const leftPct = maxValue > 0 ? (Math.abs(leftValue || 0) / maxValue) * 100 : 0;
  const rightPct = maxValue > 0 ? (Math.abs(rightValue || 0) / maxValue) * 100 : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      {label && <div style={{ fontSize: 10, color: "#8b949e", marginBottom: 2 }}>{label}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", alignItems: "center", gap: 4 }}>
        <div style={{ height, background: "#1a2030", borderRadius: 4, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: `${Math.min(leftPct, 100)}%`, background: "#ff6b6b", borderRadius: 4 }} />
        </div>
        <div style={{ textAlign: "center", fontSize: 9, color: "#484f58", fontFamily: "'Space Mono', monospace" }}>vs</div>
        <div style={{ height, background: "#1a2030", borderRadius: 4, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(rightPct, 100)}%`, background: "#4dff91", borderRadius: 4 }} />
        </div>
      </div>
    </div>
  );
}

function PositioningSummaryCard({ oiData }) {
  if (!oiData) return <div style={{ padding: 12, color: "#8b949e", flex: 1 }}>Loading OI...</div>;
  const regime = oiData.regime || {};
  const regimeColors = { BUILD_LONGS: "#3fb950", BUILD_SHORTS: "#f85149", SHORT_COVER: "#58a6ff", LONG_FLUSH: "#f0883e", PRESSURE_BUILDING: "#d29922", UNWIND: "#8b949e", NEUTRAL: "#484f58" };
  const fmtUsd = (v) => { if (!v) return "$0"; if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`; if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`; return `$${Math.round(v).toLocaleString()}`; };
  const deltaChip = (val, label) => { const c = (val || 0) >= 0 ? "#3fb950" : "#f85149"; return <span key={label} style={{ fontSize: 10, color: c, marginRight: 8, fontFamily: "'Space Mono', monospace" }}>{label} {(val || 0) >= 0 ? "+" : ""}{fmtUsd(val)}</span>; };
  const exchanges = oiData.by_exchange || {};
  const shares = [{ label: "Binance", value: 0, color: "#f0b90b" }, { label: "Bybit", value: 0, color: "#f69c3d" }, { label: "OKX", value: 0, color: "#00c2ff" }];
  Object.entries(exchanges).forEach(([k, v]) => { if (k.startsWith("binance")) shares[0].value += v.oi_usd || 0; else if (k.startsWith("bybit")) shares[1].value += v.oi_usd || 0; else if (k.startsWith("okx")) shares[2].value += v.oi_usd || 0; });
  return (
    <div style={{ background: "#161b22", borderRadius: 8, padding: 12, border: "1px solid #30363d", flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", letterSpacing: 1 }}>Positioning</div>
        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: regimeColors[regime.label] || "#484f58", color: "#fff", fontWeight: 600 }}>{(regime.label || "\u2014").replace(/_/g, " ")}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: "#c9d1d9", fontFamily: "'Space Mono', monospace" }}>{fmtUsd(oiData.total_oi_usd)}</div>
      <div style={{ marginTop: 4 }}>{deltaChip(oiData.total_oi_delta_5m, "5m")}{deltaChip(Object.values(exchanges).reduce((s, v) => s + (v.oi_delta_15m || 0), 0), "15m")}</div>
      <TinyStackedShareBar shares={shares} />
      {oiData.funding_avg != null && (
        <div style={{ marginTop: 6, fontSize: 10, color: "#8b949e" }}>
          Funding: {(oiData.funding_avg * 100).toFixed(4)}% ({oiData.funding_avg_annualized != null ? `${(oiData.funding_avg_annualized * 100).toFixed(1)}% ann` : "\u2014"})
          {oiData.funding_spread_alert && <span style={{ marginLeft: 6, color: "#f0883e", fontWeight: 600 }}>{"\u26a0"} SPREAD</span>}
        </div>
      )}
    </div>
  );
}

function CrowdingSummaryCard({ lsData }) {
  if (!lsData) return <div style={{ padding: 12, color: "#8b949e", flex: 1 }}>Loading L/S...</div>;
  const retail = lsData.composite_long;
  const whaleData = (lsData.top_trader || {})["binance:top_trader_position"] || {};
  const whale = whaleData.long_ratio;
  const delta = retail != null && whale != null ? Math.round((retail - whale) * 100) : null;
  const deltaColor = delta != null && delta > 10 ? "#f0883e" : delta != null && delta < -10 ? "#58a6ff" : "#8b949e";
  return (
    <div style={{ background: "#161b22", borderRadius: 8, padding: 12, border: "1px solid #30363d", flex: 1 }}>
      <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Crowding</div>
      <RatioBar value={retail} label="Retail composite" color="#f0883e" />
      {delta != null && (
        <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, color: deltaColor, margin: "4px 0", fontFamily: "'Space Mono', monospace" }}>
          {delta > 0 ? "+" : ""}{delta} pts divergence
        </div>
      )}
      <RatioBar value={whale} label="Whale position" color="#58a6ff" />
      {lsData.by_exchange && (
        <div style={{ marginTop: 8, borderTop: "1px solid #21262d", paddingTop: 6 }}>
          <div style={{ fontSize: 10, color: "#484f58", marginBottom: 4 }}>Per exchange</div>
          {Object.entries(lsData.by_exchange).sort((a, b) => a[0].localeCompare(b[0])).map(([ex, v]) => (
            <RatioBar key={ex} value={v.long_ratio} label={ex} color="#8b949e" />
          ))}
          {lsData.top_trader && Object.entries(lsData.top_trader).map(([key, v]) => (
            <RatioBar key={key} value={v.long_ratio} label={key.replace("binance:", "").replace(/_/g, " ")} color="#6e40c9" />
          ))}
        </div>
      )}
    </div>
  );
}

function LiquidationPulseCard({ liqData }) {
  if (!liqData) return <div style={{ padding: 12, color: "#8b949e", flex: 1 }}>Loading liqs...</div>;
  const windows = liqData.windows || {};
  const w1m = windows["1m"] || {}; const w5m = windows["5m"] || {}; const w15m = windows["15m"] || {}; const w30m = windows["30m"] || {};
  const fmtUsd = (v) => { const a = Math.abs(v || 0); if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`; if (a >= 1e3) return `$${(a / 1e3).toFixed(0)}K`; return `$${a.toFixed(0)}`; };
  const maxVal = Math.max(Math.abs(w1m.long_liquidations_usd || 0), Math.abs(w1m.short_liquidations_usd || 0), Math.abs(w5m.long_liquidations_usd || 0), Math.abs(w5m.short_liquidations_usd || 0), 1000);
  const net5m = w5m.net_pressure || 0;
  const netColor = net5m > 0 ? "#ff6b6b" : net5m < 0 ? "#4dff91" : "#8b949e";
  const netLabel = net5m > 0 ? "Longs liquidating" : net5m < 0 ? "Shorts liquidating" : "Neutral";
  return (
    <div style={{ background: "#161b22", borderRadius: 8, padding: 12, border: "1px solid #30363d", flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", letterSpacing: 1 }}>Liquidation pulse</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: netColor, fontFamily: "'Space Mono', monospace" }}>{fmtUsd(net5m)} <span style={{ fontSize: 9, fontWeight: 400 }}>{netLabel}</span></div>
      </div>
      <MirroredLiqBar leftValue={w1m.long_liquidations_usd} rightValue={w1m.short_liquidations_usd} maxValue={maxVal} label="1m" />
      <MirroredLiqBar leftValue={w5m.long_liquidations_usd} rightValue={w5m.short_liquidations_usd} maxValue={maxVal} label="5m" />
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <span style={{ fontSize: 10, color: "#484f58", fontFamily: "'Space Mono', monospace" }}>15m {fmtUsd(w15m.net_pressure)}</span>
        <span style={{ fontSize: 10, color: "#484f58", fontFamily: "'Space Mono', monospace" }}>30m {fmtUsd(w30m.net_pressure)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2, fontSize: 9, color: "#484f58" }}><span>Long liqs {"\u2190"}</span><span>{"\u2192"} Short liqs</span></div>
    </div>
  );
}

function OverviewFlowStrip({ asset }) {
  const [aggOi, setAggOi] = React.useState(null);
  const [aggLs, setAggLs] = React.useState(null);
  const [aggLiq, setAggLiq] = React.useState(null);
  React.useEffect(() => {
    if (!asset) return;
    const fetchAll = async () => {
      try {
        const [oiRes, lsRes, liqRes] = await Promise.all([
          authFetch(`${API}/aggregated/oi/${asset}`),
          authFetch(`${API}/aggregated/ls_ratio/${asset}`),
          authFetch(`${API}/aggregated/liquidations/${asset}`),
        ]);
        if (oiRes.ok) setAggOi(await oiRes.json());
        if (lsRes.ok) setAggLs(await lsRes.json());
        if (liqRes.ok) setAggLiq(await liqRes.json());
      } catch (e) { console.warn("OverviewFlowStrip fetch error:", e); }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [asset]);
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 0 }}>
      <PositioningSummaryCard oiData={aggOi} />
      <CrowdingSummaryCard lsData={aggLs} />
      <LiquidationPulseCard liqData={aggLiq} />
    </div>
  );
}


// ═══ Overview V2: 6-Panel Grid ═══

const PANEL_STYLE = { background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: 12, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" };

function PanelShell({ title, right, accent, bg, children }) {
  const mob = useIsMobile();
  return (
    <div style={{ ...PANEL_STYLE, borderLeft: accent ? `2px solid ${accent}` : undefined, background: bg || PANEL_STYLE.background, ...(mob ? { overflow: "visible", minHeight: "auto" } : {}) }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, textTransform: "uppercase" }}>{title}</div>
        {right}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: mob ? "visible" : "hidden" }}>{children}</div>
    </div>
  );
}

function MiniBadge({ label, color = "#718096" }) {
  return <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: color + "22", color, fontWeight: 600, marginLeft: 4 }}>{label}</span>;
}

function MetricChip({ label, value, color = "#c9d1d9" }) {
  return <div style={{ display: "inline-flex", alignItems: "baseline", gap: 4, marginRight: 10 }}><span style={{ fontSize: 9, color: "#718096" }}>{label}</span><span style={{ fontSize: 12, color, fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>{value}</span></div>;
}

function TradeDecisionPanel({ ms, imb, currentPrice, modelA }) {
  const ec = ms?.entry_confidence || {};
  const v = ec.verdict || "\u2014";
  const ev = ec.ev_pct;
  const vc = { GO: "#3fb950", CAUTION: "#d29922", NO_GO: "#f85149" }[v] || "#718096";
  return (
    <PanelShell title="Trade decision" accent={vc} bg={vc + "08"} right={ec.oi_regime && ec.oi_regime !== "NEUTRAL" && <MiniBadge label={ec.oi_regime.replace(/_/g, " ")} color={ec.oi_regime.includes("LONG") || ec.oi_regime === "SHORT_COVER" ? "#3fb950" : "#f85149"} />}>
      {currentPrice > 0 && <div style={{ fontSize: 20, fontWeight: 600, color: "#e2e8f0", fontFamily: "'Space Mono', monospace", marginBottom: 6 }}>${Number(currentPrice).toLocaleString()}</div>}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 36, fontWeight: 700, color: vc, fontFamily: "'Space Mono', monospace" }}>{v}</span>
        {ev != null && <span style={{ fontSize: 18, color: ev >= 0 ? "#3fb950" : "#f85149", fontFamily: "'Space Mono', monospace" }}>EV {ev >= 0 ? "+" : ""}{ev.toFixed(2)}%</span>}
        {ec.rr != null && <span style={{ fontSize: 12, color: "#718096" }}>RR {ec.rr.toFixed(1)}:1</span>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginBottom: 6 }}>
        {ec.p_touch != null && <MetricChip label="P(touch)" value={(ec.p_touch * 100).toFixed(0) + "%"} />}
        {ec.p_follow != null && <MetricChip label="P(follow)" value={(ec.p_follow * 100).toFixed(0) + "%"} />}
        {ec.stop_pct != null && <MetricChip label="stop" value={ec.stop_pct.toFixed(1) + "%"} color="#f85149" />}
      </div>
      {ec.invalidation_price != null && <div style={{ fontSize: 10, color: "#718096", marginBottom: 4 }}>Invalidation: <span style={{ color: "#f85149", fontFamily: "'Space Mono', monospace" }}>${ec.invalidation_price.toLocaleString()}</span>{ec.primary_level_name && <span> {"\u2192"} {ec.primary_level_name} @ ${ec.primary_level_price?.toLocaleString()}</span>}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, fontSize: 9, color: "#718096" }}>
        {(imb?.direction || "BALANCED") !== "BALANCED" && <MiniBadge label={imb?.direction} color={imb?.direction?.includes("LONG") ? "#f85149" : "#3fb950"} />}
        {ec.liq_touch_mod != null && ec.liq_touch_mod !== 1.0 && <MiniBadge label={`liq ${ec.liq_touch_mod.toFixed(2)}x`} color="#58a6ff" />}
        {ec.funding_spread_alert && <MiniBadge label={"\u26a0 funding spread"} color="#f0883e" />}
      </div>
      <div style={{ marginTop: 6, fontSize: 9, color: "#484f58" }}>{ec.path_status && <span>path: {ec.path_status} </span>}{ec.approach_state && <span>{"\u2022"} {ec.approach_state} </span>}{ec.direction_aligned ? <span>{"\u2022"} aligned {ec.direction_30m}</span> : ec.direction_30m && <span>{"\u2022"} 30m={ec.direction_30m} 5m={ec.direction_5m}</span>}</div>
      {modelA && modelA.state && modelA.state !== "IDLE" && modelA.state !== "ERROR" && modelA.state !== "NO_DATA" && (
        <div style={{ marginTop: 8, padding: 8, background: "#0d1117", borderRadius: 6, border: "1px solid #1a2030" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: "#718096", textTransform: "uppercase", letterSpacing: 1 }}>Model A</span>
            <MiniBadge label={modelA.state.replace(/_/g, " ")} color={modelA.state === "ENTRY_READY" ? "#3fb950" : modelA.state === "AT_POI" || modelA.state === "OTF_CEASED" ? "#d29922" : modelA.state === "PULLING_BACK" ? "#58a6ff" : modelA.state === "FAKEOUT_DETECTED" ? "#f0883e" : modelA.state === "INVALIDATED" || modelA.state === "EXPIRED" ? "#f85149" : "#718096"} />
          </div>
          <div style={{ fontSize: 10, color: "#8b949e", marginBottom: 4 }}>{modelA.state_reason}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 9 }}>
            {modelA.bias?.trade_side && <span style={{ color: modelA.bias.trade_side === "SHORT" ? "#f85149" : "#3fb950", fontWeight: 600 }}>{modelA.bias.trade_side}</span>}
            {modelA.poi?.selected && (
              <span style={{ color: "#8b949e" }}>
                POI: {modelA.poi.selected.kind?.replace(/_/g, " ")} @ <span style={{ color: "#c9d1d9", fontFamily: "'Space Mono', monospace" }}>${Number(modelA.poi.selected.center).toLocaleString()}</span>
                <span style={{ color: "#718096" }}> ({modelA.poi.selected.confluence_score}pts)</span>
              </span>
            )}
            {modelA.fvgs && <span style={{ color: "#484f58" }}>FVGs: {modelA.fvgs["30m_active"]}{"\u00d7"}30m {modelA.fvgs["5m_active"]}{"\u00d7"}5m</span>}
          </div>
          {modelA.entry && modelA.entry.ready && (
            <div style={{ marginTop: 6, padding: 6, background: "#3fb95010", borderRadius: 4, border: "1px solid #3fb95030" }}>
              <div style={{ fontSize: 10, color: "#3fb950", fontWeight: 600, marginBottom: 2 }}>ENTRY PLAN READY</div>
              <div style={{ display: "flex", gap: 10, fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                <span style={{ color: "#c9d1d9" }}>Entry ${modelA.entry.entry_price?.toLocaleString()}</span>
                <span style={{ color: "#f85149" }}>Stop ${modelA.entry.stop_loss?.toLocaleString()}</span>
                <span style={{ color: "#3fb950" }}>T1 ${modelA.entry.target_1?.toLocaleString()}</span>
                <span style={{ color: "#718096" }}>Risk {modelA.entry.risk_pct}%</span>
              </div>
            </div>
          )}
        </div>
      )}
      {(() => {
        const evs = ms?.ev_sizing;
        if (!evs || evs.direction === "FLAT") return null;
        const dc = evs.direction === "LONG" ? "#3fb950" : evs.direction === "SHORT" ? "#f85149" : "#718096";
        const evPct = evs.EV_post != null ? (evs.EV_post * 100) : 0;
        return (
          <div style={{ marginTop: 8, padding: 8, background: "#0d1117", borderRadius: 6, border: "1px solid #1a2030" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 9, color: "#718096", letterSpacing: 1 }}>EV SIZING</span>
              <span style={{ fontSize: 11, color: dc, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{evs.direction} {(evs.target_inventory * 100).toFixed(0)}%</span>
            </div>
            <div style={{ fontSize: 9, color: "#4a5568", fontFamily: "'Space Mono', monospace" }}>
              base={evs.score_core?.toFixed(3)} {"\u00d7"} str={evs.structure_scale}x {"\u00d7"} mA={evs.model_a_scale}x
              {evs.conflict_penalty > 0 && <span style={{ color: "#d29922" }}> {"\u2212"} conflict={evs.conflict_penalty.toFixed(3)}</span>}
            </div>
            <div style={{ fontSize: 9, color: "#4a5568" }}>
              caps: max={evs.caps?.max} ls={evs.caps?.ls} str={evs.caps?.structure}
            </div>
          </div>
        );
      })()}
    </PanelShell>
  );
}

function StructureRow({ tf, dir, prot, ext, badges = [] }) {
  const dc = dir === "UP" ? "#3fb950" : dir === "DOWN" ? "#f85149" : "#718096";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13 }}>
      <span style={{ width: 30, color: "#718096", fontWeight: 600 }}>{tf}</span>
      <span style={{ color: dc, fontWeight: 700, width: 46, fontSize: 14 }}>{dir || "\u2014"}</span>
      <span style={{ color: "#c9d1d9", fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 600 }}>prot ${prot ? Number(prot).toLocaleString() : "\u2014"}</span>
      <span style={{ color: "#484f58", fontFamily: "'Space Mono', monospace", fontSize: 10 }}>ext ${ext ? Number(ext).toLocaleString() : "\u2014"}</span>
      {badges.map((b, i) => <MiniBadge key={i} label={b.label} color={b.color} />)}
    </div>
  );
}

function MTFStructurePanel({ ms, ms4h }) {
  const s5 = ms?.structure_5m || {};
  const int30 = ms?.structure_30m_internal || {};
  const dir30 = ms?.ms_direction;
  const dir4h = ms4h?.ms_direction;
  const align = dir30 && dir4h && dir30 !== "NO_BOS" && dir4h !== "NO_BOS" ? (dir30 === dir4h ? "ALIGNED" : "CONFLICTING") : null;
  return (
    <PanelShell title="Structure" right={align && <MiniBadge label={align} color={align === "ALIGNED" ? "#3fb950" : "#f85149"} />}>
      <StructureRow tf="4H" dir={dir4h} prot={ms4h?.confirmed_protected_price} ext={ms4h?.last_structural_extreme_price} />
      <StructureRow tf="30m" dir={dir30} prot={ms?.confirmed_protected_price} ext={ms?.last_structural_extreme_price} badges={int30.against_30m_external ? [{ label: "INT counter", color: "#f0883e" }] : []} />
      <StructureRow tf="5m" dir={s5.ms_direction} prot={s5.confirmed_protected_price} ext={s5.last_structural_extreme_price} badges={s5.structure_event && s5.structure_event !== "NONE" ? [{ label: s5.structure_event.replace(/_/g, " "), color: "#58a6ff" }] : []} />
      <div style={{ marginTop: 8, fontSize: 11, color: "#8b949e", fontWeight: 500 }}>
        {dir30 === "DOWN" && ms?.confirmed_protected_price && <span>BOS flip UP above <span style={{ color: "#3fb950", fontFamily: "'Space Mono', monospace" }}>${Number(ms.confirmed_protected_price).toLocaleString()}</span></span>}
        {dir30 === "UP" && ms?.last_structural_extreme_price && <span>BOS flip DOWN below <span style={{ color: "#f85149", fontFamily: "'Space Mono', monospace" }}>${Number(ms.last_structural_extreme_price).toLocaleString()}</span></span>}
      </div>
      {(() => {
        const cvd = ms?.cvd_divergence_new || {};
        const tfs = ["5m", "30m"];
        const hasAny = tfs.some(tf => cvd[tf]?.strongest || (cvd[tf]?.confirmed_divergences || []).length > 0);
        if (!hasAny) return null;
        return (
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid #1a2030" }}>
            <div style={{ fontSize: 9, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>CVD Divergence</div>
            {tfs.map(tf => {
              const d = cvd[tf];
              if (!d) return null;
              const strongest = d.strongest;
              const confirmed = d.confirmed_divergences || [];
              const live = d.live || {};
              if (!strongest && confirmed.length === 0 && live.status === "NONE") return null;
              const hasDiv = strongest || confirmed.length > 0;
              return (
                <div key={tf} style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, color: "#8b949e", fontWeight: 600, width: 28 }}>{tf}</span>
                    {hasDiv ? (
                      <MiniBadge label={strongest?.type?.toUpperCase() || "DIVERGENCE"} color={strongest?.type === "bullish" ? "#3fb950" : strongest?.type === "bearish" ? "#f85149" : "#d29922"} />
                    ) : (
                      <span style={{ fontSize: 9, color: "#484f58" }}>{live.status || "none"}</span>
                    )}
                    {strongest?.status && <span style={{ fontSize: 9, color: "#484f58" }}>{strongest.status}</span>}
                  </div>
                  {confirmed.map((c, i) => (
                    <div key={i} style={{ marginTop: 3, marginLeft: 34, fontSize: 9, color: "#8b949e" }}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <span>A: <span style={{ color: "#c9d1d9", fontFamily: "'Space Mono', monospace" }}>${Number(c.pivot_a?.price || 0).toLocaleString()}</span> <span style={{ color: "#484f58" }}>{c.pivot_a?.label}</span> cvd=<span style={{ color: (c.pivot_a?.cvd || 0) >= 0 ? "#3fb950" : "#f85149", fontFamily: "'Space Mono', monospace" }}>{Math.round(c.pivot_a?.cvd || 0).toLocaleString()}</span></span>
                        <span>B: <span style={{ color: "#c9d1d9", fontFamily: "'Space Mono', monospace" }}>${Number(c.pivot_b?.price || 0).toLocaleString()}</span> <span style={{ color: "#484f58" }}>{c.pivot_b?.label}</span> cvd=<span style={{ color: (c.pivot_b?.cvd || 0) >= 0 ? "#3fb950" : "#f85149", fontFamily: "'Space Mono', monospace" }}>{Math.round(c.pivot_b?.cvd || 0).toLocaleString()}</span></span>
                      </div>
                      <div style={{ marginTop: 1 }}>
                        <span style={{ color: c.type === "bullish" ? "#3fb950" : "#f85149" }}>{c.type}</span>
                        <span style={{ color: "#484f58" }}> {"\u0394"}cvd={Math.round(c.cvd_delta || 0).toLocaleString()}</span>
                        {c.coverage && !c.coverage.reliable && <span style={{ color: "#f0883e" }}> {"\u26a0"} gap</span>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })()}
      {(() => {
        const v2 = ms?.cvd_divergence_v2;
        if (!v2 || !v2.timeframes) return null;
        const srcLabels = {binance_spot: "BNSP", binance_perp: "BNPRP", hyperliquid_perp: "HL"};
        const srcOrder = ["binance_spot", "binance_perp", "hyperliquid_perp"];
        const divColor = t => t === "bullish" ? "#3fb950" : t === "bearish" ? "#f85149" : "#484f58";
        return (
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a2030" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 4 }}>CVD SOURCES</div>
            {["5m", "30m"].map(tf => {
              const row = v2.timeframes[tf];
              if (!row?.agreement) return null;
              const a = row.agreement;
              return (
                <div key={tf} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3, fontSize: 10 }}>
                  <span style={{ color: "#718096", width: 24, fontFamily: "'Space Mono', monospace" }}>{tf}</span>
                  <span style={{ color: divColor(a.type), fontWeight: 700, fontSize: 10 }}>{(a.type || "NONE").toUpperCase()}</span>
                  <span style={{ color: "#4a5568", fontFamily: "'Space Mono', monospace", fontSize: 9 }}>({a.agree_count}/{a.active_source_count})</span>
                  {srcOrder.map(id => {
                    const s = row.sources?.[id];
                    if (!s || s.status === "NO_DATA" || s.status === "OFFLINE") return null;
                    const match = s.type === a.type && a.type;
                    return (
                      <span key={id} style={{ fontSize: 9, color: s.status === "DEGRADED" ? "#d29922" : match ? "#3fb950" : s.type ? "#f85149" : "#484f58", fontFamily: "monospace" }}>
                        {srcLabels[id] || id}{match ? "\u2713" : s.status === "DEGRADED" ? "?" : s.type ? "\u2717" : "\u2014"}
                      </span>
                    );
                  })}
                </div>
              );
            })}
            {v2.source_status && (
              <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                {srcOrder.map(id => {
                  const st = v2.source_status[id];
                  if (!st) return null;
                  const c = st.continuity === "LIVE" ? "#3fb950" : st.continuity === "DEGRADED" ? "#d29922" : st.continuity === "STALE" ? "#f0883e" : "#484f58";
                  return <span key={id} style={{ fontSize: 8, color: c }}>{srcLabels[id] || id}:{st.continuity}</span>;
                })}
              </div>
            )}
          </div>
        );
      })()}
    </PanelShell>
  );
}

function LocationPanel({ ms }) {
  const levels = ms?.structural_proximity || [];
  const primary = levels[0] || null;
  const secondary = levels.find((l, i) => i > 0 && primary && l.break_direction !== primary.break_direction) || levels[1] || null;
  const show = [primary, secondary].filter(Boolean);
  if (!show.length) return <PanelShell title="Location"><div style={{ color: "#484f58", fontSize: 11 }}>No structural levels nearby</div></PanelShell>;
  return (
    <PanelShell title="Location">
      {show.map((lv, i) => {
        const dist = lv.distance_pct;
        const dc = Math.abs(dist) < 0.5 ? "#f85149" : Math.abs(dist) < 1.5 ? "#d29922" : "#3fb950";
        return (
          <div key={i} style={{ marginBottom: i < show.length - 1 ? 10 : 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: "#c9d1d9", fontWeight: 600 }}>{lv.name?.replace(/_/g, " ")}</span>
              <span style={{ fontSize: 14, color: dc, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>{dist >= 0 ? "+" : ""}{dist?.toFixed(2)}%</span>
            </div>
            <div style={{ display: "flex", gap: 8, fontSize: 10, color: "#8b949e" }}>
              <span>${Number(lv.price).toLocaleString()}</span>
              {lv.approach_state && <MiniBadge label={lv.approach_state} color={lv.approach_state === "APPROACHING" || lv.approach_state === "ACCELERATING" ? "#d29922" : "#718096"} />}
              {lv.confluence && <MiniBadge label={lv.confluence} color="#58a6ff" />}
            </div>
            {lv.liq_fmt && <div style={{ fontSize: 9, color: "#484f58", marginTop: 2 }}>Liq: {lv.liq_fmt}</div>}
          </div>
        );
      })}
    </PanelShell>
  );
}

function FlowPulsePanel({ aggOi, aggLs, aggLiq, mmBias, whaleBias, positioning }) {
  const regime = aggOi?.regime || {};
  const rc = { BUILD_LONGS: "#3fb950", BUILD_SHORTS: "#f85149", SHORT_COVER: "#58a6ff", LONG_FLUSH: "#f0883e", PRESSURE_BUILDING: "#d29922", UNWIND: "#8b949e", NEUTRAL: "#484f58" };
  const fu = (v) => { if (!v) return "$0"; const a = Math.abs(v); if (a >= 1e9) return `$${(a/1e9).toFixed(1)}B`; if (a >= 1e6) return `$${(a/1e6).toFixed(0)}M`; if (a >= 1e3) return `$${(a/1e3).toFixed(0)}K`; return `$${a.toFixed(0)}`; };
  const retail = aggLs?.composite_long;
  const whale = (aggLs?.top_trader || {})["binance:top_trader_position"]?.long_ratio;
  const cd = retail != null && whale != null ? Math.round((retail - whale) * 100) : null;
  const w5m = (aggLiq?.windows || {})["5m"] || {};
  const net5 = w5m.net_pressure || 0;
  const nc = net5 > 0 ? "#ff6b6b" : net5 < 0 ? "#4dff91" : "#718096";
  return (
    <PanelShell title="Flow pulse" right={<MiniBadge label={(regime.label || "NEUTRAL").replace(/_/g, " ")} color={rc[regime.label] || "#484f58"} />}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: "#c9d1d9", fontFamily: "'Space Mono', monospace" }}>{fu(aggOi?.total_oi_usd)}</span>
        <span style={{ fontSize: 10, color: (aggOi?.total_oi_delta_5m || 0) >= 0 ? "#3fb950" : "#f85149", fontFamily: "'Space Mono', monospace" }}>5m {(aggOi?.total_oi_delta_5m || 0) >= 0 ? "+" : ""}{fu(aggOi?.total_oi_delta_5m)}</span>
      </div>
      <div style={{ marginBottom: 6 }}>
        {(() => {
          const a = aggLs?.analytics;
          const zColor = (z) => Math.abs(z) >= 3.0 ? "#f85149" : Math.abs(z) >= 2.5 ? "#d29922" : Math.abs(z) >= 1.5 ? "#e3b341" : "#8b949e";
          const regColor = (r) => r?.includes("EXTREME") ? "#f85149" : r?.includes("HIGH") ? "#d29922" : r?.includes("ELEVATED") ? "#e3b341" : "#484f58";
          return (
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: "#718096", textTransform: "uppercase", letterSpacing: 1 }}>L/S Ratio</span>
                {cd != null && Math.abs(cd) >= 5 && <span style={{ fontSize: 9, fontWeight: 600, color: Math.abs(cd) >= 10 ? "#f0883e" : "#8b949e", fontFamily: "'Space Mono', monospace" }}>{cd > 0 ? "+" : ""}{cd}pts R/W div</span>}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: "#c9d1d9" }}>
                  {a?.raw_ratio?.toFixed(3) || (retail != null ? (retail / Math.max(1 - retail, 0.001)).toFixed(3) : "\u2014")}
                </span>
                {a?.status === "active" && (
                  <>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: zColor(a.z_score) }}>
                      {a.z_score > 0 ? "+" : ""}{a.z_score.toFixed(1)}{"\u03C3"}
                    </span>
                    {a.regime !== "NORMAL" && (
                      <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: regColor(a.regime) + "20", color: regColor(a.regime), fontWeight: 600 }}>
                        {a.regime.replace(/_/g, " ")}
                      </span>
                    )}
                  </>
                )}
              </div>
              {a?.status === "active" && (
                <div style={{ fontSize: 9, color: "#484f58", fontFamily: "'Space Mono', monospace", marginBottom: 3 }}>
                  {"mean=" + a.mean?.toFixed(3) + " \u2022 p" + a.percentile?.toFixed(0) + " \u2022 " + a.samples + " samples"}
                  {a.contrarian_direction && (
                    <span style={{ color: a.contrarian_direction === "LONG" ? "#3fb950" : "#f85149", marginLeft: 6, fontWeight: 600 }}>
                      {"\u2192"} {a.contrarian_direction} signal
                    </span>
                  )}
                </div>
              )}
              {a?.status === "warming_up" && (
                <div style={{ fontSize: 9, color: "#484f58" }}>Warming up ({a.samples || 0} samples)...</div>
              )}
            </div>
          );
        })()}
        <RatioBar value={retail} label="Retail composite" color="#f0883e" />
        <RatioBar value={whale} label="Whale position" color="#58a6ff" />
        {aggLs?.by_exchange && <div style={{ marginTop: 3 }}>
          {Object.entries(aggLs.by_exchange).sort((a, b) => a[0].localeCompare(b[0])).map(([ex, v]) => <RatioBar key={ex} value={v.long_ratio} label={ex} color="#8b949e" />)}
        </div>}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10 }}>
        <span style={{ color: "#8b949e" }}>Liqs</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: nc, fontFamily: "'Space Mono', monospace" }}>5m {fu(net5)} {net5 > 0 ? "longs" : net5 < 0 ? "shorts" : ""}</span>
      </div>
      {aggOi?.funding_avg != null && <div style={{ marginTop: 4, fontSize: 9, color: "#484f58" }}>Funding {(aggOi.funding_avg * 100).toFixed(4)}%{aggOi.funding_spread_alert && <span style={{ color: "#f0883e" }}> {"\u26a0"} spread</span>}</div>}
      {(mmBias?.accounts_with_position > 0 || whaleBias?.whales_with_position > 0) && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a2030" }}>
          <div style={{ fontSize: 9, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Smart Money</div>
          {mmBias && mmBias.accounts_with_position > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
              <span style={{ fontSize: 10, color: "#8b949e" }}>MM bias</span>
              <span style={{ fontSize: 11, fontWeight: 600, fontFamily: "'Space Mono', monospace", color: mmBias.weighted_bias > 0.1 ? "#3fb950" : mmBias.weighted_bias < -0.1 ? "#f85149" : "#718096" }}>
                {mmBias.weighted_bias > 0 ? "+" : ""}{(mmBias.weighted_bias * 100).toFixed(0)}% {mmBias.weighted_bias > 0 ? "LONG" : mmBias.weighted_bias < 0 ? "SHORT" : "FLAT"}
              </span>
            </div>
          )}
          {mmBias && mmBias.accounts_with_position > 0 && (
            <div style={{ fontSize: 9, color: "#484f58", marginBottom: 4 }}>
              {mmBias.accounts_with_position} accts {"\u2022"} L ${(mmBias.gross_long_usd / 1e6).toFixed(1)}M / S ${(mmBias.gross_short_usd / 1e6).toFixed(1)}M
              {mmBias.mm_bias_delta_1h != null && (
                <span style={{ color: mmBias.mm_bias_delta_1h > 0 ? "#3fb950" : mmBias.mm_bias_delta_1h < 0 ? "#f85149" : "#484f58" }}>
                  {" \u2022 \u0394"}1h {mmBias.mm_bias_delta_1h > 0 ? "+" : ""}{(mmBias.mm_bias_delta_1h * 100).toFixed(0)}pts
                </span>
              )}
            </div>
          )}
          {whaleBias && whaleBias.whales_with_position > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
              <span style={{ fontSize: 10, color: "#8b949e" }}>Whale bias</span>
              <span style={{ fontSize: 11, fontWeight: 600, fontFamily: "'Space Mono', monospace", color: whaleBias.weighted_bias > 0.1 ? "#3fb950" : whaleBias.weighted_bias < -0.1 ? "#f85149" : "#718096" }}>
                {whaleBias.weighted_bias > 0 ? "+" : ""}{(whaleBias.weighted_bias * 100).toFixed(0)}% {whaleBias.weighted_bias > 0 ? "LONG" : whaleBias.weighted_bias < 0 ? "SHORT" : "FLAT"}
              </span>
            </div>
          )}
          {whaleBias && whaleBias.whales_with_position > 0 && (
            <div style={{ fontSize: 9, color: "#484f58" }}>
              {whaleBias.whales_with_position} whales {"\u2022"} L ${(whaleBias.gross_long_usd / 1e6).toFixed(1)}M / S ${(whaleBias.gross_short_usd / 1e6).toFixed(1)}M
            </div>
          )}
          {mmBias?.accounts_with_position > 0 && whaleBias?.whales_with_position > 0 && (() => {
            const mmDir = mmBias.weighted_bias > 0.1 ? "LONG" : mmBias.weighted_bias < -0.1 ? "SHORT" : "FLAT";
            const whaleDir = whaleBias.weighted_bias > 0.1 ? "LONG" : whaleBias.weighted_bias < -0.1 ? "SHORT" : "FLAT";
            const divergent = mmDir !== whaleDir && mmDir !== "FLAT" && whaleDir !== "FLAT";
            if (!divergent) return null;
            return (
              <div style={{ marginTop: 4, padding: "3px 6px", background: "#f0883e15", borderRadius: 3, border: "1px solid #f0883e30", fontSize: 9, color: "#f0883e" }}>
                {"\u26a0"} MM/Whale divergence: MMs {mmDir} vs Whales {whaleDir}
              </div>
            );
          })()}
        </div>
      )}
      {positioning && positioning.regime && positioning.regime !== "MIXED" && (
        <div style={{ marginTop: 6, padding: 8, background: "#0d1117", borderRadius: 6, border: "1px solid #1a2030" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
            <span style={{ fontSize: 9, color: "#718096", letterSpacing: 1 }}>POSITIONING</span>
            <span style={{ fontSize: 9, color: positioning.confidence === "HIGH" ? "#3fb950" : positioning.confidence === "MEDIUM" ? "#d29922" : "#484f58", fontWeight: 600 }}>{positioning.confidence}{positioning.persistence === "EMERGING" ? " emerging" : ""}</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 3, color: ({TRAPPED_SHORTS:"#3fb950",TRAPPED_LONGS:"#f85149",LONG_BUILD:"#3fb950",SHORT_BUILD:"#f85149",SHORT_COVERING:"#58a6ff",LONG_LIQUIDATION:"#d29922",COILING:"#8b949e",RISK_OFF_UNWIND:"#484f58"})[positioning.regime] || "#8b949e" }}>
            {positioning.display_regime}
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {(positioning.chips || []).map((chip, i) => (
              <span key={i} style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "#161b22", color: "#8b949e", border: "1px solid #1a2030" }}>{chip}</span>
            ))}
          </div>
        </div>
      )}
    </PanelShell>
  );
}

function BookmapTriggerPanel({ bt }) {
  if (!bt) return <PanelShell title="Bookmap trigger"><div style={{ color: "#484f58", fontSize: 11 }}>Loading...</div></PanelShell>;
  const abs = bt.absorption || {}; const tape = bt.tape || {}; const walls = bt.walls || {}; const cvd = bt.cvd || {};
  const bc = abs.bias === "bid" ? "#3fb950" : abs.bias === "ask" ? "#f85149" : "#718096";
  const tc = tape.state === "QUIET" ? "#718096" : tape.state?.includes("BUY") ? "#3fb950" : tape.state?.includes("SELL") ? "#f85149" : "#d29922";
  return (
    <PanelShell title="Bookmap trigger" right={bt.stale ? <MiniBadge label="STALE" color="#f85149" /> : <MiniBadge label="LIVE" color="#3fb950" />}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 10 }}>
        <div><span style={{ color: "#8b949e" }}>Absorption </span><span style={{ color: bc, fontWeight: 600 }}>{abs.bias?.toUpperCase()}</span><span style={{ color: "#484f58", fontSize: 9 }}> bid {abs.bid_score} / ask {abs.ask_score}</span></div>
        <div><span style={{ color: "#8b949e" }}>Tape </span><span style={{ color: tc, fontWeight: 600 }}>{tape.state}</span></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 10 }}>
        <div><span style={{ color: "#8b949e" }}>Overhead </span>{walls.nearest_overhead ? <span style={{ color: "#f85149", fontFamily: "'Space Mono', monospace" }}>${Number(walls.nearest_overhead.price).toLocaleString()} ({walls.nearest_overhead.dist_pct}%)</span> : <span style={{ color: "#484f58" }}>{"\u2014"}</span>}</div>
        <div><span style={{ color: "#8b949e" }}>Support </span>{walls.nearest_support ? <span style={{ color: "#3fb950", fontFamily: "'Space Mono', monospace" }}>${Number(walls.nearest_support.price).toLocaleString()} ({walls.nearest_support.dist_pct}%)</span> : <span style={{ color: "#484f58" }}>{"\u2014"}</span>}</div>
      </div>
      <div style={{ display: "flex", gap: 10, fontSize: 9, color: "#718096" }}>
        <span>Walls: <span style={{ color: walls.wall_imbalance === "OVERHEAD_HEAVY" ? "#f85149" : walls.wall_imbalance === "SUPPORT_HEAVY" ? "#3fb950" : "#718096" }}>{walls.wall_imbalance}</span></span>
        <span>CVD: <span style={{ color: (cvd.composite || 0) >= 0 ? "#3fb950" : "#f85149", fontFamily: "'Space Mono', monospace" }}>{(cvd.composite||0) >= 0 ? "+" : ""}{cvd.composite}</span></span>
      </div>
    </PanelShell>
  );
}

function PositionStatusPanel({ imb, ms }) {
  const ec = ms?.entry_confidence || {};
  const sq = imb?.direction || "BALANCED";
  return (
    <PanelShell title="Position">
      <div style={{ fontSize: 16, fontWeight: 600, color: "#718096", marginBottom: 8 }}>FLAT</div>
      <MiniBadge label={ec.verdict === "CAUTION" ? "WATCH FLOW" : ec.verdict === "GO" ? "ENTRY READY" : "WAIT FOR SETUP"} color="#58a6ff" />
      {sq !== "BALANCED" && <div style={{ marginTop: 8, fontSize: 10, color: "#8b949e" }}>Squeeze lean: <span style={{ color: sq.includes("LONG") ? "#f85149" : "#3fb950" }}>{sq}</span></div>}
      {ec.reason && <div style={{ marginTop: 4, fontSize: 9, color: "#484f58" }}>{ec.reason}</div>}
    </PanelShell>
  );
}

function ProximitySignalPanel({ asset }) {
  const mob = useIsMobile();
  const { data: prox } = useApi(API + "/proximity/latest/" + asset, 30000);
  const [expanded, setExpanded] = useState(false);

  if (!prox || prox.status === "no_data") {
    return (
      <div style={{ ...PANEL_STYLE, borderLeft: "2px solid #4a5568" }}>
        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>PROXIMITY SIGNAL</div>
        <div style={{ fontSize: 13, color: "#4a5568", marginTop: 8 }}>Waiting for data...</div>
      </div>
    );
  }

  const z = prox.raw_b2_robust_z;
  const extreme = prox.raw_b2_extreme;
  const direction = prox.raw_b2_signal_direction;
  const ratio = prox.raw_b2_imbalance_ratio || 0;
  const b2Total = prox.raw_b2_total_notional_usd || 0;
  const b2Long = prox.raw_b2_long_total_usd || 0;
  const b2Short = prox.raw_b2_short_total_usd || 0;
  const b1Z = prox.raw_b1_robust_z;
  const b1Ratio = prox.raw_b1_imbalance_ratio || 0;
  const lastInfo = prox.last_extreme_info || {};
  const zHist = prox.raw_b2_z_history || [];

  // Signal age
  const now = Date.now() / 1000;
  const lastTs = lastInfo.ts;
  const lastDir = lastInfo.direction;
  const lastPrice = lastInfo.price;
  let signalAge = null;
  let signalFresh = false;
  if (lastTs) {
    const ageSec = now - lastTs;
    signalFresh = ageSec < 14400; // 4h
    const h = Math.floor(ageSec / 3600);
    const m = Math.floor((ageSec % 3600) / 60);
    signalAge = h > 0 ? `${h}h ${m}m ago` : `${m}m ago`;
  }

  // Border color: active signal within 4h
  const signalColor = lastDir === "LONG" ? "#4dff91" : lastDir === "SHORT" ? "#ff6b6b" : "#4a5568";
  const borderColor = signalFresh ? signalColor : "#4a5568";
  const bgTint = signalFresh ? signalColor + "08" : PANEL_STYLE.background;

  // Sparkline data
  const sparkData = zHist.map((v, i) => ({ i, z: v }));

  // Ratio bar width
  const ratioPct = Math.min(Math.abs(ratio) * 100, 100);
  const ratioSide = ratio >= 0 ? "right" : "left";

  return (
    <div style={{ ...PANEL_STYLE, borderLeft: `2px solid ${borderColor}`, background: bgTint }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>PROXIMITY SIGNAL — RAW B2</div>
        {signalFresh && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3, background: signalColor + "22", color: signalColor, fontWeight: 600 }}>ACTIVE</span>}
      </div>

      {/* Top: direction + z-score */}
      <div style={{ display: "flex", alignItems: mob ? "flex-start" : "center", gap: mob ? 8 : 16, flexDirection: mob ? "column" : "row", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {extreme && direction ? (
            <div style={{ fontSize: mob ? 28 : 36, fontWeight: 800, color: direction === "LONG" ? "#4dff91" : "#ff6b6b", fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
              {direction === "LONG" ? "\u2191" : "\u2193"} {direction}
            </div>
          ) : (
            <div style={{ fontSize: mob ? 20 : 24, fontWeight: 700, color: "#4a5568", fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>NEUTRAL</div>
          )}
          <div style={{ fontSize: mob ? 22 : 28, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
            {z != null ? (z >= 0 ? "+" : "") + z.toFixed(2) : "—"}
          </div>
        </div>
        {lastTs && (
          <div style={{ fontSize: 10, color: signalFresh ? signalColor : "#718096" }}>
            Signal: {lastDir} {signalAge}{lastPrice ? ` at $${Number(lastPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}
          </div>
        )}
      </div>

      {/* Middle: ratio bar + notional */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 9, color: "#718096", width: 50 }}>RATIO</span>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#1a2030", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: 1, background: "#30363d" }} />
            <div style={{
              position: "absolute", top: 0, bottom: 0, borderRadius: 4,
              ...(ratioSide === "right"
                ? { left: "50%", width: ratioPct / 2 + "%", background: "#4dff91" }
                : { right: "50%", width: ratioPct / 2 + "%", background: "#ff6b6b" }),
            }} />
          </div>
          <span style={{ fontSize: 10, color: ratio >= 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace", minWidth: 48, textAlign: "right" }}>
            {ratio >= 0 ? "+" : ""}{ratio.toFixed(3)}
          </span>
        </div>
        {mob ? (
          <div>
            <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", color: "#58a6ff", fontSize: 10, cursor: "pointer", padding: 0, fontFamily: "monospace" }}>
              {expanded ? "Hide details \u25B2" : "Show details \u25BC"}
            </button>
            {expanded && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10, color: "#8b949e" }}>
                  Band 2: {fmt(b2Total)} — <span style={{ color: "#4dff91" }}>L {fmt(b2Long)}</span> / <span style={{ color: "#ff6b6b" }}>S {fmt(b2Short)}</span>
                </div>
                <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                  Near Band: z={b1Z != null ? b1Z.toFixed(2) : "—"} ratio={b1Ratio.toFixed(3)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 10, color: "#8b949e" }}>
              Band 2: {fmt(b2Total)} — <span style={{ color: "#4dff91" }}>L {fmt(b2Long)}</span> / <span style={{ color: "#ff6b6b" }}>S {fmt(b2Short)}</span>
            </div>
            <div style={{ fontSize: 10, color: "#718096" }}>
              Near Band: z={b1Z != null ? b1Z.toFixed(2) : "—"} ratio={b1Ratio.toFixed(3)}
            </div>
          </div>
        )}
      </div>

      {/* Bottom: sparkline */}
      {sparkData.length > 2 && (
        <div style={{ flex: 1, minHeight: mob ? 60 : 50 }}>
          <ResponsiveContainer width="100%" height={mob ? 60 : 50}>
            <AreaChart data={sparkData} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
              <defs>
                <linearGradient id="proxZGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#58a6ff" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#58a6ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="z" stroke="#58a6ff" strokeWidth={1.5} fill="url(#proxZGrad)" dot={false} isAnimationActive={false} />
              <ReferenceLine y={1.5} stroke="#ff6b6b" strokeDasharray="3 3" strokeWidth={0.8} />
              <ReferenceLine y={-1.5} stroke="#4dff91" strokeDasharray="3 3" strokeWidth={0.8} />
              <ReferenceLine y={0} stroke="#30363d" strokeWidth={0.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function ProximityRatioBar({ label, ratio, longVal, shortVal, total, count, muted }) {
  const ratioPct = Math.min(Math.abs(ratio || 0) * 100, 100);
  const side = (ratio || 0) >= 0 ? "right" : "left";
  const labelColor = muted ? "#4a5568" : "#718096";
  return (
    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "12px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 9, color: labelColor, letterSpacing: 2 }}>{label}</div>
        <div style={{ fontSize: 10, color: "#4a5568" }}>{count} positions</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1, height: 10, borderRadius: 5, background: "#1a2030", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: 1, background: "#30363d" }} />
          <div style={{
            position: "absolute", top: 0, bottom: 0, borderRadius: 5,
            ...(side === "right"
              ? { left: "50%", width: ratioPct / 2 + "%", background: "#4dff91" }
              : { right: "50%", width: ratioPct / 2 + "%", background: "#ff6b6b" }),
          }} />
        </div>
        <span style={{ fontSize: 11, color: (ratio || 0) >= 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace", minWidth: 55, textAlign: "right", fontWeight: 600 }}>
          {(ratio || 0) >= 0 ? "+" : ""}{(ratio || 0).toFixed(4)}
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
        <span style={{ color: "#4dff91" }}>L {fmt(longVal || 0)}</span>
        <span style={{ color: "#8b949e" }}>Total {fmt(total || 0)}</span>
        <span style={{ color: "#ff6b6b" }}>S {fmt(shortVal || 0)}</span>
      </div>
    </div>
  );
}

function ProximityTradeTable({ trades, mob }) {
  if (!trades || trades.length === 0) {
    return <div style={{ color: "#4a5568", fontSize: 12, padding: 16, textAlign: "center" }}>No signals yet. Waiting for RAW B2 extreme to fire.</div>;
  }
  const fmtRet = (entryPx, fwdPx, direction) => {
    if (fwdPx == null) return <span style={{ color: "#30363d" }}>--</span>;
    const ret = direction === "LONG" ? (fwdPx - entryPx) / entryPx : (entryPx - fwdPx) / entryPx;
    const pct = (ret * 100).toFixed(2);
    const color = ret > 0 ? "#4dff91" : ret < 0 ? "#ff6b6b" : "#8b949e";
    return <span style={{ color, fontFamily: "'Space Mono', monospace" }}>{ret > 0 ? "+" : ""}{pct}%</span>;
  };
  const cols = ["Time", "Dir", "Entry", "z", "15m", "30m", "1h", "2h", "4h", "8h", "MFE", "Hit?"];
  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: mob ? 9 : 10, fontFamily: "'Space Mono', monospace" }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} style={{ padding: "6px 6px", textAlign: c === "Dir" || c === "Hit?" ? "center" : "right", color: "#718096", fontWeight: 600, fontSize: 9, letterSpacing: 1, borderBottom: "1px solid #1a2030", whiteSpace: "nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((t, i) => {
            const d = new Date(t.entry_ts * 1000);
            const timeStr = d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
            const dirColor = t.direction === "LONG" ? "#4dff91" : "#ff6b6b";
            return (
              <tr key={i} style={{ borderBottom: "1px solid #0d1117" }}>
                <td style={{ padding: "5px 6px", color: "#8b949e", whiteSpace: "nowrap" }}>{timeStr}</td>
                <td style={{ padding: "5px 6px", textAlign: "center", color: dirColor, fontWeight: 700 }}>{t.direction === "LONG" ? "\u2191" : "\u2193"}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: "#e2e8f0" }}>${Number(t.entry_price).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: "#58a6ff" }}>{t.entry_z != null ? Number(t.entry_z).toFixed(2) : "--"}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_15m, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_30m, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_1h, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_2h, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_4h, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{fmtRet(t.entry_price, t.price_8h, t.direction)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{t.mfe_4h != null ? <span style={{ color: "#4dff91" }}>+{(t.mfe_4h * 100).toFixed(2)}%</span> : <span style={{ color: "#30363d" }}>--</span>}</td>
                <td style={{ padding: "5px 6px", textAlign: "center" }}>{t.hit_03pct ? <span style={{ color: "#4dff91", fontWeight: 700 }}>YES</span> : t.resolved ? <span style={{ color: "#ff6b6b" }}>NO</span> : <span style={{ color: "#30363d" }}>...</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProximityTab({ asset }) {
  const mob = useIsMobile();
  const { data: prox } = useApi(API + "/proximity/latest/" + asset, 30000);
  const { data: tradesData } = useApi(API + "/proximity/trades?limit=20", 60000);

  if (!prox || prox.status === "no_data") {
    return (
      <div style={{ ...PANEL_STYLE, padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 14, color: "#4a5568" }}>Waiting for proximity data...</div>
        <div style={{ fontSize: 11, color: "#30363d", marginTop: 8 }}>First compute cycle runs ~60s after backend start</div>
      </div>
    );
  }

  const z = prox.raw_b2_robust_z;
  const extreme = prox.raw_b2_extreme;
  const direction = prox.raw_b2_signal_direction;
  const ratio = prox.raw_b2_imbalance_ratio || 0;
  const b2Total = prox.raw_b2_total_notional_usd || 0;
  const b2Long = prox.raw_b2_long_total_usd || 0;
  const b2Short = prox.raw_b2_short_total_usd || 0;
  const b2Count = prox.raw_b2_position_count || 0;
  const b1Z = prox.raw_b1_robust_z;
  const b1Ratio = prox.raw_b1_imbalance_ratio || 0;
  const b1Total = prox.raw_b1_total_notional_usd || 0;
  const b1Long = prox.raw_b1_long_total_usd || 0;
  const b1Short = prox.raw_b1_short_total_usd || 0;
  const b1Count = prox.raw_b1_position_count || 0;
  const totalPositions = prox.total_positions_in_snapshot || 0;
  const lastInfo = prox.last_extreme_info || {};
  const zHist = prox.raw_b2_z_history || [];

  const now = Date.now() / 1000;
  const lastTs = lastInfo.ts;
  const lastDir = lastInfo.direction;
  const lastPrice = lastInfo.price;
  let signalAge = null;
  let signalFresh = false;
  if (lastTs) {
    const ageSec = now - lastTs;
    signalFresh = ageSec < 14400;
    const h = Math.floor(ageSec / 3600);
    const m = Math.floor((ageSec % 3600) / 60);
    signalAge = h > 0 ? `${h}h ${m}m ago` : `${m}m ago`;
  }

  const signalColor = lastDir === "LONG" ? "#4dff91" : lastDir === "SHORT" ? "#ff6b6b" : "#4a5568";
  const borderColor = signalFresh ? signalColor : "#1a2030";
  const bgTint = signalFresh ? signalColor + "06" : "#060a0f";

  // Chart data for z-score
  const chartData = zHist.map((v, i) => ({ idx: i, z: v }));
  // Mark extreme fires: z above 1.5 or below -1.5
  const extremeDots = chartData.filter(d => Math.abs(d.z) >= 1.5);

  const trades = tradesData?.trades || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, animation: "fadeIn 0.3s ease", maxHeight: mob ? undefined : "calc(100vh - 168px)", overflowY: mob ? undefined : "auto" }}>

      {/* ═══ THE SIGNAL ═══ */}
      <div style={{ background: bgTint, border: `1px solid ${borderColor}`, borderRadius: 8, padding: mob ? "20px 16px" : "28px 32px", textAlign: "center" }}>
        {signalFresh && <div style={{ fontSize: 9, color: signalColor, letterSpacing: 3, marginBottom: 8, fontWeight: 700 }}>ACTIVE SIGNAL</div>}
        {!signalFresh && <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 3, marginBottom: 8 }}>PROXIMITY SIGNAL</div>}

        {extreme && direction ? (
          <div style={{ fontSize: mob ? 48 : 72, fontWeight: 800, color: direction === "LONG" ? "#4dff91" : "#ff6b6b", fontFamily: "'Syne', sans-serif", lineHeight: 1, marginBottom: 8 }}>
            {direction === "LONG" ? "\u2191" : "\u2193"} {direction}
          </div>
        ) : signalFresh && lastDir ? (
          <div style={{ fontSize: mob ? 40 : 56, fontWeight: 800, color: signalColor + "88", fontFamily: "'Syne', sans-serif", lineHeight: 1, marginBottom: 8 }}>
            {lastDir === "LONG" ? "\u2191" : "\u2193"} {lastDir}
          </div>
        ) : (
          <div style={{ fontSize: mob ? 36 : 48, fontWeight: 700, color: "#30363d", fontFamily: "'Syne', sans-serif", lineHeight: 1, marginBottom: 8 }}>NEUTRAL</div>
        )}

        <div style={{ fontSize: mob ? 28 : 40, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace", marginBottom: 8 }}>
          z = {z != null ? (z >= 0 ? "+" : "") + z.toFixed(2) : "\u2014"}
        </div>

        {lastTs ? (
          <div style={{ fontSize: mob ? 11 : 13, color: signalFresh ? signalColor : "#718096" }}>
            Signal: {lastDir} {signalAge}{lastPrice ? ` at $${Number(lastPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "#30363d" }}>No signal fired yet</div>
        )}
      </div>

      {/* ═══ Z-SCORE CHART ═══ */}
      {chartData.length > 2 && (
        <div style={{ ...PANEL_STYLE }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 8 }}>RAW B2 Z-SCORE — LAST {Math.round(chartData.length * 3 / 60)}H</div>
          <div style={{ height: mob ? 160 : 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
                <XAxis dataKey="idx" tick={false} axisLine={{ stroke: "#1a2030" }} />
                <YAxis tick={{ fill: "#4a5568", fontSize: 10 }} axisLine={{ stroke: "#1a2030" }} tickLine={false} domain={["auto", "auto"]} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const val = payload[0]?.value;
                  return (
                    <div style={{ background: "rgba(10,14,20,0.95)", border: "1px solid #1a2030", borderRadius: 4, padding: "6px 10px", fontSize: 11 }}>
                      <span style={{ color: "#718096" }}>z: </span>
                      <span style={{ color: val >= 1.5 ? "#ff6b6b" : val <= -1.5 ? "#4dff91" : "#e2e8f0", fontWeight: 700 }}>{Number(val).toFixed(3)}</span>
                    </div>
                  );
                }} />
                <defs>
                  <linearGradient id="proxZGradFull" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#58a6ff" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#58a6ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="z" stroke="#58a6ff" strokeWidth={2} fill="url(#proxZGradFull)" dot={false} isAnimationActive={false} />
                <ReferenceLine y={1.5} stroke="#ff6b6b" strokeDasharray="4 4" strokeWidth={1} label={{ value: "z=1.5", fill: "#ff6b6b", fontSize: 9, position: "right" }} />
                <ReferenceLine y={-1.5} stroke="#4dff91" strokeDasharray="4 4" strokeWidth={1} label={{ value: "z=-1.5", fill: "#4dff91", fontSize: 9, position: "right" }} />
                <ReferenceLine y={0} stroke="#30363d" strokeWidth={1} />
                {extremeDots.map((d, i) => (
                  <ReferenceDot key={i} x={d.idx} y={d.z} r={4} fill={d.z >= 1.5 ? "#ff6b6b" : "#4dff91"} stroke="none" />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ═══ BAND BREAKDOWN ═══ */}
      <div style={mob ? { display: "flex", flexDirection: "column", gap: 10 } : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <ProximityRatioBar label="RAW B2 — PRIMARY (3-8% distance)" ratio={ratio} longVal={b2Long} shortVal={b2Short} total={b2Total} count={b2Count} />
        <ProximityRatioBar label="RAW B1 — NEAR BAND (0-3% distance)" ratio={b1Ratio} longVal={b1Long} shortVal={b1Short} total={b1Total} count={b1Count} muted />
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 10, color: "#4a5568" }}>Total positions: <span style={{ color: "#8b949e" }}>{totalPositions.toLocaleString()}</span></div>
        {prox.atr_value && <div style={{ fontSize: 10, color: "#4a5568" }}>ATR: <span style={{ color: "#8b949e" }}>${Number(prox.atr_value).toLocaleString()}</span></div>}
        {prox.data_quality_flags && prox.data_quality_flags !== "" && <div style={{ fontSize: 10, color: "#d29922" }}>Flags: {prox.data_quality_flags}</div>}
      </div>

      {/* ═══ VIRTUAL TRADE JOURNAL ═══ */}
      <div style={{ ...PANEL_STYLE }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>SIGNAL JOURNAL</div>
          <div style={{ fontSize: 10, color: "#4a5568" }}>{trades.length} signal{trades.length !== 1 ? "s" : ""}</div>
        </div>
        <ProximityTradeTable trades={trades} mob={mob} />
      </div>
    </div>
  );
}

function OverviewTabV2({ ms, ms4h, imb, scenario, aggOi, aggLs, aggLiq, bt, modelA, mmBias, whaleBias, currentPrice, asset }) {
  const mob = useIsMobile();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: mob ? undefined : "calc(100vh - 168px)", overflow: mob ? undefined : "hidden" }}>
      <ProximitySignalPanel asset={asset || "BTC"} />
      <div style={mob ? {
        display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", paddingBottom: 24, WebkitOverflowScrolling: "touch",
      } : {
        display: "grid", gridTemplateColumns: "minmax(0, 1.18fr) minmax(0, 0.82fr)", gridTemplateRows: "minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.7fr)", gap: 12, flex: 1, overflow: "hidden",
      }}>
        <TradeDecisionPanel ms={ms} imb={imb} currentPrice={currentPrice} modelA={modelA} />
        <MTFStructurePanel ms={ms} ms4h={ms4h} />
        <LocationPanel ms={ms} />
        <FlowPulsePanel aggOi={aggOi} aggLs={aggLs} aggLiq={aggLiq} mmBias={mmBias} whaleBias={whaleBias} positioning={ms?.positioning} />
        <BookmapTriggerPanel bt={bt} />
        <PositionStatusPanel imb={imb} ms={ms} />
      </div>
    </div>
  );
}


function OIBar({ longNtl, shortNtl, accent }) {
  const total = longNtl + shortNtl;
  const longPct = total > 0 ? (longNtl / total) * 100 : 50;
  return (
    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px" }}>
      <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 8 }}>OI IMBALANCE</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 11, color: "#4dff91", minWidth: 70 }}>{fmt(longNtl)}</span>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#1a2030", overflow: "hidden", display: "flex" }}>
          <div style={{ width: longPct + "%", height: "100%", background: "#4dff91", borderRadius: "4px 0 0 4px" }} />
          <div style={{ width: (100 - longPct) + "%", height: "100%", background: "#ff6b6b", borderRadius: "0 4px 4px 0" }} />
        </div>
        <span style={{ fontSize: 11, color: "#ff6b6b", minWidth: 70, textAlign: "right" }}>{fmt(shortNtl)}</span>
      </div>
    </div>
  );
}

function ScenarioSummaryCard({ label, accent = "#4da2ff", children }) {
  return (
    <div style={{ background: "#0a0e14", border: `1px solid ${accent}33`, borderRadius: 6, padding: "14px 18px", minHeight: 142, boxShadow: `inset 0 1px 0 ${accent}14` }}>
      <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>{label}</div>
      {children}
    </div>
  );
}

function ScenarioChartTooltip({ active, payload }) {
  const point = active && payload?.length ? payload[0].payload : null;
  if (!point) return null;
  return (
    <div style={{ background: "rgba(10,14,20,0.96)", border: "1px solid #1a2030", borderRadius: 6, padding: "10px 12px", minWidth: 230 }}>
      <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 700, marginBottom: 6 }}>
        {fmtPct(point.shock_pct, point.shock_pct % 1 === 0 ? 0 : 1)} · ${point.target_price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 10px", fontSize: 10, color: "#a0aec0" }}>
        <span>Longs / Shorts</span><span>{point.longs_liquidated} / {point.shorts_liquidated}</span>
        <span>Liq notional</span><span style={{ color: "#e2e8f0" }}>{fmt(point.notional_liquidated)}</span>
        <span>Margin lost</span><span style={{ color: "#ff9900" }}>{fmt(point.total_margin_lost)}</span>
        <span>Net flow</span><span style={{ color: point.net_forced_flow < 0 ? "#ff6b6b" : point.net_forced_flow > 0 ? "#4dff91" : "#a0aec0" }}>{fmtSignedMoney(point.net_forced_flow)}</span>
        <span>Top10 concentration</span><span>{point.concentration_top10_pct?.toFixed(1)}%</span>
        <span>Accounts</span><span>{point.unique_accounts}</span>
        <span>Dominant cohort</span><span>{point.dominant_cohort || "—"}</span>
      </div>
    </div>
  );
}

function ScenarioTab({ asset, currentPrice, scenarioData, exposureData, setLiqModal }) {
  const rawRows = Array.isArray(scenarioData?.bands) && scenarioData.bands.length
    ? scenarioData.bands
    : Object.values(scenarioData?.scenarios || {});
  const rows = rawRows.map(normalizeScenarioBand).filter(Boolean).sort((a, b) => a.shock_pct - b.shock_pct);
  const downRows = rows.filter(r => r.shock_pct < 0).sort((a, b) => a.shock_pct - b.shock_pct);
  const upRows = rows.filter(r => r.shock_pct > 0).sort((a, b) => a.shock_pct - b.shock_pct);
  const [expandedBand, setExpandedBand] = useState(null);
  const [cascadeEstimate, setCascadeEstimate] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [selectedCascadeBand, setSelectedCascadeBand] = useState(null);

  const fetchCascadeEstimate = (shockPct) => {
    setCascadeLoading(true);
    setCascadeEstimate(null);
    setSelectedCascadeBand(shockPct);
    authFetch(`${API}/scenario/${asset}/cascade-estimate?start_shock_pct=${shockPct}&max_steps=2`)
      .then(r => { if (r.status === 401) { handle401(); return null; } return r.json(); })
      .then(d => { if (d) setCascadeEstimate(d); })
      .catch(() => setCascadeEstimate({ status: "error", layer: "L3_ESTIMATE", reason: "Request failed" }))
      .finally(() => setCascadeLoading(false));
  };

  const downNearRows = [...downRows].sort((a, b) => b.shock_pct - a.shock_pct);
  const upNearRows = [...upRows].sort((a, b) => a.shock_pct - b.shock_pct);
  const maxDownNtl = Math.max(...downRows.map(r => r.notional_liquidated || 0), 1);
  const maxUpNtl = Math.max(...upRows.map(r => r.notional_liquidated || 0), 1);
  const longExposure = scenarioData?.total_long_exposure ?? exposureData?.total_long_exposure ?? 0;
  const shortExposure = scenarioData?.total_short_exposure ?? exposureData?.total_short_exposure ?? 0;
  const skewRatio = shortExposure > 0 ? longExposure / shortExposure : (longExposure > 0 ? Infinity : 1);
  const skewLabel = skewRatio > 1.1 ? "LONG HEAVY" : skewRatio < 0.9 ? "SHORT HEAVY" : "BALANCED";
  const nearestDownBand = downNearRows.find(r => r.long_notional_liquidated > 0) || null;
  const nearestUpBand = upNearRows.find(r => r.short_notional_liquidated > 0) || null;
  const asymmetryRows = [1, 3, 5].map((pct) => ({
    pct,
    down_notional: Number(scenarioData?.asymmetry?.[`at_${pct}pct`]?.down_notional ?? 0),
    up_notional: Number(scenarioData?.asymmetry?.[`at_${pct}pct`]?.up_notional ?? 0),
    ratio: Number(scenarioData?.asymmetry?.[`at_${pct}pct`]?.ratio ?? 1),
    direction: scenarioData?.asymmetry?.[`at_${pct}pct`]?.direction ?? "BALANCED",
  }));
  const dominantAsymmetry = asymmetryRows.reduce((best, row) => {
    const bestScore = best ? Math.abs(Math.log(Math.max(best.ratio, 0.0001))) : -1;
    const rowScore = Math.abs(Math.log(Math.max(row.ratio, 0.0001)));
    return rowScore > bestScore ? row : best;
  }, null);
  const chartData = rows.map((r) => ({
    ...r,
    downCumulative: r.shock_pct < 0 ? r.long_notional_liquidated : null,
    upCumulative: r.shock_pct > 0 ? r.short_notional_liquidated : null,
    incrementalFlow: r.incremental_flow ?? 0,
  }));

  const openLiqModal = (r) => {
    const totalLiq = (r.longs_liquidated || 0) + (r.shorts_liquidated || 0);
    if (totalLiq === 0) return;
    authFetch(`${API}/scenario/${asset}/positions?target_price=${r.target_price}&limit=200`)
      .then(res => res.json())
      .then(data => setLiqModal({ pct: r.pct_change, target: r.target_price, positions: data.positions || [] }))
      .catch(() => {});
  };

  // --- Narrative summary helpers ---
  // "Meaningful" threshold: at least 1% of the larger side's exposure, floor $1M
  const _meaningfulThreshold = Math.max(1_000_000, 0.01 * Math.max(longExposure, shortExposure));

  const findNearestMeaningful = (nearRows, side) => {
    const key = side === "down" ? "long_notional_liquidated" : "short_notional_liquidated";
    return nearRows.find(r => (r[key] || 0) >= _meaningfulThreshold) || null;
  };

  const meaningfulDown = findNearestMeaningful(downNearRows, "down");
  const meaningfulUp = findNearestMeaningful(upNearRows, "up");

  const narrativeData = (() => {
    const a1 = asymmetryRows[0]?.direction || "BALANCED"; // 1%
    const a3 = asymmetryRows[1]?.direction || "BALANCED"; // 3%
    const a5 = asymmetryRows[2]?.direction || "BALANCED"; // 5%
    const hasDown = !!meaningfulDown;
    const hasUp = !!meaningfulUp;

    // Regime classification
    let regime;
    if (!hasDown && !hasUp) {
      regime = "quiet";
    } else if (a1 === "DOWNSIDE_HEAVY" && a3 === "DOWNSIDE_HEAVY" && hasDown) {
      regime = "downside_heavy";
    } else if (a1 === "UPSIDE_HEAVY" && a3 === "UPSIDE_HEAVY" && hasUp) {
      regime = "upside_heavy";
    } else if (a1 !== a5 && a1 !== "BALANCED" && a5 !== "BALANCED") {
      regime = "mixed_by_distance";
    } else {
      regime = "balanced_two_sided";
    }

    // Build sentence
    const concStr = (band) => {
      const c = band?.concentration_top10_pct || 0;
      if (c >= 60) return ", highly concentrated in top 10 accounts";
      if (c >= 35) return `, ${c.toFixed(0)}% concentrated in top 10`;
      return "";
    };
    const cohortStr = (band) => {
      const dc = band?.dominant_cohort;
      return dc && dc !== "—" ? ` and ${dc} leverage dominant` : "";
    };
    const flowStr = (band, side) => {
      const ntl = side === "down" ? band?.long_notional_liquidated : band?.short_notional_liquidated;
      const verb = side === "down" ? "selling" : "buying";
      return `${fmt(ntl)} of forced ${verb}`;
    };

    let sentence, accent;
    switch (regime) {
      case "quiet":
        sentence = "The book is relatively quiet near spot, with no meaningful liquidation pocket on either side until deeper moves.";
        accent = "#718096";
        break;
      case "downside_heavy": {
        const b = meaningfulDown;
        sentence = `The book is downside-heavy near spot: a ${fmtPct(b.shock_pct, Math.abs(b.shock_pct % 1) > 0 ? 1 : 0)} move would force ${flowStr(b, "down")}${concStr(b)}${cohortStr(b)}.`;
        accent = "#ff6b6b";
        break;
      }
      case "upside_heavy": {
        const b = meaningfulUp;
        sentence = `The book is upside-heavy near spot: a ${fmtPct(b.shock_pct, Math.abs(b.shock_pct % 1) > 0 ? 1 : 0)} move would force ${flowStr(b, "up")}${concStr(b)}${cohortStr(b)}.`;
        accent = "#4dff91";
        break;
      }
      case "mixed_by_distance":
        sentence = `The book is mixed by distance: ${meaningfulDown ? `downside pressure appears at ${fmtPct(meaningfulDown.shock_pct, Math.abs(meaningfulDown.shock_pct % 1) > 0 ? 1 : 0)}` : "no near downside pocket"}${meaningfulDown && meaningfulUp ? ", while " : ""}${meaningfulUp ? `upside fragility builds by ${fmtPct(meaningfulUp.shock_pct, Math.abs(meaningfulUp.shock_pct % 1) > 0 ? 1 : 0)}` : ""}.`;
        accent = "#4da2ff";
        break;
      default: {
        const parts = [];
        if (meaningfulDown) parts.push(`downside selling risk at ${fmtPct(meaningfulDown.shock_pct, Math.abs(meaningfulDown.shock_pct % 1) > 0 ? 1 : 0)} (${flowStr(meaningfulDown, "down")})`);
        if (meaningfulUp) parts.push(`upside buy-pressure at ${fmtPct(meaningfulUp.shock_pct, Math.abs(meaningfulUp.shock_pct % 1) > 0 ? 1 : 0)} (${flowStr(meaningfulUp, "up")})`);
        sentence = parts.length === 2
          ? `The book is two-sided near spot: ${parts[0]}, while ${parts[1]}.`
          : parts.length === 1
            ? `The book has ${parts[0]}.`
            : "Scenario data is available, but no meaningful liquidation asymmetry is visible near spot.";
        accent = "#4da2ff";
      }
    }
    return { regime, sentence, accent };
  })();

  const renderDangerCard = (band, label, accent) => (
    <ScenarioSummaryCard label={label} accent={accent}>
      {band ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10 }}>
            <span style={{ color: accent, fontSize: 24, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{fmtPct(band.shock_pct, Math.abs(band.shock_pct % 1) > 0 ? 1 : 0)}</span>
            <span style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700 }}>${band.target_price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "5px 10px", fontSize: 11 }}>
            <span style={{ color: "#718096" }}>Net flow</span>
            <span style={{ color: band.net_forced_flow < 0 ? "#ff6b6b" : band.net_forced_flow > 0 ? "#4dff91" : "#a0aec0", fontWeight: 700, fontSize: 13 }}>{fmtSignedMoney(band.net_forced_flow)}</span>
            <span style={{ color: "#718096" }}>Liq notional</span>
            <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 12 }}>{fmt(band.notional_liquidated)}</span>
            <span style={{ color: "#718096" }}>Top10 concentration</span>
            <span style={{ color: "#a0aec0" }}>{band.concentration_top10_pct?.toFixed(1)}%</span>
            <span style={{ color: "#718096" }}>Accounts</span>
            <span style={{ color: "#a0aec0" }}>{band.unique_accounts}</span>
          </div>
          <button className="btn" onClick={(e) => { e.stopPropagation(); fetchCascadeEstimate(band.shock_pct); }} style={{ marginTop: 10, background: "#f6ad5515", border: "1px solid #f6ad5540", color: "#f6ad55", padding: "4px 10px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: 1, width: "100%" }}>
            {cascadeLoading && selectedCascadeBand === band.shock_pct ? "ESTIMATING..." : "CASCADE ESTIMATE"}
          </button>
        </>
      ) : (
        <div style={{ color: "#718096", fontSize: 12, marginTop: 28 }}>No meaningful pocket in current sweep.</div>
      )}
    </ScenarioSummaryCard>
  );

  const _COHORT_ORDER = ["1-5x", "5-10x", "10-25x", "25-50x"];
  const _COHORT_COLORS = { "1-5x": "#4da2ff", "5-10x": "#4dff91", "10-25x": "#f6ad55", "25-50x": "#ff6b6b" };

  const renderExpansionPanel = (r) => {
    const cohorts = r.cohorts || {};
    const marginType = r.margin_type || {};
    const crossRisk = r.cross_at_risk || {};
    const cohortEntries = _COHORT_ORDER.map(k => ({ label: k, ...(cohorts[k] || { count: 0, notional: 0, margin: 0 }) }));
    const maxCohortNtl = Math.max(...cohortEntries.map(c => c.notional || 0), 1);
    const isoData = marginType.isolated || { count: 0, notional: 0 };
    const crossData = marginType.cross || { count: 0, notional: 0 };
    const mtTotal = (isoData.notional || 0) + (crossData.notional || 0);
    const isoPct = mtTotal > 0 ? ((isoData.notional || 0) / mtTotal * 100) : 0;
    const crossPct = mtTotal > 0 ? ((crossData.notional || 0) / mtTotal * 100) : 0;

    // Summary sentence
    const dominantLev = r.dominant_cohort || "—";
    const crossMaterial = crossRisk.notional > 0 && mtTotal > 0 && (crossRisk.notional / mtTotal) > 0.2;
    const summaryText = crossMaterial
      ? `Estimated cross-margin at risk is material in this band (${fmt(crossRisk.notional)} / ${crossRisk.count} accts).`
      : cohortEntries.some(c => c.notional > 0)
        ? `Dominated by ${dominantLev} accounts with ${isoPct > 70 ? "heavy" : "mixed"} isolated participation.`
        : "No breakdown available for this band.";

    return (
      <div style={{ minWidth: 1120, background: "#0c1018", borderTop: "1px solid #1a2030", borderBottom: "1px solid #1a2030", padding: "16px 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 16 }}>

          {/* Section A — Leverage Cohorts */}
          <div>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>LEVERAGE COHORTS</div>
            {cohortEntries.every(c => !c.notional && !c.count) ? (
              <div style={{ color: "#4a5568", fontSize: 11 }}>No breakdown available</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cohortEntries.map(c => {
                  const barW = maxCohortNtl > 0 ? (c.notional / maxCohortNtl) * 100 : 0;
                  const isDom = c.label === r.dominant_cohort;
                  const color = _COHORT_COLORS[c.label] || "#a0aec0";
                  return (
                    <div key={c.label} style={{ display: "grid", gridTemplateColumns: "50px 1fr auto", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: isDom ? "#e2e8f0" : "#718096", fontWeight: isDom ? 700 : 400, fontFamily: "'Space Mono', monospace" }}>{c.label}</span>
                      <div style={{ height: 10, borderRadius: 2, background: "#1a2030", overflow: "hidden" }}>
                        <div style={{ width: `${Math.max(barW, barW > 0 ? 2 : 0)}%`, height: "100%", background: color + (isDom ? "" : "80"), borderRadius: 2, border: isDom ? `1px solid ${color}` : "none" }} />
                      </div>
                      <span style={{ fontSize: 10, color: isDom ? "#e2e8f0" : "#a0aec0", fontFamily: "'Space Mono', monospace", minWidth: 70, textAlign: "right" }}>
                        {fmt(c.notional)}{c.count > 0 ? ` (${c.count})` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section B — Margin Type Split */}
          <div>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>MARGIN TYPE</div>
            {mtTotal === 0 ? (
              <div style={{ color: "#4a5568", fontSize: 11 }}>No breakdown available</div>
            ) : (
              <>
                {/* Stacked bar */}
                <div style={{ display: "flex", height: 16, borderRadius: 3, overflow: "hidden", marginBottom: 8, border: "1px solid #1a2030" }}>
                  {isoPct > 0 && <div style={{ width: `${isoPct}%`, background: "#4da2ff60", minWidth: 2 }} />}
                  {crossPct > 0 && <div style={{ width: `${crossPct}%`, background: "#f6ad5560", minWidth: 2 }} />}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", fontSize: 11 }}>
                  <span style={{ color: "#4da2ff" }}>Isolated</span>
                  <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{fmt(isoData.notional)} ({isoPct.toFixed(0)}%){isoData.count ? ` · ${isoData.count} pos` : ""}</span>
                  <span style={{ color: "#f6ad55" }}>Cross</span>
                  <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{fmt(crossData.notional)} ({crossPct.toFixed(0)}%){crossData.count ? ` · ${crossData.count} pos` : ""}</span>
                </div>
                {crossRisk.count > 0 && (
                  <div style={{ marginTop: 8, padding: "6px 8px", background: "#f6ad5510", borderRadius: 3, border: "1px solid #f6ad5520", fontSize: 10, color: "#f6ad55" }}>
                    Est. cross at risk: {fmt(crossRisk.notional)} / {crossRisk.count} accts / {fmt(crossRisk.margin)} margin
                  </div>
                )}
              </>
            )}
          </div>

          {/* Section C — Band Summary */}
          <div>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>BAND DETAIL</div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 11 }}>
              <span style={{ color: "#718096" }}>Net flow</span>
              <span style={{ color: r.net_forced_flow < 0 ? "#ff6b6b" : r.net_forced_flow > 0 ? "#4dff91" : "#a0aec0", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{fmtSignedMoney(r.net_forced_flow)}</span>
              <span style={{ color: "#718096" }}>Liq notional</span>
              <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>{fmt(r.notional_liquidated)}</span>
              <span style={{ color: "#718096" }}>Margin lost</span>
              <span style={{ color: "#ff9900", fontFamily: "'Space Mono', monospace" }}>{fmt(r.total_margin_lost)}</span>
              <span style={{ color: "#718096" }}>Longs / Shorts</span>
              <span style={{ fontFamily: "'Space Mono', monospace" }}><span style={{ color: "#4dff91" }}>{r.longs_liquidated || 0}</span> / <span style={{ color: "#ff6b6b" }}>{r.shorts_liquidated || 0}</span></span>
              <span style={{ color: "#718096" }}>Top10 conc.</span>
              <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{(r.concentration_top10_pct || 0).toFixed(1)}%</span>
              <span style={{ color: "#718096" }}>Accounts</span>
              <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{r.unique_accounts || 0}</span>
              <span style={{ color: "#718096" }}>Dominant</span>
              <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{r.dominant_cohort || "—"}</span>
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: "#4a5568", fontStyle: "italic", lineHeight: 1.4 }}>{summaryText}</div>
            <button className="btn" onClick={(e) => { e.stopPropagation(); fetchCascadeEstimate(r.shock_pct); }} style={{ marginTop: 10, background: "#f6ad5515", border: "1px solid #f6ad5540", color: "#f6ad55", padding: "4px 10px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>
              {cascadeLoading && selectedCascadeBand === r.shock_pct ? "ESTIMATING..." : "CASCADE ESTIMATE"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRow = (r, i) => {
    const isDown = r.shock_pct < 0;
    const liqNtl = r.notional_liquidated;
    const totalLiq = (r.longs_liquidated || 0) + (r.shorts_liquidated || 0);
    const barPct = isDown ? (liqNtl / maxDownNtl) * 100 : (liqNtl / maxUpNtl) * 100;
    const barColor = isDown ? "#ff6b6b" : "#4dff91";
    const netFlowColor = r.net_forced_flow < 0 ? "#ff6b6b" : r.net_forced_flow > 0 ? "#4dff91" : "#718096";
    const concColor = r.concentration_top10_pct >= 60 ? "#ff6b6b" : r.concentration_top10_pct >= 35 ? "#ff9900" : "#a0aec0";
    const isExpanded = expandedBand === r.shock_pct;
    return (
      <React.Fragment key={`${r.shock_pct}_${i}`}>
        <div onClick={() => setExpandedBand(isExpanded ? null : r.shock_pct)} style={{ display: "grid", gridTemplateColumns: "70px 100px minmax(240px,1fr) 110px 120px 120px 80px 90px 90px 90px", minWidth: 1120, padding: "14px 20px", borderBottom: isExpanded ? "none" : "1px solid #0d1117", alignItems: "center", background: isExpanded ? "#0a0f16" : (i % 2 === 0 ? "#080c12" : "transparent"), cursor: "pointer" }}>
          <span style={{ color: isDown ? "#ff6b6b" : "#4dff91", fontWeight: 700, fontSize: 13 }}>{r.shock_pct > 0 ? "+" : ""}{r.shock_pct}%</span>
          <span style={{ color: "#a0aec0", fontSize: 13 }}>${r.target_price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 13, minWidth: 80 }}>{fmt(liqNtl)}</span>
              <span onClick={(e) => { e.stopPropagation(); openLiqModal(r); }} style={{ color: "#4da2ff", fontSize: 11, cursor: totalLiq > 0 ? "pointer" : "default", textDecoration: totalLiq > 0 ? "underline" : "none" }}>({totalLiq} pos)</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "#1a2030", overflow: "hidden", maxWidth: 320 }}>
              <div style={{ width: Math.max(barPct, 1) + "%", height: "100%", background: barColor, borderRadius: 3 }} />
            </div>
          </div>
          <span style={{ color: netFlowColor, fontWeight: 700, fontSize: 12 }}>{fmtSignedMoney(r.net_forced_flow)}</span>
          <span style={{ color: "#ff9900", fontWeight: 700, fontSize: 13 }}>{fmt(r.total_margin_lost)}</span>
          <span style={{ color: "#ff4444", fontWeight: 700, fontSize: 13 }}>{fmt(r.total_unrealized_loss)}</span>
          <span style={{ color: concColor, fontWeight: 700, fontSize: 12 }}>{r.concentration_top10_pct?.toFixed(1)}%</span>
          <span style={{ color: "#a0aec0", fontSize: 12 }}>{r.dominant_cohort || "—"}</span>
          <span style={{ color: "#a0aec0", fontSize: 12 }}>{r.unique_accounts}</span>
          <CascadeBadge risk={r.cascade_risk} />
        </div>
        {isExpanded && renderExpansionPanel(r)}
      </React.Fragment>
    );
  };

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 12 }}>LIQUIDATION SCENARIOS — {asset} @ ${currentPrice?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>

      {/* Narrative summary */}
      <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderLeft: `3px solid ${narrativeData.accent}`, borderRadius: 4, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.5 }}>{narrativeData.sentence}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 12 }}>
        {renderDangerCard(nearestDownBand, "NEAREST DOWNSIDE DANGER", "#ff6b6b")}
        {renderDangerCard(nearestUpBand, "NEAREST UPSIDE DANGER", "#4dff91")}

        <ScenarioSummaryCard label="ASYMMETRY" accent="#4da2ff">
          <div style={{ color: "#4da2ff", fontSize: 20, fontWeight: 700, fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>
            {(() => {
              const dirs = asymmetryRows.map(r => r.direction);
              const d1 = dirs[0], d3 = dirs[1], d5 = dirs[2];
              if (d1 === d3 && d3 === d5 && d1 !== "BALANCED") return (d1 === "DOWNSIDE_HEAVY" ? "DOWNSIDE" : "UPSIDE") + " ALL BANDS";
              if (d1 === d3 && d1 !== "BALANCED") return "NEAR-SPOT " + (d1 === "DOWNSIDE_HEAVY" ? "DOWNSIDE" : "UPSIDE");
              if (d1 !== d5 && d1 !== "BALANCED" && d5 !== "BALANCED") return "MIXED BY DISTANCE";
              return (d1 || "BALANCED").replace(/_/g, " ");
            })()}
          </div>
          <div style={{ fontSize: 9, color: "#4a5568", marginBottom: 8 }}>Nearest band ({asymmetryRows[0]?.pct}%): {asymmetryRows[0]?.ratio?.toFixed(1)}x {asymmetryRows[0]?.direction?.replace(/_/g, " ").toLowerCase()}</div>
          <div style={{ display: "grid", gap: 6 }}>
            {asymmetryRows.map((row) => (
              <div key={row.pct} style={{ display: "grid", gridTemplateColumns: "34px 1fr auto", gap: 10, fontSize: 10, alignItems: "center" }}>
                <span style={{ color: "#718096" }}>{row.pct}%</span>
                <span style={{ color: "#a0aec0" }}>{fmt(row.down_notional)} vs {fmt(row.up_notional)}</span>
                <span style={{ color: row.direction === "DOWNSIDE_HEAVY" ? "#ff6b6b" : row.direction === "UPSIDE_HEAVY" ? "#4dff91" : "#a0aec0", fontWeight: 700 }}>
                  {row.ratio === 999 ? "∞x" : `${row.ratio.toFixed(2)}x`}
                </span>
              </div>
            ))}
          </div>
        </ScenarioSummaryCard>

        <ScenarioSummaryCard label="EXPOSURE BALANCE" accent="#ff9900">
          <div style={{ color: "#ff9900", fontSize: 20, fontWeight: 700, fontFamily: "'Space Mono', monospace", marginBottom: 10 }}>{skewLabel}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "5px 10px", fontSize: 10 }}>
            <span style={{ color: "#718096" }}>Long exposure</span>
            <span style={{ color: "#4dff91", fontWeight: 700 }}>{fmt(longExposure)}</span>
            <span style={{ color: "#718096" }}>Short exposure</span>
            <span style={{ color: "#ff6b6b", fontWeight: 700 }}>{fmt(shortExposure)}</span>
            <span style={{ color: "#718096" }}>Skew ratio</span>
            <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{isFinite(skewRatio) ? `${skewRatio.toFixed(2)}x` : "∞"}</span>
            <span style={{ color: "#718096" }}>Positions</span>
            <span style={{ color: "#a0aec0" }}>{scenarioData?.total_positions?.toLocaleString() || 0}</span>
          </div>
        </ScenarioSummaryCard>
      </div>

      {/* Layer 3 — Cascade Estimate Panel */}
      {cascadeEstimate && (
        <div style={{ background: "#0d0f14", border: "1px solid #f6ad5530", borderLeft: "3px solid #f6ad55", borderRadius: 6, padding: "16px 20px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 9, color: "#f6ad55", letterSpacing: 2, fontWeight: 700 }}>LAYER 3 — CASCADE ESTIMATE</span>
              <span style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>This is an estimate, not mechanical truth</span>
            </div>
            <span onClick={() => setCascadeEstimate(null)} style={{ color: "#4a5568", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✕</span>
          </div>

          {cascadeEstimate.status !== "ok" ? (
            <div style={{ color: "#718096", fontSize: 12 }}>{cascadeEstimate.reason || "Insufficient data for cascade estimate."}</div>
          ) : (() => {
            const ce = cascadeEstimate;
            const r = ce.result;
            const outcomeColor = r.outcome === "absorbed" ? "#4dff91" : r.outcome.includes("follow") ? "#f6ad55" : "#718096";
            return (
              <>
                <div style={{ fontSize: 18, fontWeight: 700, color: outcomeColor, fontFamily: "'Space Mono', monospace", marginBottom: 12 }}>
                  {r.outcome === "absorbed" ? "ABSORBED" : r.outcome.toUpperCase()}
                </div>

                {/* Assumptions */}
                <div style={{ display: "flex", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
                  {[
                    ["Depth assumed", `${fmt(ce.assumptions.absorbable_depth_per_1pct)}/1%`],
                    ["Source", ce.assumptions.source?.replace(/_/g, " ")],
                    ["Max steps", ce.assumptions.max_steps],
                    ["Start band", fmtPct(ce.start_band.shock_pct)],
                  ].map(([k, v]) => (
                    <div key={k} style={{ fontSize: 10 }}>
                      <span style={{ color: "#4a5568" }}>{k}: </span>
                      <span style={{ color: "#718096" }}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Key outputs */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 14 }}>
                  {[
                    ["Initial flow", fmtSignedMoney(-r.initial_flow * (ce.start_band.shock_pct < 0 ? 1 : -1)), r.initial_flow > 0 ? (ce.start_band.shock_pct < 0 ? "#ff6b6b" : "#4dff91") : "#718096"],
                    ["Additional flow", fmt(r.additional_flow), r.additional_flow > 0 ? "#f6ad55" : "#718096"],
                    ["Total estimated", fmt(r.total_estimated_flow), "#e2e8f0"],
                    ["Secondary move", `${r.estimated_secondary_move_pct.toFixed(2)}%`, "#a0aec0"],
                    ["Final shock", fmtPct(r.final_shock_pct), r.final_shock_pct < 0 ? "#ff6b6b" : "#4dff91"],
                    ["Final price", `$${r.final_price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, "#e2e8f0"],
                  ].map(([label, value, color]) => (
                    <div key={label} style={{ background: "#080c12", borderRadius: 4, padding: "8px 10px", border: "1px solid #1a2030" }}>
                      <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "'Space Mono', monospace" }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Step ladder */}
                {ce.steps.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 6 }}>CASCADE STEPS</div>
                    {ce.steps.map(s => (
                      <div key={s.step} style={{ display: "flex", gap: 16, alignItems: "center", padding: "4px 0", fontSize: 11, borderBottom: "1px solid #1a203040" }}>
                        <span style={{ color: "#f6ad55", fontWeight: 700, minWidth: 55 }}>Step {s.step}</span>
                        <span style={{ color: "#718096" }}>Flow: {fmt(s.input_flow)}</span>
                        <span style={{ color: "#718096" }}>Implied: {s.implied_move_pct.toFixed(2)}%</span>
                        <span style={{ color: "#a0aec0" }}>{fmtPct(s.shock_before)} → {fmtPct(s.shock_after)}</span>
                        <span style={{ color: s.bands_crossed.length > 0 ? "#f6ad55" : "#4dff91" }}>
                          {s.bands_crossed.length > 0 ? `Crossed: ${s.bands_crossed.map(b => fmtPct(b)).join(", ")}` : "No new bands crossed"}
                        </span>
                        {s.additional_flow > 0 && <span style={{ color: "#ff6b6b" }}>+{fmt(s.additional_flow)} triggered</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "16px 20px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>FRAGILITY CURVE</div>
            <div style={{ fontSize: 12, color: "#a0aec0" }}>Cumulative liquidation notional with incremental net flow by price band</div>
          </div>
          <div style={{ display: "flex", gap: 18, fontSize: 10, color: "#718096" }}>
            <span><span style={{ color: "#ff6b6b" }}>â– </span> Downside cumulative</span>
            <span><span style={{ color: "#4dff91" }}>â– </span> Upside cumulative</span>
            <span><span style={{ color: "#4da2ff" }}>â– </span> Incremental flow</span>
          </div>
        </div>
        <div style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 14, right: 24, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="scenarioDownFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff6b6b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ff6b6b" stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="scenarioUpFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4dff91" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4dff91" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1a2030" strokeDasharray="2 2" />
              <XAxis
                type="number"
                dataKey="shock_pct"
                domain={["dataMin", "dataMax"]}
                tick={{ fill: "#718096", fontSize: 10 }}
                tickFormatter={(v) => fmtPct(v, Math.abs(v % 1) > 0 ? 1 : 0)}
              />
              <YAxis yAxisId="cum" tick={{ fill: "#718096", fontSize: 10 }} tickFormatter={fmt} width={70} />
              <YAxis yAxisId="flow" orientation="right" tick={{ fill: "#718096", fontSize: 10 }} tickFormatter={fmt} width={70} />
              <Tooltip content={<ScenarioChartTooltip />} />
              <ReferenceLine x={0} stroke="#718096" strokeDasharray="4 4" />
              {nearestDownBand && <ReferenceLine x={nearestDownBand.shock_pct} stroke="#ff6b6b" strokeDasharray="3 3" />}
              {nearestUpBand && <ReferenceLine x={nearestUpBand.shock_pct} stroke="#4dff91" strokeDasharray="3 3" />}
              <Area yAxisId="cum" type="monotone" dataKey="downCumulative" name="Downside cumulative" stroke="#ff6b6b" fill="url(#scenarioDownFill)" strokeWidth={2} connectNulls={false} />
              <Area yAxisId="cum" type="monotone" dataKey="upCumulative" name="Upside cumulative" stroke="#4dff91" fill="url(#scenarioUpFill)" strokeWidth={2} connectNulls={false} />
              <Bar yAxisId="flow" dataKey="incrementalFlow" name="Incremental net flow" barSize={16}>
                {chartData.map((entry, idx) => (
                  <Cell key={`scenario-flow-${idx}`} fill={entry.incrementalFlow < 0 ? "#ff6b6b" : entry.incrementalFlow > 0 ? "#4dff91" : "#4a5568"} />
                ))}
              </Bar>
              {nearestDownBand && (
                <ReferenceDot
                  yAxisId="cum"
                  x={nearestDownBand.shock_pct}
                  y={nearestDownBand.long_notional_liquidated || nearestDownBand.notional_liquidated}
                  r={5}
                  fill="#ff6b6b"
                  stroke="#0a0e14"
                  strokeWidth={2}
                />
              )}
              {nearestUpBand && (
                <ReferenceDot
                  yAxisId="cum"
                  x={nearestUpBand.shock_pct}
                  y={nearestUpBand.short_notional_liquidated || nearestUpBand.notional_liquidated}
                  r={5}
                  fill="#4dff91"
                  stroke="#0a0e14"
                  strokeWidth={2}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div style={{ display: "grid", gridTemplateColumns: "70px 100px minmax(240px,1fr) 110px 120px 120px 80px 90px 90px 90px", minWidth: 1120, padding: "10px 20px", borderBottom: "1px solid #1a2030", fontSize: 10, color: "#718096", letterSpacing: 2 }}>
          <span>MOVE</span><span>PRICE</span><span>LIQ NOTIONAL</span><span>NET FLOW</span><span>MARGIN LOST</span><span>UNREAL. LOSS</span><span>TOP10</span><span>COHORT</span><span>ACCTS</span><span>RISK</span>
        </div>
        {downRows.map(renderRow)}
        {downRows.length > 0 && upRows.length > 0 && (
          <div style={{ minWidth: 1120, borderBottom: "2px solid #1a2030", background: "#060a0f", padding: "6px 20px", fontSize: 10, color: "#718096", letterSpacing: 2, textAlign: "center" }}>
            ▲ CURRENT PRICE: ${currentPrice?.toLocaleString(undefined, { maximumFractionDigits: 0 })} ▼
          </div>
        )}
        {upRows.map((r, i) => renderRow(r, i + downRows.length))}
      </div>

      {exposureData && (
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, marginTop: 12, padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1a2030", flexWrap: "wrap", gap: 12 }}>
            <span style={{ color: "#a0aec0", fontSize: 12 }}>Extended exposure context</span>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12 }}>Beyond ±99.5%: <span style={{ color: "#4dff91" }}>{fmt(exposureData.beyond_long_ntl)}</span> longs / <span style={{ color: "#ff6b6b" }}>{fmt(exposureData.beyond_short_ntl)}</span> shorts</span>
              <span style={{ fontSize: 12 }}>No liq price: <span style={{ color: "#4dff91" }}>{fmt(exposureData.unliq_long_ntl)}</span> longs / <span style={{ color: "#ff6b6b" }}>{fmt(exposureData.unliq_short_ntl)}</span> shorts</span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, flexWrap: "wrap", gap: 12, color: "#718096", fontSize: 12 }}>
            <span>Beyond move counts: {exposureData.beyond_long_count} longs / {exposureData.beyond_short_count} shorts</span>
            <span>No liq counts: {exposureData.unliq_long_count} longs / {exposureData.unliq_short_count} shorts</span>
          </div>
        </div>
      )}
    </div>
  );
}

const INTERVAL_MS = { "5m": 300000, "15m": 900000, "30m": 1800000, "1h": 3600000, "4h": 14400000, "1d": 86400000, "1w": 604800000 };

function HeatmapChart({ asset, assetColor, currentPrice, heatmapData, API }) {
  const [candles, setCandles] = useState(null);
  const [heatgrid, setHeatgrid] = useState(null);
  const [interval, setInterval_] = useState("1h");
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState(0);
  const [priceZoom, setPriceZoom] = useState(1);
  const [pricePanPct, setPricePanPct] = useState(0);
  const [chartWidth, setChartWidth] = useState(window.innerWidth - 80);
  const [sideFilter, setSideFilter] = useState("all"); // "all" | "long" | "short"
  const [scalingMode, setScalingMode] = useState("p95"); // "p95" | "absolute"
  const [heatSensitivity, setHeatSensitivity] = useState("balanced"); // "context" | "balanced" | "highsignal"
  const [heatgridAge, setHeatgridAge] = useState(null);
  const [liqEventsAge, setLiqEventsAge] = useState(null);
  const [showStructural, setShowStructural] = useState(true);
  const [showQuality, setShowQuality] = useState(true);
  const [qualityLevels, setQualityLevels] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [marketStructure, setMarketStructure] = useState(null);
  const [recentSweeps, setRecentSweeps] = useState([]);
  const containerRef = React.useRef(null);

  // Measure available width on mount and resize
  useEffect(() => {
    const measure = () => {
      // Use parent container width, or fall back to window width minus padding
      const parent = containerRef.current?.parentElement;
      setChartWidth(parent ? parent.offsetWidth : window.innerWidth - 80);
    };
    measure();
    // Re-measure after a short delay to catch layout shifts
    setTimeout(measure, 100);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []); // <1 = wider range, >1 = tighter

  const intervalHours = { "5m": 48, "15m": 72, "30m": 168, "1h": 504, "4h": 2016, "1d": 8760, "1w": 26280 };

  useEffect(() => {
    setCandles(null);
    setZoom(1);
    setPanOffset(0);
    setPriceZoom(1);
    setPricePanPct(0);
    const hours = intervalHours[interval] || 168;
    authFetch(`${API}/candles/${asset}?interval=${interval}&hours=${hours}`)
      .then(r => r.json()).then(d => setCandles(d.candles || [])).catch(() => {});
  }, [asset, interval, API]);

  // Auto-refresh candles every 60s without resetting zoom/pan
  useEffect(() => {
    const id = setInterval(() => {
      const hours = intervalHours[interval] || 168;
      authFetch(`${API}/candles/${asset}?interval=${interval}&hours=${hours}`)
        .then(r => r.json()).then(d => setCandles(d.candles || [])).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [asset, interval, API]);

  // Fetch 2D heatgrid + auto-refresh every 120s (FIX 4)
  useEffect(() => {
    const fetchGrid = () => {
      authFetch(`${API}/liq-heatgrid/${asset}`)
        .then(r => r.json()).then(d => { setHeatgrid(d); setHeatgridAge(Date.now()); }).catch(() => {});
    };
    fetchGrid();
    const id = setInterval(fetchGrid, 120000);
    return () => clearInterval(id);
  }, [asset, API]);

  // Fetch liquidation events + auto-refresh every 120s (FIX 4)
  const [liqEvents, setLiqEvents] = useState([]);
  useEffect(() => {
    const fetchEvents = () => {
      authFetch(`${API}/liq-events/${asset}`)
        .then(r => r.json()).then(d => { setLiqEvents(d.events || []); setLiqEventsAge(Date.now()); }).catch(() => {});
    };
    fetchEvents();
    const id = setInterval(fetchEvents, 120000);
    return () => clearInterval(id);
  }, [asset, API]);

  // Clear overlay state on asset change
  useEffect(() => {
    setAnalysisData(null);
    setMarketStructure(null);
    setRecentSweeps([]);
    setQualityLevels(null);
  }, [asset]);

  // Fetch analysis data for structural levels (magnets) — refresh every 30s
  useEffect(() => {
    const fetchAnalysis = () => {
      authFetch(`${API}/analysis/${asset}/imbalance`)
        .then(r => r.json()).then(d => setAnalysisData(d)).catch(() => {});
    };
    fetchAnalysis();
    const id = setInterval(fetchAnalysis, 30000);
    return () => clearInterval(id);
  }, [asset, API]);

  // Fetch market structure for sweep markers — refresh every 30s
  useEffect(() => {
    const fetchMS = () => {
      authFetch(`${API}/market_structure/${asset}`)
        .then(r => r.json()).then(d => {
          setMarketStructure(d);
          // Accumulate recent sweeps with deduplication
          if (d.sweep_5m && d.trigger_candle?.time) {
            setRecentSweeps(prev => {
              const ts = d.trigger_candle.time;
              if (prev.some(s => s.ts === ts)) return prev;
              const entry = {
                ts,
                price: d.sweep_price || (d.sweep_5m_type === "low" ? d.trigger_candle.low : d.trigger_candle.high),
                type: d.sweep_5m_type,
              };
              return [...prev, entry].slice(-10);
            });
          }
        }).catch(() => {});
    };
    fetchMS();
    const id = setInterval(fetchMS, 30000);
    return () => clearInterval(id);
  }, [asset, API]);

  // Fetch quality-scored persistence levels — refresh every 30s
  useEffect(() => {
    const fetchQLT = () => {
      authFetch(`${API}/liq-persistence/${asset}`)
        .then(r => r.json()).then(d => setQualityLevels(d)).catch(() => {});
    };
    fetchQLT();
    const id = setInterval(fetchQLT, 30000);
    return () => clearInterval(id);
  }, [asset, API]);

  const svgRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [mousePos, setMousePos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = React.useRef(null);
  const panOffsetRef = React.useRef(panOffset);
  const pricePanPctRef = React.useRef(pricePanPct);
  panOffsetRef.current = panOffset;
  pricePanPctRef.current = pricePanPct;

  // TradingView-style: drag chart=pan time, drag price axis=pan price
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const rightAxisStart = rect.width - 75;
    if (x > rightAxisStart) {
      dragRef.current = { type: "pricePan", startY: e.clientY, startPan: pricePanPctRef.current };
    } else {
      dragRef.current = { type: "bothPan", startX: e.clientX, startY: e.clientY, startOffset: panOffsetRef.current, startPan: pricePanPctRef.current };
    }
    setIsDragging(true);
    setMousePos(null);
  }, []); // stable — reads current values via refs

  // Global drag listeners — attach to window only while dragging
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.type === "bothPan") {
        setPanOffset(d.startOffset + Math.round((e.clientX - d.startX) * 0.3));
        setPricePanPct(Math.max(-2, Math.min(2, d.startPan + (e.clientY - d.startY) * 0.002)));
      } else if (d.type === "pricePan") {
        setPricePanPct(Math.max(-2, Math.min(2, d.startPan + (e.clientY - d.startY) * 0.002)));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // Crosshair tracking (SVG only, skipped during drag)
  const handleMouseMove = (e) => {
    if (dragRef.current) return; // global handler owns drag movement
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setMousePos({ mx, my });
    });
  };

  // Mouse leave: clear crosshair only — do NOT touch dragRef
  const handleMouseLeave = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setMousePos(null);
  };

  // Touch handlers with drag support
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault(); // prevent browser touch gestures
    const touch = e.touches[0];
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = touch.clientX - rect.left;
    const rightAxisStart = rect.width - 75;
    if (x > rightAxisStart) {
      dragRef.current = { type: "pricePan", startY: touch.clientY, startPan: pricePanPctRef.current };
    } else {
      dragRef.current = { type: "bothPan", startX: touch.clientX, startY: touch.clientY, startOffset: panOffsetRef.current, startPan: pricePanPctRef.current };
    }
    setIsDragging(true);
    setMousePos(null);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (dragRef.current && e.touches.length === 1) {
      const touch = e.touches[0];
      const d = dragRef.current;
      if (d.type === "bothPan") {
        setPanOffset(d.startOffset + Math.round((touch.clientX - d.startX) * 0.3));
        setPricePanPct(Math.max(-2, Math.min(2, d.startPan + (touch.clientY - d.startY) * 0.002)));
      } else if (d.type === "pricePan") {
        setPricePanPct(Math.max(-2, Math.min(2, d.startPan + (touch.clientY - d.startY) * 0.002)));
      }
      return;
    }
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMousePos({ mx: touch.clientX - rect.left, my: touch.clientY - rect.top });
    }
  };

  const handleTouchEnd = () => {
    if (dragRef.current) {
      dragRef.current = null;
      setIsDragging(false);
    }
    setMousePos(null);
  };

  useEffect(() => {
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const handleDoubleClick = useCallback(() => {
    setZoom(1); setPanOffset(0); setPriceZoom(1); setPricePanPct(0);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.altKey && e.key === 'r') {
        e.preventDefault();
        setZoom(1); setPanOffset(0); setPriceZoom(1); setPricePanPct(0);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  // Scroll: chart area=time zoom, price axis=price zoom, alt/shift+scroll=pan price
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const rightAxisStart = rect.width - 75;
      if (x > rightAxisStart) {
        setPriceZoom(prev => {
          const next = e.deltaY < 0 ? prev * 1.12 : prev / 1.12;
          return Math.max(0.03, Math.min(next, 5));
        });
      } else if (e.altKey || e.shiftKey) {
        setPricePanPct(prev => Math.max(-2, Math.min(2, prev + e.deltaY * 0.0005)));
      } else {
        setZoom(prev => {
          const next = e.deltaY < 0 ? prev * 1.15 : prev / 1.15;
          return Math.max(0.15, Math.min(next, 30));
        });
      }
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [candles]);

  // Sensitivity presets: { gateLo, gateHi, pow }
  const HEAT_PRESETS = {
    context:    { gateLo: 0.10, gateHi: 0.22, pow: 0.65 },
    balanced:   { gateLo: 0.18, gateHi: 0.32, pow: 0.80 },
    highsignal: { gateLo: 0.30, gateHi: 0.45, pow: 0.90 },
  };
  const heatPreset = HEAT_PRESETS[heatSensitivity] || HEAT_PRESETS.balanced;

  // Color function: compressed thermal gradient — warm colors only for truly dense zones
  // 0.00-0.40 dark navy/blue, 0.40-0.55 blue/cyan, 0.55-0.70 cyan/green,
  // 0.70-0.85 green/yellow/orange, 0.85-1.00 orange/red
  const intensityToColor = (t) => {
    t = Math.max(0, Math.min(1, t));
    const stops = [
      [0.000, 3, 3, 20],   [0.100, 4, 4, 40],   [0.200, 5, 8, 70],
      [0.300, 0, 0, 120],   [0.400, 0, 40, 200],
      [0.450, 0, 100, 255],  [0.500, 0, 180, 255], [0.550, 0, 230, 220],
      [0.600, 0, 240, 140],  [0.650, 40, 255, 40], [0.700, 140, 255, 0],
      [0.750, 220, 255, 0],  [0.800, 255, 220, 0], [0.850, 255, 160, 0],
      [0.900, 255, 80, 0],   [0.950, 255, 20, 0],  [1.000, 255, 30, 30],
    ];
    let i0 = 0;
    for (let j = 0; j < stops.length - 1; j++) {
      if (t >= stops[j][0] && t <= stops[j + 1][0]) { i0 = j; break; }
    }
    if (t >= stops[stops.length - 1][0]) i0 = stops.length - 2;
    const s0 = stops[i0], s1 = stops[i0 + 1];
    const frac = s1[0] === s0[0] ? 0 : (t - s0[0]) / (s1[0] - s0[0]);
    return [
      Math.round(s0[1] + frac * (s1[1] - s0[1])),
      Math.round(s0[2] + frac * (s1[2] - s0[2])),
      Math.round(s0[3] + frac * (s1[3] - s0[3]))
    ];
  };

  // Crosshair: sorted levels for binary search (must be before early return for hooks rules)
  const sortedLevels = useMemo(() => {
    if (!heatmapData?.levels) return [];
    return [...heatmapData.levels].sort((a, b) => a.price - b.price);
  }, [heatmapData]);

  // Memoize max liq notional — computed once, not per-bubble (FIX 3)
  const maxLiqNtl = useMemo(() => {
    if (!liqEvents || liqEvents.length === 0) return 1;
    return Math.max(...liqEvents.map(e => e.notional), 1);
  }, [liqEvents]);

  const heatmapLoading = !candles || !heatmapData?.levels;

  const levels = heatmapLoading ? [] : heatmapData.levels;
  const cp = heatmapData?.current_price || currentPrice;
  const W = chartWidth, H = Math.max(window.innerHeight - 140, 500);
  const padL = 75, padR = 75, padT = 40, padB = 50;
  const chartW = W - padL - padR, chartH = H - padT - padB;

  const allCandleData = (candles || []).map(c => ({
    t: c.t, T: c.T, o: parseFloat(c.o), c: parseFloat(c.c),
    h: parseFloat(c.h), l: parseFloat(c.l), v: parseFloat(c.v)
  }));

  const visibleCount = Math.max(Math.round(allCandleData.length / zoom), 10);
  const maxOffset = allCandleData.length - visibleCount;
  const clampedOffset = Math.max(0, Math.min(maxOffset, maxOffset - panOffset));
  const candleData = allCandleData.slice(clampedOffset, clampedOffset + visibleCount);

  const candleW = chartW / candleData.length;
  const candleHigh = Math.max(...candleData.map(c => c.h), cp * 1.005);
  const candleLow = Math.min(...candleData.map(c => c.l), cp * 0.995);
  const priceMid = (candleHigh + candleLow) / 2;
  const priceSpan = (candleHigh - candleLow) * 1.02;
  const adjustedSpan = priceSpan / priceZoom;
  // Apply vertical pan offset
  const panShift = adjustedSpan * pricePanPct;
  const priceHigh = priceMid + panShift + adjustedSpan / 2;
  const priceLow = priceMid + panShift - adjustedSpan / 2;
  const priceToY = (p) => padT + chartH - ((p - priceLow) / (priceHigh - priceLow)) * chartH;

  // Time range of visible candles
  const timeStart = candleData[0]?.t || 0;
  const timeEnd = candleData[candleData.length - 1]?.T || 0;
  const candleDurMs = INTERVAL_MS[interval] || 3600000;
  const heatgridPadMs = candleDurMs * 2; // FIX 8: interval-aware pad

  // Determine if we have 2D heatgrid data that overlaps with visible candles
  const hasHeatgrid = heatgrid?.grid?.length > 1 && heatgrid.grid[0][0] <= timeEnd && heatgrid.grid[heatgrid.grid.length - 1][0] >= timeStart;

  // Precompute visible heatgrid columns once (FIX 3, 8) — memoized to stabilize identity
  const visibleGrid = useMemo(() => (
    hasHeatgrid
      ? heatgrid.grid.filter(([ts]) => ts >= timeStart - heatgridPadMs && ts <= timeEnd + heatgridPadMs)
      : []
  ), [hasHeatgrid, heatgrid, timeStart, timeEnd, heatgridPadMs]);

  // Precompute median heatgrid spacing for gap detection (FIX 11)
  const medianGridSpacing = (() => {
    if (visibleGrid.length < 3) return 900000;
    const spacings = [];
    for (let i = 1; i < visibleGrid.length; i++) spacings.push(visibleGrid[i][0] - visibleGrid[i - 1][0]);
    spacings.sort((a, b) => a - b);
    return spacings[Math.floor(spacings.length / 2)];
  })();

  // Precompute P95 intensity threshold for scaling (FIX 10)
  const p95Threshold = (() => {
    if (scalingMode !== "p95" || visibleGrid.length === 0) return 1.0;
    const allVals = [];
    for (const [, buckets] of visibleGrid) {
      for (const v of buckets) { if (v > 0.01) allVals.push(v); }
    }
    if (allVals.length === 0) return 1.0;
    allVals.sort((a, b) => a - b);
    return allVals[Math.floor(allVals.length * 0.95)] || 1.0;
  })();

  // Build candle timestamp index for O(1) bubble placement (FIX 1, 3)
  const candleByTime = (() => {
    const map = {};
    for (let i = 0; i < candleData.length; i++) {
      const c = candleData[i];
      map[i] = c;
    }
    return { data: candleData };
  })();

  // Pre-bin bubbles to candle indices (FIX 1, 3, 5, 9)
  const visibleBubbles = (() => {
    const threshold = candleDurMs * 1.5;
    const filtered = liqEvents.filter(ev => {
      if (ev.liqPx < priceLow || ev.liqPx > priceHigh) return false;
      if (sideFilter === "long" && ev.side !== "long") return false;
      if (sideFilter === "short" && ev.side !== "short") return false;
      return true;
    });
    return filtered.map(ev => {
      // FIX 1: first try exact bar match [candle.t, candle.T]
      let bestIdx = -1, bestDist = Infinity;
      for (let ci = 0; ci < candleData.length; ci++) {
        const c = candleData[ci];
        if (ev.timestamp >= c.t && ev.timestamp <= (c.T || c.t + candleDurMs)) {
          bestIdx = ci; bestDist = 0; break;
        }
        const dist = Math.abs(c.t - ev.timestamp);
        if (dist < bestDist) { bestDist = dist; bestIdx = ci; }
      }
      if (bestIdx < 0 || bestDist > threshold) return null;
      return { ...ev, candleIdx: bestIdx };
    }).filter(Boolean);
  })();

  // Crosshair: derive display values from raw pixel coords (plain const, not useMemo — inputs are render-local)
  const crosshair = (() => {
    if (!mousePos) return null;
    const { mx, my } = mousePos;
    if (mx < padL || mx > padL + chartW || my < padT || my > padT + chartH) return null;
    const price = priceHigh - ((my - padT) / chartH) * (priceHigh - priceLow);
    const candleFrac = (mx - padL) / chartW;
    const candleIdx = Math.min(candleData.length - 1, Math.max(0, Math.floor(candleFrac * candleData.length)));
    const snappedX = padL + (candleIdx + 0.5) * (chartW / candleData.length);
    const LIQ_PROXIMITY_THRESHOLD = 0.02;
    let liqAtPrice = null;
    const result = findNearestLevel(sortedLevels, price);
    if (result && result.dist < (priceHigh - priceLow) * LIQ_PROXIMITY_THRESHOLD) {
      liqAtPrice = result.level;
    }
    return { x: snappedX, y: my, price, candleIdx, liqAtPrice };
  })();

  // Crosshair JSX
  const crosshairJSX = (() => {
    if (!crosshair) return null;
    const { x, y, price, candleIdx, liqAtPrice } = crosshair;
    const candle = candleData[candleIdx];
    const priceLabel = fmtTooltipPrice(price);
    const timeLabel = candle ? fmtTooltipTime(candle.t, interval) : "";
    const cpY = priceToY(cp);
    const showPriceLabel = Math.abs(y - cpY) >= 22;
    const tooltipW = 175;
    const hasLiq = liqAtPrice && (liqAtPrice.long_ntl > 0 || liqAtPrice.short_ntl > 0);
    const tooltipH = hasLiq ? 118 : 90;
    let tx = x + 14;
    let ty = y - tooltipH - 10;
    if (tx + tooltipW > padL + chartW) tx = x - tooltipW - 14;
    if (ty < padT) ty = y + 14;
    if (ty + tooltipH > padT + chartH) ty = padT + chartH - tooltipH - 4;
    const timeTooltipW = 76;
    const timeTooltipX = Math.max(padL, Math.min(padL + chartW - timeTooltipW, x - timeTooltipW / 2));

    return (
      <g style={{ pointerEvents: "none" }}>
        <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke="#718096" strokeWidth="0.5" strokeDasharray="3,3" opacity="0.7" />
        <line x1={x} y1={padT} x2={x} y2={padT + chartH} stroke="#718096" strokeWidth="0.5" strokeDasharray="3,3" opacity="0.7" />
        {showPriceLabel && (
          <>
            <rect x={W - padR + 2} y={y - 10} width={padR - 4} height={20} rx={3} fill="#1e3050" stroke="#4da2ff" strokeWidth="0.5" />
            <text x={W - padR + padR / 2} y={y + 4} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600" fontFamily="monospace">{priceLabel}</text>
          </>
        )}
        {timeLabel && (
          <>
            <rect x={timeTooltipX} y={padT + chartH + 2} width={timeTooltipW} height={18} rx={3} fill="#1e3050" stroke="#4da2ff" strokeWidth="0.5" />
            <text x={timeTooltipX + timeTooltipW / 2} y={padT + chartH + 14} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600" fontFamily="monospace">{timeLabel}</text>
          </>
        )}
        {candle && (
          <g>
            <rect x={tx} y={ty} width={tooltipW} height={tooltipH} rx={4} fill="rgba(10,14,20,0.92)" stroke="#1e3050" strokeWidth="1" />
            <text x={tx + 8} y={ty + 15} fill="#718096" fontSize="8" fontFamily="monospace">
              O <tspan fill={candle.c >= candle.o ? "#4dff91" : "#ff6b6b"}>{fmtTooltipPrice(candle.o)}</tspan>
              {"  "}H <tspan fill="#e2e8f0">{fmtTooltipPrice(candle.h)}</tspan>
            </text>
            <text x={tx + 8} y={ty + 28} fill="#718096" fontSize="8" fontFamily="monospace">
              L <tspan fill="#e2e8f0">{fmtTooltipPrice(candle.l)}</tspan>
              {"  "}C <tspan fill={candle.c >= candle.o ? "#4dff91" : "#ff6b6b"}>{fmtTooltipPrice(candle.c)}</tspan>
            </text>
            <text x={tx + 8} y={ty + 41} fill="#718096" fontSize="8" fontFamily="monospace">
              Vol <tspan fill="#4da2ff">{candle.v >= 1e6 ? (candle.v / 1e6).toFixed(1) + "M" : candle.v >= 1e3 ? (candle.v / 1e3).toFixed(0) + "K" : candle.v.toFixed(0)}</tspan>
            </text>
            {(() => {
              const pct = candle.o > 0 ? ((candle.c - candle.o) / candle.o * 100) : 0;
              const color = pct >= 0 ? "#4dff91" : "#ff6b6b";
              return (
                <text x={tx + 8} y={ty + 54} fill="#718096" fontSize="8" fontFamily="monospace">
                  Chg <tspan fill={color}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</tspan>
                </text>
              );
            })()}
            <line x1={tx + 6} y1={ty + 60} x2={tx + tooltipW - 6} y2={ty + 60} stroke="#1e3050" strokeWidth="0.5" />
            <text x={tx + 8} y={ty + 74} fill="#718096" fontSize="8" fontFamily="monospace">
              Price <tspan fill="#e2e8f0">{priceLabel}</tspan>
            </text>
            <text x={tx + 8} y={ty + 86} fill="#718096" fontSize="8" fontFamily="monospace">
              Time <tspan fill="#e2e8f0">{timeLabel}</tspan>
            </text>
            {hasLiq && (
              <>
                <line x1={tx + 6} y1={ty + 92} x2={tx + tooltipW - 6} y2={ty + 92} stroke="#1e3050" strokeWidth="0.5" />
                <text x={tx + 8} y={ty + 106} fill="#718096" fontSize="8" fontFamily="monospace">
                  Liq L <tspan fill="#ff6b6b">{fmt(liqAtPrice.long_ntl)}</tspan>
                  {"  "}S <tspan fill="#4dff91">{fmt(liqAtPrice.short_ntl)}</tspan>
                </text>
              </>
            )}
          </g>
        )}
      </g>
    );
  })();

  // Memoize heavy heatgrid rect computation — avoids recomputing on every mouse move
  const heatgridRects = useMemo(() => {
    if (!hasHeatgrid || visibleGrid.length === 0) return null;
    const gridPriceLow = heatgrid.price_low;
    const gridPriceHigh = heatgrid.price_high;
    const numGridBuckets = heatgrid.price_buckets;
    const bucketH = (gridPriceHigh - gridPriceLow) / numGridBuckets;
    const timeRange = timeEnd - timeStart;
    if (timeRange <= 0) return null;

    const defaultInterval = visibleGrid.length > 1 ? visibleGrid[1][0] - visibleGrid[0][0] : 900000;
    const gridTsStart = visibleGrid[0][0];
    const gridTsEnd = visibleGrid[visibleGrid.length - 1][0] + defaultInterval;
    const gridX1 = Math.max(padL, padL + ((gridTsStart - timeStart) / timeRange) * chartW);
    const gridX2 = Math.min(padL + chartW, padL + ((gridTsEnd - timeStart) / timeRange) * chartW);
    const gridW = gridX2 - gridX1;
    if (gridW < 2) return null;

    const rects = [
      <rect key="hg_mask" x={gridX1} y={padT} width={gridW} height={chartH} fill="#060a0f" />
    ];

    const zoomedOut = timeRange > 7 * 24 * 3600 * 1000;
    const CELL_PX = zoomedOut ? 5 : 3;
    const numRenderCols = Math.max(1, Math.min(visibleGrid.length, Math.floor(gridW / CELL_PX)));
    const numRenderRows = Math.max(1, Math.floor(chartH / CELL_PX));
    const renderColW = gridW / numRenderCols;
    const renderRowH = chartH / numRenderRows;
    const rowPriceSpan = (priceHigh - priceLow) / numRenderRows;

    const gridTsSpan = gridTsEnd - gridTsStart;
    const colSlices = new Array(numRenderCols).fill(null).map(() => []);
    for (let si = 0; si < visibleGrid.length; si++) {
      const frac = (visibleGrid[si][0] - gridTsStart) / gridTsSpan;
      const col = Math.min(numRenderCols - 1, Math.max(0, Math.floor(frac * numRenderCols)));
      colSlices[col].push(si);
    }

    const useP95 = scalingMode === "p95" && p95Threshold < 1.0;
    const cells = [];
    for (let ci = 0; ci < numRenderCols; ci++) {
      if (colSlices[ci].length === 0) continue;
      const x = gridX1 + ci * renderColW;
      const w = Math.ceil(renderColW) + 2;

      for (let ri = 0; ri < numRenderRows; ri++) {
        const rowPriceLo = priceLow + ri * rowPriceSpan;
        const rowPriceHi = rowPriceLo + rowPriceSpan;
        if (rowPriceHi < gridPriceLow || rowPriceLo > gridPriceHigh) continue;

        const biLo = Math.max(0, Math.floor((rowPriceLo - gridPriceLow) / bucketH));
        const biHi = Math.min(numGridBuckets - 1, Math.ceil((rowPriceHi - gridPriceLow) / bucketH));

        let maxVal = 0;
        for (const si of colSlices[ci]) {
          const buckets = visibleGrid[si][1];
          for (let bi = biLo; bi <= biHi; bi++) {
            if (buckets[bi] > maxVal) maxVal = buckets[bi];
          }
        }
        if (useP95) maxVal = Math.min(maxVal / p95Threshold, 1.0);

        if (maxVal < heatPreset.gateLo) continue;
        const fadeAlpha = maxVal < heatPreset.gateHi ? (maxVal - heatPreset.gateLo) / (heatPreset.gateHi - heatPreset.gateLo) : 1.0;

        const y = padT + chartH - (ri + 1) * renderRowH;
        cells.push({ ci, ri, maxVal, fadeAlpha, x, y, w, h: Math.ceil(renderRowH) });
      }
    }

    const cellSet = new Set(cells.map(c => `${c.ci},${c.ri}`));
    const filtered = cells.filter(c => {
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          if (cellSet.has(`${c.ci + dc},${c.ri + dr}`)) return true;
        }
      }
      return false;
    });

    filtered.sort((a, b) => a.maxVal - b.maxVal);
    const nf = filtered.length;
    filtered.forEach((cell, idx) => {
      cell.pctRank = nf > 1 ? idx / (nf - 1) : 0.5;
      cell.pctRank = Math.pow(cell.pctRank, 1.3);
    });

    for (const cell of filtered) {
      const intensity = Math.pow(cell.pctRank, heatPreset.pow);
      const [r, g, b] = intensityToColor(intensity);
      const baseAlpha = 0.12 + intensity * 0.55;
      const a = Math.min(baseAlpha * cell.fadeAlpha, 0.65);
      rects.push(<rect key={`hg${cell.ci}_${cell.ri}`} x={cell.x} y={cell.y} width={cell.w} height={cell.h} fill={`rgba(${r},${g},${b},${a})`} />);
    }
    return rects;
  }, [heatgrid, visibleGrid, scalingMode, p95Threshold, heatSensitivity,
      timeStart, timeEnd, priceLow, priceHigh, chartW, chartH, padL, padT]);

  // Net liquidation pull indicator — computed from heatgrid at 3 distances
  const netPullBars = useMemo(() => {
    if (!hasHeatgrid || !heatgrid?.grid?.length || !cp) return null;
    const gridPriceLow = heatgrid.price_low;
    const gridPriceHigh = heatgrid.price_high;
    const numBuckets = heatgrid.price_buckets;
    const bucketH = (gridPriceHigh - gridPriceLow) / numBuckets;
    if (bucketH <= 0) return null;

    const distances = [0.005, 0.01, 0.02]; // 0.5%, 1%, 2%
    return distances.map(dist => {
      const upperBound = cp * (1 + dist);
      const lowerBound = cp * (1 - dist);
      let above = 0, below = 0;
      for (const col of visibleGrid) {
        const buckets = col[1];
        for (let bi = 0; bi < numBuckets; bi++) {
          const bPrice = gridPriceLow + (bi + 0.5) * bucketH;
          const val = buckets[bi] || 0;
          if (val <= 0) continue;
          if (bPrice > cp && bPrice <= upperBound) above += val;
          else if (bPrice < cp && bPrice >= lowerBound) below += val;
        }
      }
      const total = above + below;
      const ratio = total > 0 ? (above - below) / total : 0;
      return { dist, ratio, above, below };
    });
  }, [heatgrid, visibleGrid, hasHeatgrid, cp]);

  if (heatmapLoading) return (
    <div style={{ color: "#718096", textAlign: "center", padding: 40, animation: "fadeIn 0.3s ease" }}>Loading heatmap...</div>
  );

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2 }}>LIQUIDATION HEATMAP — {asset} @ ${cp?.toLocaleString()}</div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {["5m", "15m", "30m", "1h", "4h", "1d", "1w"].map(iv => (
            <button key={iv} onClick={() => setInterval_(iv)} style={{
              background: interval === iv ? assetColor + "30" : "transparent",
              border: `1px solid ${interval === iv ? assetColor : "#1a2030"}`,
              color: interval === iv ? assetColor : "#718096",
              padding: "4px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", fontFamily: "monospace"
            }}>{iv.toUpperCase()}</button>
          ))}
          {/* Side filter toggle (FIX 9) */}
          <span style={{ marginLeft: 12, display: "flex", gap: 2 }}>
            {["all", "long", "short"].map(s => (
              <button key={s} onClick={() => setSideFilter(s)} style={{
                background: sideFilter === s ? (s === "long" ? "#ff6b6b20" : s === "short" ? "#4dff9120" : "#4da2ff20") : "transparent",
                border: `1px solid ${sideFilter === s ? (s === "long" ? "#ff6b6b" : s === "short" ? "#4dff91" : "#4da2ff") : "#1a2030"}`,
                color: sideFilter === s ? (s === "long" ? "#ff6b6b" : s === "short" ? "#4dff91" : "#4da2ff") : "#4a5568",
                padding: "2px 6px", fontSize: 9, borderRadius: 3, cursor: "pointer", fontFamily: "monospace", textTransform: "uppercase"
              }}>{s === "all" ? "ALL" : s === "long" ? "L" : "S"}</button>
            ))}
          </span>
          {/* Scaling mode toggle (FIX 10) */}
          <button onClick={() => setScalingMode(prev => prev === "p95" ? "absolute" : "p95")} style={{
            background: "transparent", border: "1px solid #1a2030", color: "#4a5568",
            padding: "2px 6px", fontSize: 9, borderRadius: 3, cursor: "pointer", fontFamily: "monospace", marginLeft: 4
          }}>{scalingMode === "p95" ? "P95" : "ABS"}</button>
          {/* Sensitivity presets */}
          <span style={{ marginLeft: 8, display: "flex", gap: 2 }}>
            {[["context", "CTX"], ["balanced", "BAL"], ["highsignal", "SIG"]].map(([key, label]) => (
              <button key={key} onClick={() => setHeatSensitivity(key)} style={{
                background: heatSensitivity === key ? "#4da2ff20" : "transparent",
                border: `1px solid ${heatSensitivity === key ? "#4da2ff" : "#1a2030"}`,
                color: heatSensitivity === key ? "#4da2ff" : "#4a5568",
                padding: "2px 6px", fontSize: 9, borderRadius: 3, cursor: "pointer", fontFamily: "monospace"
              }}>{label}</button>
            ))}
          </span>
          <button onClick={() => setShowStructural(v => !v)} style={{
            background: showStructural ? "#ffffff20" : "transparent",
            border: `1px solid ${showStructural ? "#ffffff60" : "#1a2030"}`,
            color: showStructural ? "#ffffffcc" : "#4a5568",
            padding: "2px 6px", fontSize: 9, borderRadius: 3, cursor: "pointer", fontFamily: "monospace", marginLeft: 4
          }}>STR</button>
          <button onClick={() => setShowQuality(v => !v)} style={{
            background: showQuality ? "#ffd70020" : "transparent",
            border: `1px solid ${showQuality ? "#ffd70060" : "#1a2030"}`,
            color: showQuality ? "#ffd700cc" : "#4a5568",
            padding: "2px 6px", fontSize: 9, borderRadius: 3, cursor: "pointer", fontFamily: "monospace", marginLeft: 4
          }}>QLT</button>
          {hasHeatgrid && <span style={{ color: "#4da2ff", fontSize: 9, marginLeft: 8 }}>â—‰ HEATGRID ({heatgrid.time_slices})</span>}
          {/* Data age indicator (FIX 4) */}
          {heatgridAge && <span style={{ color: "#4a5568", fontSize: 8, marginLeft: 6 }}>
            {Math.floor((Date.now() - Math.min(heatgridAge || Date.now(), liqEventsAge || Date.now())) / 1000)}s ago
          </span>}
        </div>
      </div>
      <div ref={containerRef} style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", cursor: "crosshair", position: "relative", width: "100%", touchAction: "none" }}>
        <svg ref={svgRef} width={W} height={H} style={{ display: "block", cursor: isDragging ? "grabbing" : "crosshair" }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onDoubleClick={handleDoubleClick}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          <rect x={padL} y={padT} width={chartW} height={chartH} fill="#060a0f" style={{ cursor: "grab" }} />
          {/* Right price axis — scroll to zoom, drag to pan */}
          <rect x={W - padR} y={padT} width={padR} height={chartH} fill="#0d1520" style={{ cursor: "ns-resize" }} />
          <line x1={W - padR} y1={padT} x2={W - padR} y2={padT + chartH} stroke="#1e3050" strokeWidth="2" />

          {/* Static heatmap bands (base layer — only where no heatgrid data) */}
          {(() => {
            const maxNtl = Math.max(...levels.map(l => {
              if (sideFilter === "long") return l.long_ntl;
              if (sideFilter === "short") return l.short_ntl;
              return l.long_ntl + l.short_ntl;
            }), 1);
            const heatgridStartX = visibleGrid.length > 0
              ? Math.max(padL, padL + ((visibleGrid[0][0] - timeStart) / (timeEnd - timeStart)) * chartW)
              : padL + chartW;
            const staticW = Math.max(heatgridStartX - padL, 0);
            if (staticW < 2) return null;
            return levels.map((l, li) => {
              const totalNtl = sideFilter === "long" ? l.long_ntl : sideFilter === "short" ? l.short_ntl : l.long_ntl + l.short_ntl;
              if (totalNtl <= 0) return null;
              const intensity = Math.pow(totalNtl / maxNtl, 0.45);
              const price = l.price;
              if (price < priceLow || price > priceHigh) return null;
              const y = priceToY(price);
              const bandH = Math.max(chartH / levels.length * 1.5, 3);
              const [r, g, b] = intensityToColor(intensity);
              const a = Math.min(0.15 + intensity * 0.35, 0.5);
              return <rect key={"hm" + li} x={padL} y={y - bandH / 2} width={staticW} height={bandH} fill={`rgba(${r},${g},${b},${a})`} />;
            });
          })()}

          {/* 2D Heatgrid overlay (memoized above) */}
          {heatgridRects}

          {/* Data gap markers — dashed lines where heatgrid time slices are >30min apart */}
          {hasHeatgrid && visibleGrid.length > 1 && (() => {
            const timeRange = timeEnd - timeStart;
            if (timeRange <= 0) return null;
            const GAP_MS = 1800000; // 30 min
            const markers = [];
            for (let i = 1; i < visibleGrid.length; i++) {
              const gap = visibleGrid[i][0] - visibleGrid[i - 1][0];
              if (gap > GAP_MS) {
                const gapMid = visibleGrid[i - 1][0] + gap / 2;
                const gapX1 = padL + ((visibleGrid[i - 1][0] - timeStart) / timeRange) * chartW;
                const gapX2 = padL + ((visibleGrid[i][0] - timeStart) / timeRange) * chartW;
                const x = padL + ((gapMid - timeStart) / timeRange) * chartW;
                if (x < padL || x > padL + chartW) continue;
                const gapSec = Math.round(gap / 1000);
                const gapH = Math.floor(gapSec / 3600);
                const gapM = Math.floor((gapSec % 3600) / 60);
                const durStr = gapH > 0 ? `${gapH}h ${gapM}m` : `${gapM}m`;
                markers.push(
                  <g key={"gap" + i}>
                    <rect x={Math.max(padL, gapX1)} y={padT} width={Math.min(gapX2 - gapX1, padL + chartW - gapX1)} height={chartH} fill="rgba(15,20,30,0.4)" />
                    <line x1={x} y1={padT} x2={x} y2={padT + chartH} stroke="#3a3a3a" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                    <rect x={x - 22} y={padT + chartH / 2 - 10} width={44} height={20} rx={3} fill="rgba(10,14,20,0.85)" stroke="#3a3a3a" strokeWidth="0.5" />
                    <text x={x} y={padT + chartH / 2 - 1} textAnchor="middle" fill="#666" fontSize="8" fontFamily="monospace">feed gap</text>
                    <text x={x} y={padT + chartH / 2 + 9} textAnchor="middle" fill="#555" fontSize="7" fontFamily="monospace">{durStr}</text>
                  </g>
                );
              }
            }
            return markers;
          })()}

          {/* Top liquidation level bands with labels */}
          {heatgrid?.top_levels?.length > 0 && (() => {
            const topLevels = heatgrid.top_levels;
            const above = topLevels.filter(l => l.price > cp).sort((a, b) => b.intensity - a.intensity);
            const below = topLevels.filter(l => l.price <= cp).sort((a, b) => b.intensity - a.intensity);
            // Pick 5 total: more from whichever side has higher density
            const aboveTotal = above.reduce((s, l) => s + l.intensity, 0);
            const belowTotal = below.reduce((s, l) => s + l.intensity, 0);
            let picked;
            if (aboveTotal >= belowTotal) {
              picked = [...above.slice(0, 3), ...below.slice(0, 2)];
            } else {
              picked = [...below.slice(0, 3), ...above.slice(0, 2)];
            }
            if (picked.length < 5) picked = topLevels.slice(0, Math.min(5, topLevels.length));
            // Identify nearest above and nearest below current price by distance_pct
            const nearestAbove = picked.filter(l => l.price > cp).sort((a, b) => a.distance_pct - b.distance_pct)[0];
            const nearestBelow = picked.filter(l => l.price <= cp).sort((a, b) => a.distance_pct - b.distance_pct)[0];
            const nearestSet = new Set([nearestAbove, nearestBelow].filter(Boolean));

            const usedY = []; // prevent label overlap
            return picked.map((lv, i) => {
              if (lv.price < priceLow || lv.price > priceHigh) return null;
              const y = priceToY(lv.price);
              if (y < padT + 16 || y > padT + chartH - 8) return null;
              // skip if too close to another label
              if (usedY.some(uy => Math.abs(uy - y) < 16)) return null;
              usedY.push(y);
              const cpY = priceToY(cp);
              if (Math.abs(y - cpY) < 20) return null; // don't overlap current price
              const isLong = lv.side === "long";
              const color = isLong ? "#ff6b6b" : "#4dff91";
              const isNearest = nearestSet.has(lv);
              const bandFillOpacity = isNearest ? 0.10 : 0.06;
              const lineStrokeOpacity = isNearest ? 0.5 : 0.3;
              const bandH = isNearest ? 10 : 6;
              const bandFill = isLong ? `rgba(255,107,107,${bandFillOpacity})` : `rgba(77,255,145,${bandFillOpacity})`;
              const lineStroke = isLong ? `rgba(255,107,107,${lineStrokeOpacity})` : `rgba(77,255,145,${lineStrokeOpacity})`;
              const fmtP = lv.price >= 10000 ? "$" + Math.round(lv.price).toLocaleString() : "$" + lv.price.toFixed(1);
              const fmtN = lv.notional >= 1e6 ? "$" + (lv.notional / 1e6).toFixed(1) + "M" : "$" + (lv.notional / 1e3).toFixed(0) + "K";
              const label = fmtP + " \u00b7 " + fmtN + " \u00b7 " + lv.distance_pct.toFixed(1) + "%";
              const labelW = label.length * 5 + 10;
              const glowFill = isLong ? "rgba(255,107,107,0.03)" : "rgba(77,255,145,0.03)";
              return (
                <g key={"tl" + i}>
                  {isNearest && <rect x={padL} y={y - bandH} width={chartW} height={bandH * 2} fill={glowFill} />}
                  <rect x={padL} y={y - bandH / 2} width={chartW} height={bandH} fill={bandFill} />
                  <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={lineStroke} strokeWidth="0.7" strokeDasharray="6,3" />
                  <rect x={padL + chartW - labelW - 2} y={y - 8} width={labelW} height={14} rx={2} fill="rgba(6,10,15,0.88)" stroke={lineStroke} strokeWidth="0.5" />
                  <text x={padL + chartW - 5} y={y + 3} textAnchor="end" fill={color} fontSize="8" fontFamily="monospace" opacity="0.9">{label}</text>
                </g>
              );
            });
          })()}

          {/* Structural levels from magnet metric */}
          {showStructural && analysisData?.liquidity_magnet?.magnets?.length > 0 && (() => {
            const magnets = analysisData.liquidity_magnet.magnets;
            const usedY = [];
            return magnets.map((m, i) => {
              if (m.tf_weight <= 1) return null; // skip session levels
              const price = m.level_price;
              if (price < priceLow || price > priceHigh) return null;
              const y = priceToY(price);
              if (y < padT + 5 || y > padT + chartH - 5) return null;
              if (usedY.some(uy => Math.abs(uy - y) < 10)) return null;
              usedY.push(y);
              const isMonthly = m.tf_weight >= 4;
              const isWeekly = m.tf_weight >= 3 && m.tf_weight < 4;
              const strokeW = isMonthly ? 1.5 : isWeekly ? 1 : 0.8;
              const opacity = isMonthly ? 0.4 : isWeekly ? 0.25 : 0.15;
              const dash = isMonthly ? "none" : isWeekly ? "6,4" : "2,3";
              const prefix = isMonthly ? "M" : isWeekly ? "W" : "D";
              const fmtP = price >= 10000 ? "$" + Math.round(price).toLocaleString() : "$" + price.toFixed(1);
              const label = prefix + " " + fmtP;
              return (
                <g key={"str" + i}>
                  <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke="#ffffff" strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity} />
                  <rect x={padL + 2} y={y - 6} width={label.length * 5.5 + 6} height={12} rx={2} fill="rgba(6,10,15,0.85)" />
                  <text x={padL + 5} y={y + 3} fill="#ffffffaa" fontSize="8" fontFamily="monospace">{label}</text>
                </g>
              );
            });
          })()}

          {/* Quality-scored persistence levels */}
          {showQuality && qualityLevels?.levels?.length > 0 && (() => {
            const levels = qualityLevels.levels.filter(lv => lv.quality > 0.5);
            const usedY = [];
            return levels.map((lv, i) => {
              const price = lv.price;
              if (price < priceLow || price > priceHigh) return null;
              const y = priceToY(price);
              if (y < padT + 5 || y > padT + chartH - 5) return null;
              if (usedY.some(uy => Math.abs(uy - y) < 8)) return null;
              usedY.push(y);
              const isLong = lv.side === "long";
              const baseColor = isLong ? "255,107,107" : "77,255,145";
              const opacity = 0.15 + lv.quality * 0.55;
              const strokeW = 0.6 + lv.quality * 1.2;
              const fmtP = price >= 10000 ? "$" + Math.round(price).toLocaleString() : "$" + price.toFixed(1);
              const qPct = Math.round(lv.quality * 100);
              const label = `Q${qPct} ${fmtP} ${fmt(lv.notional)}`;
              return (
                <g key={"qlt" + i}>
                  <line x1={padL} y1={y} x2={padL + chartW} y2={y}
                    stroke={`rgba(${baseColor},${opacity})`} strokeWidth={strokeW}
                    strokeDasharray={lv.growing ? "none" : "4,4"} />
                  {lv.growing && <line x1={padL} y1={y} x2={padL + chartW} y2={y}
                    stroke={`rgba(${baseColor},0.12)`} strokeWidth={strokeW + 3} />}
                  <rect x={padL + chartW - label.length * 5.2 - 10} y={y - 7} width={label.length * 5.2 + 8} height={14} rx={2}
                    fill="rgba(6,10,15,0.88)" stroke={`rgba(${baseColor},0.4)`} strokeWidth="0.5" />
                  <text x={padL + chartW - 5} y={y + 3} textAnchor="end"
                    fill={`rgba(${baseColor},0.9)`} fontSize="8" fontFamily="monospace">{label}</text>
                </g>
              );
            });
          })()}

          {/* Spoof flash markers from recently removed levels */}
          {showQuality && qualityLevels?.removed_recent?.length > 0 && (() => {
            const now = Date.now();
            return qualityLevels.removed_recent
              .filter(r => r.spoof_risk && (now - r.removed_at) < 3600000) // show for 1h
              .map((r, i) => {
                const price = r.price;
                if (price < priceLow || price > priceHigh) return null;
                const y = priceToY(price);
                if (y < padT || y > padT + chartH) return null;
                const age = (now - r.removed_at) / 3600000;
                const fadeOpacity = Math.max(0.15, 1 - age);
                return (
                  <g key={"spf" + i} opacity={fadeOpacity}>
                    <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke="rgba(255,60,60,0.4)" strokeWidth="0.8" strokeDasharray="2,6" />
                    <rect x={padL + 2} y={y - 6} width={42} height={12} rx={2} fill="rgba(255,30,30,0.15)" stroke="rgba(255,60,60,0.3)" strokeWidth="0.5" />
                    <text x={padL + 5} y={y + 3} fill="#ff4444" fontSize="7" fontFamily="monospace" opacity="0.8">SPOOF</text>
                  </g>
                );
              });
          })()}

          {/* Candlesticks */}
          {candleData.map((c, i) => {
            const x = padL + (i / candleData.length) * chartW + candleW / 2;
            const isGreen = c.c >= c.o;
            const color = isGreen ? "#4dff91" : "#ff6b6b";
            const bodyTop = priceToY(Math.max(c.o, c.c));
            const bodyBot = priceToY(Math.min(c.o, c.c));
            const bodyH = Math.max(bodyBot - bodyTop, 1);
            return (
              <g key={"c" + i}>
                <line x1={x} y1={priceToY(c.h)} x2={x} y2={priceToY(c.l)} stroke={color} strokeWidth="1" opacity="0.6" />
                <rect x={x - candleW / 2} y={bodyTop} width={candleW} height={bodyH} fill={color} opacity="0.9" />
              </g>
            );
          })}

          {/* Liquidation bubbles — pre-binned, interval-aware, confidence-colored (FIX 1, 3, 5, 9) */}
          {visibleBubbles.map((ev, i) => {
            const x = padL + (ev.candleIdx / candleData.length) * chartW + candleW / 2;
            const y = priceToY(ev.liqPx);
            const sizeFactor = Math.sqrt(ev.notional / maxLiqNtl);
            const r = Math.max(3, Math.min(14, 3 + sizeFactor * 11));
            const isLong = ev.side === "long";
            const color = isLong ? "#ff6b6b" : "#4dff91";
            // FIX 5: confidence-based opacity
            const confOpacity = ev.confidence === "high" ? 1.0 : ev.confidence === "medium" ? 0.75 : 0.5;
            return (
              <g key={"liq" + i} opacity={confOpacity * 0.85}>
                <circle cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth="1.5" opacity="0.8" />
                <circle cx={x} cy={y} r={r * 0.4} fill={color} opacity="0.5" />
                <line x1={x - r * 0.5} y1={y - r * 0.5} x2={x + r * 0.5} y2={y + r * 0.5} stroke={color} strokeWidth="0.8" opacity="0.6" />
                <line x1={x + r * 0.5} y1={y - r * 0.5} x2={x - r * 0.5} y2={y + r * 0.5} stroke={color} strokeWidth="0.8" opacity="0.6" />
              </g>
            );
          })}

          {/* Sweep markers */}
          {recentSweeps.map((sw, i) => {
            const y = priceToY(sw.price);
            if (y < padT || y > padT + chartH) return null;
            // Find which candle contains this sweep timestamp
            const sweepCandleIdx = candleData.findIndex(
              c => sw.ts >= c.t && sw.ts <= (c.T || c.t + 300000)
            );
            if (sweepCandleIdx < 0) return null;
            const x = padL + (sweepCandleIdx / candleData.length) * chartW + candleW / 2;
            const color = sw.type === "low" ? "#ffd700" : "#ff00ff";
            const d = 5; // diamond half-size
            return (
              <g key={"sw" + i}>
                <polygon points={`${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`} fill={color} opacity="0.8" />
                <text x={x + d + 3} y={y + 3} fill={color} fontSize="7" fontFamily="monospace" opacity="0.9">SWEEP</text>
              </g>
            );
          })}

          {/* Net liquidation pull indicator */}
          {netPullBars && (() => {
            const barW = 8, barH = 50, gap = 3;
            const totalW = netPullBars.length * (barW + gap) - gap;
            const bx = padL + chartW - totalW - 8;
            const by = padT + 40;
            return (
              <g>
                <rect x={bx - 4} y={by - 14} width={totalW + 8} height={barH + 26} rx={3} fill="rgba(6,10,15,0.85)" stroke="#1a2030" strokeWidth="0.5" />
                <text x={bx + totalW / 2} y={by - 4} textAnchor="middle" fill="#718096" fontSize="7" fontFamily="monospace">PULL</text>
                {netPullBars.map((bar, i) => {
                  const x = bx + i * (barW + gap);
                  const midY = by + barH / 2;
                  const upH = Math.max(0, bar.ratio) * (barH / 2);
                  const dnH = Math.max(0, -bar.ratio) * (barH / 2);
                  const pct = Math.round(bar.ratio * 100);
                  const label = (bar.dist * 100).toFixed(1) + "%";
                  return (
                    <g key={"np" + i}>
                      <rect x={x} y={by} width={barW} height={barH} fill="#1a2030" rx={1} />
                      {upH > 0 && <rect x={x} y={midY - upH} width={barW} height={upH} fill="#4dff91" opacity="0.7" rx={1} />}
                      {dnH > 0 && <rect x={x} y={midY} width={barW} height={dnH} fill="#ff6b6b" opacity="0.7" rx={1} />}
                      <line x1={x} y1={midY} x2={x + barW} y2={midY} stroke="#ffffff30" strokeWidth="0.5" />
                      <text x={x + barW / 2} y={by + barH + 8} textAnchor="middle" fill="#4a5568" fontSize="6" fontFamily="monospace">{label}</text>
                      <text x={x + barW / 2} y={by + barH + 15} textAnchor="middle" fill={pct >= 0 ? "#4dff91" : "#ff6b6b"} fontSize="6" fontFamily="monospace">{pct > 0 ? "+" : ""}{pct}%</text>
                    </g>
                  );
                })}
              </g>
            );
          })()}

          {/* Current price line */}
          <line x1={padL} y1={priceToY(cp)} x2={padL + chartW} y2={priceToY(cp)} stroke="#f7931a" strokeWidth="1.5" strokeDasharray="4,3" />
          <rect x={W - padR + 4} y={priceToY(cp) - 9} width={68} height={18} rx={3} fill="#f7931a" />
          <text x={W - padR + 38} y={priceToY(cp) + 4} textAnchor="middle" fill="#000" fontSize="10" fontWeight="700" fontFamily="monospace">${cp?.toLocaleString()}</text>

          {/* Price grid: right-side labels, adaptive step like TradingView */}
          {(() => {
            const range = priceHigh - priceLow;
            const step = range > 50000 ? 10000 : range > 20000 ? 5000 : range > 10000 ? 2000 : range > 5000 ? 1000 : range > 2000 ? 500 : range > 800 ? 200 : range > 400 ? 100 : 50;
            const startP = Math.ceil(priceLow / step) * step;
            const ticks = [];
            for (let p = startP; p <= priceHigh; p += step) {
              const y = priceToY(p);
              if (y < padT + 10 || y > padT + chartH - 5) continue;
              ticks.push(
                <g key={"pg" + p}>
                  <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#141c28" strokeWidth="0.5" />
                  <text x={W - padR + 8} y={y + 3} textAnchor="start" fill="#718096" fontSize="9" fontFamily="monospace">${p.toLocaleString()}</text>
                </g>
              );
            }
            return ticks;
          })()}

          {/* Time axis: bottom labels + vertical grid lines */}
          {(() => {
            const timeRange = timeEnd - timeStart;
            const targetTicks = Math.max(4, Math.floor(chartW / 140));
            const ticks = [];
            for (let i = 0; i <= targetTicks; i++) {
              const t = timeStart + (timeRange * i / targetTicks);
              const x = padL + (chartW * i / targetTicks);
              const d = new Date(t);
              const mo = d.getMonth() + 1;
              const da = d.getDate();
              const hr = d.getHours();
              const mn = String(d.getMinutes()).padStart(2, "0");
              const label = (interval === "1d" || interval === "1w") ? (mo + "/" + da) : (mo + "/" + da + " " + hr + ":" + mn);
              ticks.push(
                <g key={"tg" + i}>
                  <line x1={x} y1={padT} x2={x} y2={padT + chartH} stroke="#141c28" strokeWidth="0.5" />
                  <text x={x} y={H - padB + 16} textAnchor="middle" fill="#718096" fontSize="9" fontFamily="monospace">{label}</text>
                </g>
              );
            }
            return ticks;
          })()}
          {/* Gradient Legend */}
          <defs>
            <linearGradient id="heatGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgb(3,3,20)" />
              <stop offset="10%" stopColor="rgb(4,4,40)" />
              <stop offset="20%" stopColor="rgb(5,8,70)" />
              <stop offset="30%" stopColor="rgb(0,0,120)" />
              <stop offset="40%" stopColor="rgb(0,40,200)" />
              <stop offset="50%" stopColor="rgb(0,180,255)" />
              <stop offset="60%" stopColor="rgb(0,240,140)" />
              <stop offset="70%" stopColor="rgb(140,255,0)" />
              <stop offset="80%" stopColor="rgb(255,220,0)" />
              <stop offset="90%" stopColor="rgb(255,80,0)" />
              <stop offset="100%" stopColor="rgb(255,30,30)" />
            </linearGradient>
          </defs>
          <rect x={padL + 10} y={padT + 8} width={180} height={10} rx={3} fill="url(#heatGrad)" />
          <text x={padL + 10} y={padT + 28} fill="#718096" fontSize="8" fontFamily="monospace">Low</text>
          <text x={padL + 95} y={padT + 28} fill="#718096" fontSize="8" fontFamily="monospace" textAnchor="middle">Medium</text>
          <text x={padL + 190} y={padT + 28} fill="#718096" fontSize="8" fontFamily="monospace" textAnchor="end">Extreme</text>
          {crosshairJSX}
        </svg>
      </div>
    </div>
  );
}

function CascadeSimulation({ asset, assetColor, currentPrice, API }) {
  const [direction, setDirection] = useState("down");
  const [triggerPct, setTriggerPct] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSimulation = useCallback(() => {
    setLoading(true);
    authFetch(`${API}/cascade/${asset}?direction=${direction}&initial_pct=${triggerPct}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [API, asset, direction, triggerPct]);

  useEffect(() => { runSimulation(); }, [asset, direction, triggerPct, runSimulation]);

  const sevColors = { EXTREME: "#ff0000", HIGH: "#ff4444", MODERATE: "#ff9900", LOW: "#4dff91" };
  const triggers = [0.5, 1, 2, 3, 5, 10];

  // Chart dimensions
  const W = 1200, H = 400;
  const padL = 80, padR = 40, padT = 30, padB = 40;
  const chartW = W - padL - padR, chartH = H - padT - padB;

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 16 }}>CASCADE SIMULATION — {asset}</div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => setDirection("down")} style={{
            background: direction === "down" ? "#ff6b6b30" : "transparent",
            border: `1px solid ${direction === "down" ? "#ff6b6b" : "#1a2030"}`,
            color: direction === "down" ? "#ff6b6b" : "#718096",
            padding: "6px 14px", fontSize: 11, borderRadius: 4, cursor: "pointer", fontFamily: "monospace"
          }}>▼ CRASH</button>
          <button onClick={() => setDirection("up")} style={{
            background: direction === "up" ? "#4dff9130" : "transparent",
            border: `1px solid ${direction === "up" ? "#4dff91" : "#1a2030"}`,
            color: direction === "up" ? "#4dff91" : "#718096",
            padding: "6px 14px", fontSize: 11, borderRadius: 4, cursor: "pointer", fontFamily: "monospace"
          }}>▲ SQUEEZE</button>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span style={{ color: "#718096", fontSize: 10 }}>TRIGGER:</span>
          {triggers.map(t => (
            <button key={t} onClick={() => setTriggerPct(t)} style={{
              background: triggerPct === t ? assetColor + "30" : "transparent",
              border: `1px solid ${triggerPct === t ? assetColor : "#1a2030"}`,
              color: triggerPct === t ? assetColor : "#718096",
              padding: "4px 10px", fontSize: 10, borderRadius: 4, cursor: "pointer", fontFamily: "monospace"
            }}>{t}%</button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>CASCADE MULTIPLIER</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: sevColors[data.severity] || "#4dff91", fontFamily: "monospace" }}>
              {data.cascade_multiplier}x
            </div>
            <div style={{ fontSize: 10, marginTop: 4 }}>
              <span style={{ padding: "2px 8px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 1, background: (sevColors[data.severity] || "#4dff91") + "20", color: sevColors[data.severity] || "#4dff91" }}>
                {data.severity}
              </span>
            </div>
          </div>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>INITIAL â†’ FINAL</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", fontFamily: "monospace" }}>{data.initial_trigger_pct}% â†’ {data.final_move_pct}%</div>
            <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>${data.initial_trigger_price?.toLocaleString()} â†’ ${data.final_price?.toLocaleString()}</div>
          </div>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>LIQUIDATED</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#ff9900", fontFamily: "monospace" }}>{fmt(data.total_liq_notional)}</div>
            <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>{data.total_liq_count} positions · {data.total_steps} steps</div>
          </div>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>BOOK DEPTH (±5%)</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#4da2ff", fontFamily: "monospace" }}>{fmt(data.book_depth_usd)}</div>
            <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>{data.depth_source}</div>
          </div>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>LIQ CLUSTERS</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: data.liq_clusters > 5 ? "#ff6b6b" : "#e2e8f0", fontFamily: "monospace" }}>{data.liq_clusters}</div>
            <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>dense zones (&gt;$1M)</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: "#4a5568", marginBottom: 16, fontFamily: "monospace" }}>
          OI: {fmt(data.total_oi)} · Effective Depth: {fmt(data.effective_depth)} · Margin Lost: {fmt(data.total_liq_margin)}
        </div>
        </>
      )}

      {/* Cascade Waterfall Chart */}
      {data?.steps?.length > 0 && (() => {
        const steps = data.steps;
        const prices = [data.current_price, ...steps.map(s => s.result_price)];
        const maxP = Math.max(...prices) * 1.01;
        const minP = Math.min(...prices) * 0.99;
        const maxStepNtl = Math.max(...steps.map(s => s.liq_notional), 1);

        const priceToY = (p) => padT + chartH - ((p - minP) / (maxP - minP)) * chartH;
        const stepW = chartW / (steps.length + 1);

        return (
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", marginBottom: 20 }}>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
              <rect x={padL} y={padT} width={chartW} height={chartH} fill="#060a0f" style={{ cursor: "grab" }} />
          {/* Right price axis — scroll to zoom, drag to pan */}
          <rect x={W - padR} y={padT} width={padR} height={chartH} fill="#0d1520" style={{ cursor: "ns-resize" }} />
          <line x1={W - padR} y1={padT} x2={W - padR} y2={padT + chartH} stroke="#1e3050" strokeWidth="2" />

              {/* Grid */}
              {Array.from({ length: 6 }, (_, i) => {
                const p = minP + (i / 5) * (maxP - minP);
                return (
                  <g key={"g" + i}>
                    <line x1={padL} y1={priceToY(p)} x2={padL + chartW} y2={priceToY(p)} stroke="#0d1117" strokeWidth="0.5" />
                    <text x={padL - 8} y={priceToY(p) + 4} textAnchor="end" fill="#718096" fontSize="9" fontFamily="monospace">${Math.round(p).toLocaleString()}</text>
                  </g>
                );
              })}

              {/* Starting price line */}
              <line x1={padL} y1={priceToY(data.current_price)} x2={padL + chartW} y2={priceToY(data.current_price)} stroke="#f7931a" strokeWidth="1" strokeDasharray="4,3" />

              {/* Price path */}
              {steps.map((s, i) => {
                const x1 = padL + i * stepW + stepW / 2;
                const x2 = padL + (i + 1) * stepW + stepW / 2;
                const y1 = priceToY(s.trigger_price);
                const y2 = priceToY(s.result_price);
                return <line key={"path" + i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={direction === "down" ? "#ff6b6b" : "#4dff91"} strokeWidth="2" />;
              })}

              {/* Liquidation bars at each step */}
              {steps.map((s, i) => {
                const x = padL + (i + 1) * stepW;
                const barH = (s.liq_notional / maxStepNtl) * (chartH * 0.3);
                const y = priceToY(s.result_price);
                const intensity = s.liq_notional / maxStepNtl;
                let r, g, b;
                if (intensity < 0.25) { r = 0; g = 0; b = Math.round(80 + intensity * 4 * 175); }
                else if (intensity < 0.5) { const t = (intensity - 0.25) * 4; r = 0; g = Math.round(t * 255); b = Math.round(255 - t * 100); }
                else if (intensity < 0.75) { const t = (intensity - 0.5) * 4; r = 0; g = 255; b = Math.round(155 - t * 155); }
                else { const t = (intensity - 0.75) * 4; r = Math.round(t * 255); g = 255; b = 0; }
                return (
                  <g key={"bar" + i}>
                    <rect x={x - stepW * 0.3} y={y - barH} width={stepW * 0.6} height={barH} fill={`rgba(${r},${g},${b},0.7)`} rx={2} />
                    <circle cx={x + stepW / 2 - stepW * 0.3} cy={y} r={3} fill={direction === "down" ? "#ff6b6b" : "#4dff91"} />
                  </g>
                );
              })}

              {/* Labels */}
              <text x={padL + 10} y={padT + 16} fill="#718096" fontSize="9" fontFamily="monospace">
                Price cascade: {data.initial_trigger_pct}% trigger â†’ {data.final_move_pct}% final ({data.cascade_multiplier}x amplification)
              </text>
            </svg>
          </div>
        );
      })()}

      {/* Step-by-step table */}
      {data?.steps?.length > 0 && (
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 70px 1fr 1fr 1fr 80px", minWidth: 640, padding: "10px 20px", borderBottom: "1px solid #1a2030", fontSize: 10, color: "#718096", letterSpacing: 1 }}>
            <span>STEP</span><span>TRIGGER $</span><span>RESULT $</span><span>LIQS</span><span>NOTIONAL</span><span>LARGEST</span><span>CUM. NTL</span><span>IMPACT</span>
          </div>
          {data.steps.map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 70px 1fr 1fr 1fr 80px", minWidth: 640, padding: "8px 20px", borderBottom: "1px solid #0d1117", fontSize: 12 }}>
              <span style={{ color: "#718096" }}>#{s.step}</span>
              <span style={{ color: "#e2e8f0", fontFamily: "monospace" }}>${s.trigger_price?.toLocaleString()}</span>
              <span style={{ color: direction === "down" ? "#ff6b6b" : "#4dff91", fontFamily: "monospace" }}>${s.result_price?.toLocaleString()}</span>
              <span style={{ color: "#ff9900" }}>{s.liq_count}</span>
              <span style={{ color: "#e2e8f0", fontFamily: "monospace" }}>{fmt(s.liq_notional)}</span>
              <span style={{ color: "#ff9900", fontFamily: "monospace" }}>{fmt(s.largest_position)}</span>
              <span style={{ color: "#e2e8f0", fontFamily: "monospace" }}>{fmt(s.cumulative_liq_ntl)}</span>
              <span style={{ color: s.impact_pct > 1 ? "#ff6b6b" : "#718096", fontFamily: "monospace" }}>{s.impact_pct}%</span>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ color: "#718096", textAlign: "center", padding: 40 }}>Running simulation...</div>}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(API + "/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (r.ok) {
        const data = await r.json();
        setToken(data.token);
        onLogin();
      } else {
        const data = await r.json().catch(() => ({}));
        setError(data.detail || "Authentication failed");
      }
    } catch {
      setError("Network error — is the backend running?");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#060a0f", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', 'Space Grotesk', system-ui, sans-serif" }}>
      <form onSubmit={handleSubmit} style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 8, padding: "40px 36px", width: 360, textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "#718096", letterSpacing: 3, marginBottom: 8 }}>HLRISK</div>
        <div style={{ fontSize: 18, color: "#e2e8f0", fontWeight: 700, marginBottom: 28 }}>Dashboard Login</div>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          autoFocus
          style={{
            width: "100%", padding: "12px 16px", fontSize: 14, borderRadius: 6,
            border: "1px solid #1a2030", background: "#060a0f", color: "#e2e8f0",
            outline: "none", boxSizing: "border-box", fontFamily: "monospace",
          }}
        />
        <button
          type="submit"
          disabled={loading || !password}
          style={{
            width: "100%", padding: "12px 0", marginTop: 16, fontSize: 12, fontWeight: 700,
            letterSpacing: 2, borderRadius: 6, border: "none", cursor: loading ? "wait" : "pointer",
            background: loading ? "#1a2030" : "#4da2ff", color: "#fff",
            transition: "background 0.2s",
          }}
        >
          {loading ? "AUTHENTICATING..." : "ENTER"}
        </button>
        {error && <div style={{ color: "#ff6b6b", fontSize: 12, marginTop: 14 }}>{error}</div>}
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EXECUTION ENGINE TAB — State machine, structural edge, trigger, exit
// ---------------------------------------------------------------------------

function ExecutionEngineTab({ API, asset }) {
  const [engineData, setEngineData] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const poll = async () => {
      try {
        const [stateR, logsR] = await Promise.all([
          authFetch(`${API}/execution_state/${asset}`, { signal: controller.signal }),
          authFetch(`${API}/execution_logs/${asset}?limit=20`, { signal: controller.signal }),
        ]);
        if (stateR.status === 401) { handle401(); return; }
        if (stateR.ok) setEngineData(await stateR.json());
        if (logsR.ok) { const d = await logsR.json(); setLogs(d.logs || []); }
      } catch (e) { if (e.name !== "AbortError") console.error("[ExecutionEngineTab]", e); }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, asset]);

  const sectionStyle = { background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: 16, marginBottom: 16 };

  const state = engineData?.state || "OFFLINE";
  const stateColors = { IDLE: "#718096", OBSERVE: "#f6ad55", PROBE: "#f6ad55", BUILD: "#4dff91", ACCELERATE: "#4dff91", HARVEST: "#4da2ff", ABORT: "#ff6b6b", OFFLINE: "#4a5568", STALE: "#f6ad55" };
  const stateColor = stateColors[state] || "#718096";

  const se = engineData?.structural_edge || {};
  const tq = engineData?.trigger_quality || {};
  const ep = engineData?.exit_pressure || {};
  const pos = engineData?.position;

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      {/* State Machine */}
      <div style={{ ...sectionStyle, padding: 20, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>ENGINE STATE</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: stateColor, fontFamily: "'Space Mono', monospace" }}>{state}</div>
        </div>
        <div style={{ flex: 1, display: "flex", gap: 4, alignItems: "center" }}>
          {["IDLE", "OBSERVE", "PROBE", "BUILD", "ACCELERATE", "HARVEST"].map(s => (
            <div key={s} style={{ flex: 1, height: 6, borderRadius: 3, background: s === state ? stateColor : "#1a2030", transition: "background 0.3s" }} />
          ))}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>ASSET</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>{asset}</div>
        </div>
      </div>

      {/* Layer panels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
        {/* Structural Edge */}
        <div style={sectionStyle}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>STRUCTURAL EDGE</div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 11 }}>
            <span style={{ color: "#718096" }}>Direction</span>
            <span style={{ color: se.direction === "LONG" ? "#4dff91" : se.direction === "SHORT" ? "#ff6b6b" : "#718096", fontWeight: 700 }}>{se.direction || "NEUTRAL"}</span>
            <span style={{ color: "#718096" }}>Confidence</span>
            <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>{se.edge_confidence?.toFixed(1) || "0"}%</span>
            <span style={{ color: "#718096" }}>Target inventory</span>
            <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{se.target_inventory?.toFixed(2) || "0"}</span>
            <span style={{ color: "#718096" }}>Thesis half-life</span>
            <span style={{ color: "#a0aec0" }}>{se.thesis_half_life_minutes?.toFixed(0) || "60"}m</span>
            <span style={{ color: "#718096" }}>Imbalance score</span>
            <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{se.imbalance_score?.toFixed(1) || "0"}</span>
            <span style={{ color: "#718096" }}>MS bias</span>
            <span style={{ color: "#a0aec0" }}>{se.market_structure_direction || se.market_structure_bias || "NO_BOS"}</span>
          </div>
        </div>

        {/* Trigger Quality */}
        <div style={sectionStyle}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>TRIGGER QUALITY</div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 11 }}>
            <span style={{ color: "#718096" }}>Score</span>
            <span style={{ color: tq.trigger_score >= 75 ? "#4dff91" : tq.trigger_score >= 55 ? "#f6ad55" : "#ff6b6b", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{tq.trigger_score?.toFixed(1) || "0"}</span>
            <span style={{ color: "#718096" }}>Action</span>
            <span style={{ color: tq.action === "GO_AGGRESSIVE" ? "#4dff91" : tq.action === "STAY_PASSIVE" ? "#f6ad55" : tq.action === "PAUSE" ? "#718096" : "#ff6b6b", fontWeight: 700 }}>{tq.action || "PAUSE"}</span>
            <span style={{ color: "#718096" }}>Toxicity</span>
            <span style={{ color: tq.book_toxicity >= 0.6 ? "#ff6b6b" : "#a0aec0", fontFamily: "'Space Mono', monospace" }}>{(tq.book_toxicity * 100)?.toFixed(0) || "0"}%</span>
            <span style={{ color: "#718096" }}>CVD align</span>
            <span style={{ color: "#a0aec0" }}>{tq.cvd_alignment?.toFixed(2) || "0"}</span>
            <span style={{ color: "#718096" }}>Spread</span>
            <span style={{ color: "#a0aec0" }}>{tq.spread_bps?.toFixed(1) || "0"} bps</span>
            <span style={{ color: "#718096" }}>Bookmap</span>
            <span style={{ color: tq.bookmap_confirms ? "#4dff91" : "#ff6b6b" }}>{tq.bookmap_confirms ? "Confirms" : "No"}</span>
          </div>
        </div>

        {/* Exit Pressure (only when position exists) */}
        <div style={sectionStyle}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>EXIT PRESSURE</div>
          {pos ? (
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 11 }}>
              <span style={{ color: "#718096" }}>Pressure</span>
              <span style={{ color: ep.exit_pressure >= 80 ? "#ff6b6b" : ep.exit_pressure >= 50 ? "#f6ad55" : "#4dff91", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{ep.exit_pressure?.toFixed(0) || "0"}%</span>
              <span style={{ color: "#718096" }}>Action</span>
              <span style={{ color: ep.action === "FLATTEN" ? "#ff6b6b" : ep.action === "HOLD" ? "#4dff91" : "#f6ad55", fontWeight: 700 }}>{ep.action || "HOLD"}</span>
              <span style={{ color: "#718096" }}>Thesis health</span>
              <span style={{ color: "#a0aec0" }}>{ep.thesis_health?.toFixed(0) || "100"}%</span>
              <span style={{ color: "#718096" }}>Cascade</span>
              <span style={{ color: "#a0aec0" }}>{(ep.cascade_progress * 100)?.toFixed(0) || "0"}%</span>
              <span style={{ color: "#718096" }}>Stop</span>
              <span style={{ color: "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>${ep.dynamic_stop?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || "—"}</span>
              <span style={{ color: "#718096" }}>TP1 / TP2</span>
              <span style={{ color: "#4dff91", fontFamily: "'Space Mono', monospace" }}>${ep.tp1?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || "—"} / ${ep.tp2?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || "—"}</span>
            </div>
          ) : (
            <div style={{ color: "#4a5568", fontSize: 12 }}>No position open</div>
          )}
        </div>
      </div>

      {/* Position */}
      {pos && (
        <div style={{ ...sectionStyle, borderLeft: `3px solid ${pos.side === "LONG" ? "#4dff91" : "#ff6b6b"}` }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 8 }}>POSITION</div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
            <span><span style={{ color: "#718096" }}>Side: </span><span style={{ color: pos.side === "LONG" ? "#4dff91" : "#ff6b6b", fontWeight: 700 }}>{pos.side}</span></span>
            <span><span style={{ color: "#718096" }}>Entry: </span><span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>${pos.entry_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></span>
            <span><span style={{ color: "#718096" }}>Notional: </span><span style={{ color: "#e2e8f0" }}>{fmt(pos.notional)}</span></span>
            <span><span style={{ color: "#718096" }}>Fills: </span><span style={{ color: "#a0aec0" }}>{pos.fills?.length || 0}</span></span>
          </div>
        </div>
      )}

      {/* Recent Logs */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 10 }}>RECENT ACTIVITY</div>
        {logs.length === 0 ? (
          <div style={{ color: "#4a5568", fontSize: 12 }}>No engine activity logged yet.</div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {logs.slice(-15).reverse().map((l, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "4px 0", borderBottom: "1px solid #060a0f", fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                <span style={{ color: "#4a5568", minWidth: 55 }}>{new Date(l.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                <span style={{ color: l.log_type === "structural_update" ? "#4da2ff" : l.log_type === "trigger_update" ? "#f6ad55" : l.log_type === "exit_update" ? "#ff6b6b" : "#a0aec0", minWidth: 90 }}>{l.log_type?.replace("_update", "").replace("_", " ")}</span>
                <span style={{ color: "#718096", flex: 1 }}>
                  {l.log_type === "structural_update" && `dir=${l.direction} conf=${l.edge_confidence} inv=${l.target_inventory}`}
                  {l.log_type === "trigger_update" && `score=${l.trigger_score} action=${l.action} spread=${l.spread_bps}bps`}
                  {l.log_type === "exit_update" && `pressure=${l.exit_pressure} action=${l.action} health=${l.thesis_health}`}
                  {l.log_type === "child_order" && `${l.action} ${l.side} ${l.order_type} @${l.price}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PAPER TRADER TAB — Performance stats, Sharpe tracking, returns chart
// ---------------------------------------------------------------------------

function PaperTraderTab({ API }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/paper-trader/stats", { signal: controller.signal });
        if (r.status === 401) { handle401(); return; }
        if (r.ok) setStats(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[PaperTraderTab] poll:", e); }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API]);

  if (!stats || stats.status === "no_data") {
    return (
      <div style={{ animation: "fadeIn 0.3s ease", color: "#4a5568", fontSize: 12, textAlign: "center", padding: 60 }}>
        Loading paper trader stats...
      </div>
    );
  }

  const sectionStyle = { background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: 16, marginBottom: 16 };
  const headerStyle = { fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 };

  const sharpeColor = (v) => v == null ? "#4a5568" : v >= 2 ? "#4dff91" : v >= 1 ? "#f6ad55" : "#ff6b6b";
  const ddColor = (v) => v < 5 ? "#4dff91" : v < 10 ? "#f6ad55" : "#ff6b6b";

  const winRate = stats.win_rate || 0;
  const profitFactor = stats.avg_loss ? Math.abs(stats.avg_win / stats.avg_loss) : stats.avg_win > 0 ? Infinity : 0;
  const pfStr = profitFactor === Infinity ? "∞" : profitFactor.toFixed(2);
  const pfColor = profitFactor >= 2 ? "#4dff91" : profitFactor >= 1.5 ? "#f6ad55" : "#ff6b6b";

  // Build returns chart data with cumulative equity
  const returns = stats.returns || [];
  let cum = 1;
  const chartData = returns.map(r => {
    cum *= (1 + (r.return_pct || 0));
    return {
      date: r.date,
      ret: Math.round(r.return_pct * 10000) / 100,
      cumulative: Math.round((cum - 1) * 10000) / 100,
      balance: r.balance,
    };
  });

  const sharpe30 = stats.sharpe_30d;
  const sharpe30Val = sharpe30 != null ? sharpe30 : 0;
  const sharpeTarget = 2.0;
  const sharpeRatio = Math.max(0, Math.min(sharpe30Val / sharpeTarget, 1));
  const sharpeFilled = Math.round(sharpeRatio * 20);

  const tooltipStyle = { contentStyle: { background: "#0d1117", border: "1px solid #1a2030", borderRadius: 4, fontSize: 11, fontFamily: "'Space Mono', monospace" }, labelStyle: { color: "#718096", fontSize: 10 } };

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 1 — Performance Summary Cards
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
        {/* PnL Card */}
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px" }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>TOTAL PnL</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: stats.total_pnl >= 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>
            ${stats.total_pnl >= 0 ? "+" : ""}{stats.total_pnl?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
          </div>
          <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>Win Rate: {winRate.toFixed(1)}%</div>
        </div>

        {/* Sharpe Card */}
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px" }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>SHARPE RATIO</div>
          <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
            <div>
              <div style={{ fontSize: 10, color: "#4a5568" }}>7d</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: sharpeColor(stats.sharpe_7d), fontFamily: "'Space Mono', monospace" }}>
                {stats.sharpe_7d != null ? stats.sharpe_7d.toFixed(2) : "—"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#4a5568" }}>30d</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: sharpeColor(stats.sharpe_30d), fontFamily: "'Space Mono', monospace" }}>
                {stats.sharpe_30d != null ? stats.sharpe_30d.toFixed(2) : "—"}
              </div>
            </div>
          </div>
          {stats.sharpe_7d == null && <div style={{ fontSize: 10, color: "#4a5568", marginTop: 4 }}>Accumulating data...</div>}
        </div>

        {/* Drawdown Card */}
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px" }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>MAX DRAWDOWN</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: ddColor(stats.max_drawdown_pct || 0), fontFamily: "'Space Mono', monospace" }}>
            {(stats.max_drawdown_pct || 0).toFixed(2)}%
          </div>
          <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>{stats.max_drawdown_pct < 5 ? "Healthy" : stats.max_drawdown_pct < 10 ? "Elevated" : "High risk"}</div>
        </div>

        {/* Trade Count Card */}
        <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "14px 20px" }}>
          <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>TRADES</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
            {stats.total_trades || 0}
          </div>
          <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>
            <span style={{ color: "#4dff91" }}>{stats.wins || 0}W</span>
            {" / "}
            <span style={{ color: "#ff6b6b" }}>{stats.losses || 0}L</span>
          </div>
        </div>
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 2 — Win/Loss Metrics
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={sectionStyle}>
        <div style={headerStyle}>WIN/LOSS METRICS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          {/* Win Rate Bar */}
          <div>
            <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 6 }}>WIN RATE</div>
            <div style={{ display: "flex", height: 20, borderRadius: 4, overflow: "hidden", border: "1px solid #1a2030" }}>
              <div style={{ width: `${winRate}%`, background: "#4dff91", minWidth: winRate > 0 ? 2 : 0, transition: "width 0.5s" }} />
              <div style={{ flex: 1, background: "#ff6b6b20" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
              <span style={{ color: "#4dff91" }}>{winRate.toFixed(1)}%</span>
              <span style={{ color: "#ff6b6b" }}>{(100 - winRate).toFixed(1)}%</span>
            </div>
          </div>

          {/* Avg Win/Loss Bars */}
          <div>
            <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 6 }}>AVG WIN / AVG LOSS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ height: 14, borderRadius: 2, background: "#4dff9140", border: "1px solid #4dff9160", width: `${Math.min(100, Math.abs(stats.avg_win) / Math.max(Math.abs(stats.avg_win), Math.abs(stats.avg_loss), 1) * 100)}%`, minWidth: 4 }} />
                <span style={{ fontSize: 11, color: "#4dff91", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>${(stats.avg_win || 0).toFixed(0)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ height: 14, borderRadius: 2, background: "#ff6b6b40", border: "1px solid #ff6b6b60", width: `${Math.min(100, Math.abs(stats.avg_loss) / Math.max(Math.abs(stats.avg_win), Math.abs(stats.avg_loss), 1) * 100)}%`, minWidth: 4 }} />
                <span style={{ fontSize: 11, color: "#ff6b6b", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>${(stats.avg_loss || 0).toFixed(0)}</span>
              </div>
            </div>
          </div>

          {/* Profit Factor */}
          <div>
            <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 6 }}>PROFIT FACTOR</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: pfColor, fontFamily: "'Space Mono', monospace" }}>{pfStr}</div>
            <div style={{ fontSize: 10, color: "#4a5568", marginTop: 4 }}>{profitFactor >= 2 ? "Strong" : profitFactor >= 1.5 ? "Acceptable" : profitFactor >= 1 ? "Marginal" : "Unprofitable"}</div>
          </div>
        </div>
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 3 — Returns Chart
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={{ ...sectionStyle, padding: 20 }}>
        <div style={headerStyle}>
          DAILY RETURNS
          <span style={{ fontSize: 9, color: "#4a5568", fontWeight: 400, letterSpacing: 0 }}>
            {chartData.length > 0 ? `${chartData.length} days` : "No data yet"}
          </span>
        </div>
        {chartData.length < 2 ? (
          <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 60 }}>
            Daily returns will appear here once data accumulates (need 2+ days).
            {chartData.length === 0 && " The paper trader records returns at midnight UTC."}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
              <XAxis dataKey="date" stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={d => d.slice(5)} />
              <YAxis yAxisId="ret" stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={v => v.toFixed(2) + "%"} />
              <YAxis yAxisId="cum" orientation="right" stroke="#4a556840" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={v => v.toFixed(1) + "%"} />
              <Tooltip {...tooltipStyle} formatter={(v, name) => [name === "ret" ? v.toFixed(3) + "%" : v.toFixed(2) + "%", name === "ret" ? "Daily Return" : "Cumulative"]} />
              <ReferenceLine yAxisId="ret" y={0} stroke="#4a556880" strokeDasharray="3 3" />
              <Bar yAxisId="ret" dataKey="ret" isAnimationActive={false}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.ret >= 0 ? "#4dff9160" : "#ff6b6b60"} stroke={entry.ret >= 0 ? "#4dff91" : "#ff6b6b"} />
                ))}
              </Bar>
              <Line yAxisId="cum" type="monotone" dataKey="cumulative" stroke="#4da2ff" strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 4 — Sharpe Target Tracker
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={sectionStyle}>
        <div style={headerStyle}>SHARPE TARGET TRACKER</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Progress bar */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 10 }}>
              <span style={{ color: "#718096" }}>Target: {sharpeTarget.toFixed(1)}</span>
              <span style={{ color: sharpeColor(sharpe30), fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>
                {sharpe30 != null ? sharpe30.toFixed(3) : "N/A"}
              </span>
            </div>
            <div style={{ height: 24, borderRadius: 4, background: "#080c12", border: "1px solid #1a2030", overflow: "hidden", position: "relative" }}>
              <div style={{
                height: "100%", borderRadius: 4, transition: "width 0.5s",
                width: `${sharpeRatio * 100}%`,
                background: sharpe30 != null
                  ? sharpe30 >= 2 ? "linear-gradient(90deg, #4dff91, #00cc66)"
                  : sharpe30 >= 1 ? "linear-gradient(90deg, #f6ad55, #dd8800)"
                  : "linear-gradient(90deg, #ff6b6b, #cc0000)"
                  : "#2d3f52",
              }} />
              {/* Target marker at 100% */}
              <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 2, background: "#e2e8f040" }} />
            </div>
            <div style={{ fontSize: 10, color: "#4a5568", marginTop: 6, fontFamily: "'Space Mono', monospace" }}>
              {"â–ˆ".repeat(sharpeFilled)}{"â–‘".repeat(20 - sharpeFilled)} {sharpe30 != null ? (sharpe30 / sharpeTarget * 100).toFixed(0) : "0"}%
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 20 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 4 }}>DAYS</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>{returns.length}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 4 }}>NEED</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#718096", fontFamily: "'Space Mono', monospace" }}>{Math.max(0, 7 - returns.length)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 4 }}>STATUS</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: sharpe30 != null ? (sharpe30 >= 2 ? "#4dff91" : sharpe30 >= 1 ? "#f6ad55" : "#ff6b6b") : "#4a5568" }}>
                {sharpe30 != null ? (sharpe30 >= 2 ? "ON TARGET" : sharpe30 >= 1 ? "IMPROVING" : "BELOW TARGET") : "WARMING UP"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ SECTION 4 — Trade Blotter ═══ */}
      <BlotterTable API={API} />
    </div>
  );
}

function BlotterTable({ API }) {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/paper-trader/trades?limit=20", { signal: controller.signal });
        if (r.ok) {
          const d = await r.json();
          setTrades(d.items || []);
          setTotal(d.total || 0);
        }
      } catch (e) { if (e.name !== "AbortError") console.error("[Blotter]", e); }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { controller.abort(); clearInterval(iv); };
  }, [API]);

  if (total === 0) return null;

  const fu = (v) => { if (!v && v !== 0) return "\u2014"; const a = Math.abs(v); if (a >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (a >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${v.toFixed(2)}`; };
  const pnlColor = (v) => v > 0 ? "#4dff91" : v < 0 ? "#ff6b6b" : "#718096";
  const statusColor = (s) => s === "OPEN" ? "#58a6ff" : s === "CLOSED" ? "#4dff91" : s === "FORCE_CLOSED" ? "#d29922" : "#484f58";

  return (
    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #1a2030", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#718096", letterSpacing: 2 }}>TRADE BLOTTER</span>
        <span style={{ fontSize: 10, color: "#4a5568" }}>{total} trades</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "60px 50px 50px 80px 80px 70px 60px 60px 60px 80px", minWidth: 700, padding: "6px 16px", borderBottom: "1px solid #1a2030", fontSize: 9, color: "#718096", letterSpacing: 1 }}>
        <span>STATUS</span><span>ASSET</span><span>SIDE</span><span>ENTRY</span><span>EXIT</span><span>NET PnL</span><span>NET %</span><span>MFE</span><span>MAE</span><span>REASON</span>
      </div>
      {trades.map((t, i) => (
        <div key={t.trade_id} style={{ display: "grid", gridTemplateColumns: "60px 50px 50px 80px 80px 70px 60px 60px 60px 80px", minWidth: 700, padding: "6px 16px", borderBottom: "1px solid #060a0f", fontSize: 11, alignItems: "center", background: i % 2 === 0 ? "#080c12" : "transparent" }}>
          <span style={{ color: statusColor(t.status), fontSize: 9, fontWeight: 600 }}>{t.status}</span>
          <span style={{ color: "#c9d1d9", fontWeight: 600 }}>{t.asset}</span>
          <span style={{ color: t.side === "LONG" ? "#4dff91" : "#ff6b6b", fontWeight: 700 }}>{t.side}</span>
          <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace", fontSize: 10 }}>{t.entry_fill_price ? "$" + Number(t.entry_fill_price).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "\u2014"}</span>
          <span style={{ color: "#a0aec0", fontFamily: "'Space Mono', monospace", fontSize: 10 }}>{t.exit_fill_price ? "$" + Number(t.exit_fill_price).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "\u2014"}</span>
          <span style={{ color: pnlColor(t.net_pnl), fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 10 }}>{fu(t.net_pnl)}</span>
          <span style={{ color: pnlColor(t.net_return_pct), fontFamily: "'Space Mono', monospace", fontSize: 9 }}>{t.net_return_pct != null ? (t.net_return_pct >= 0 ? "+" : "") + t.net_return_pct.toFixed(2) + "%" : "\u2014"}</span>
          <span style={{ color: "#3fb950", fontSize: 9, fontFamily: "'Space Mono', monospace" }}>{t.mfe_pct != null ? "+" + t.mfe_pct.toFixed(2) + "%" : "\u2014"}</span>
          <span style={{ color: "#ff6b6b", fontSize: 9, fontFamily: "'Space Mono', monospace" }}>{t.mae_pct != null ? t.mae_pct.toFixed(2) + "%" : "\u2014"}</span>
          <span style={{ color: "#484f58", fontSize: 9 }}>{t.exit_reason_code || (t.status === "OPEN" ? "OPEN" : "\u2014")}</span>
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Pace of Tape — narrative layer
// ---------------------------------------------------------------------------

function buildTapeNarrative({
  tapeImpulse = 0,
  signedIntensityZ = 0,
  aggressionRatio = 0.5,
  volAccel = 0,
  tradeRate3s = 0,
  tapeState = "QUIET",
  bidAbsorptionScore = 0,
  askAbsorptionScore = 0,
  netAbsorptionBias = "neutral",
  structuralDirection = "NEUTRAL",
}) {
  const intensityX = Math.abs(signedIntensityZ).toFixed(1);
  const aggrPct = Math.round(aggressionRatio * 100);
  const tradesPs = tradeRate3s.toFixed(1);

  const sameSideAbsorption =
    tapeImpulse > 0 ? bidAbsorptionScore > askAbsorptionScore + 10 :
    tapeImpulse < 0 ? askAbsorptionScore > bidAbsorptionScore + 10 :
    false;

  const oppositeAbsorption =
    tapeImpulse > 0 ? askAbsorptionScore > bidAbsorptionScore + 10 :
    tapeImpulse < 0 ? bidAbsorptionScore > askAbsorptionScore + 10 :
    false;

  const strongAbsorption = Math.max(bidAbsorptionScore, askAbsorptionScore) >= 60;
  const noMeaningfulAbsorption = bidAbsorptionScore < 25 && askAbsorptionScore < 25;

  const dirLabel =
    structuralDirection === "LONG" ? "long thesis" :
    structuralDirection === "SHORT" ? "short thesis" :
    "broader thesis";

  switch (tapeState) {
    case "QUIET":
      if (strongAbsorption) {
        return {
          headline: "Quiet tape, defended level",
          detail: `Tape is inactive, but ${netAbsorptionBias} absorption remains visible; watch for the next burst to confirm whether that defense attracts follow-through or gets retested.`,
        };
      }
      return {
        headline: "No tape signal",
        detail: "Trade flow is near baseline with no meaningful directional aggression; use structure, walls, and absorption for context.",
      };

    case "BALANCED_ACTIVE":
      if (strongAbsorption) {
        return {
          headline: "Balanced tape into defense",
          detail: `Trade flow is active but not one-sided, while ${netAbsorptionBias} absorption is stronger; watch whether the next directional burst resolves toward that defended side.`,
        };
      }
      return {
        headline: "Active but balanced",
        detail: `Tape is running above quiet conditions at ${tradesPs} trades/s, but aggression is mixed at ${aggrPct}% buy; this is participation without clear control.`,
      };

    case "BUY_BUILDING":
      if (oppositeAbsorption) {
        return {
          headline: "Buying into resistance",
          detail: `Aggressive buying is building at ${intensityX}z, but ask absorption is stronger overhead; watch whether this burst sweeps the wall or stalls into exhaustion.`,
        };
      }
      if (sameSideAbsorption) {
        return {
          headline: "Buying with support",
          detail: `Buy tape is building at ${intensityX}z and bid absorption remains stronger below; this supports continuation if overhead liquidity starts to lift.`,
        };
      }
      return {
        headline: structuralDirection === "LONG" ? "Buy tape supports long thesis" : "Buy tape building",
        detail: structuralDirection === "LONG"
          ? `Aggressive buying is building at ${intensityX}z and aligns with the ${dirLabel}; watch for offer sweeps or overhead walls pulling.`
          : `Aggressive buying is accelerating at ${intensityX}z with ${aggrPct}% buy aggression; watch for follow-through through nearby offers and confirmation from CVD.`,
      };

    case "SELL_BUILDING":
      if (oppositeAbsorption) {
        return {
          headline: "Selling into support",
          detail: `Aggressive selling is building at ${intensityX}z, but bid absorption is active below; watch whether sellers break the defense or fade into a reversal setup.`,
        };
      }
      if (sameSideAbsorption) {
        return {
          headline: "Selling with pressure",
          detail: `Sell tape is building at ${intensityX}z and ask absorption remains stronger overhead; this supports continuation lower if nearby bid support does not hold.`,
        };
      }
      return {
        headline: structuralDirection === "SHORT" ? "Sell tape supports short thesis" : "Sell tape building",
        detail: structuralDirection === "SHORT"
          ? `Aggressive selling is building at ${intensityX}z and aligns with the ${dirLabel}; watch for bid support to thin or fail.`
          : `Aggressive selling is accelerating at ${intensityX}z with ${100 - aggrPct}% sell aggression; watch for continuation through nearby bids and confirmation from CVD.`,
      };

    case "BUY_EXHAUSTING":
      if (oppositeAbsorption) {
        return {
          headline: "Buying fading into ask absorption",
          detail: "Buy pressure is losing speed while ask absorption remains stronger overhead; this weakens continuation and raises the chance of rejection.",
        };
      }
      return {
        headline: "Buying is fading",
        detail: "Buy-side tape remains positive, but acceleration is fading; watch whether price still advances or stalls into overhead absorption.",
      };

    case "SELL_EXHAUSTING":
      if (oppositeAbsorption) {
        return {
          headline: "Selling fading into bid absorption",
          detail: "Sell pressure is losing speed while bid absorption remains stronger below; this weakens downside continuation and improves reversal quality.",
        };
      }
      return {
        headline: "Selling is fading",
        detail: "Sell-side tape remains active, but acceleration is slowing; watch whether price still pushes lower or stalls into support.",
      };

    case "CASCADE_BUY":
      if (oppositeAbsorption) {
        return {
          headline: "Cascade into ask wall",
          detail: "Extreme buy tape is colliding with strong ask absorption overhead; this is the highest-risk zone for either a sweep-through or a sharp exhaustion turn.",
        };
      }
      if (noMeaningfulAbsorption) {
        return {
          headline: "Pure buy momentum",
          detail: `Tape is in an extreme buy burst at ${intensityX}z with no meaningful ask absorption overhead; treat this as momentum until the burst slows or new resistance appears.`,
        };
      }
      return {
        headline: structuralDirection === "LONG" ? "Buy cascade confirms long thesis" : "Buy cascade conditions",
        detail: `Tape is in an extreme buy burst at ${intensityX}z with ${aggrPct}% buy aggression; watch for exhaustion once the burst stops accelerating.`,
      };

    case "CASCADE_SELL":
      if (oppositeAbsorption) {
        return {
          headline: "Cascade into bid wall",
          detail: "Extreme sell tape is colliding with strong bid absorption below; this is the highest-risk zone for either a break through the defense or a sharp exhaustion turn.",
        };
      }
      if (noMeaningfulAbsorption) {
        return {
          headline: "Pure sell momentum",
          detail: `Tape is in an extreme sell burst at ${intensityX}z with no meaningful bid absorption below; treat this as momentum until the burst slows or support appears.`,
        };
      }
      return {
        headline: structuralDirection === "SHORT" ? "Sell cascade confirms short thesis" : "Sell cascade conditions",
        detail: `Tape is in an extreme sell burst at ${intensityX}z with ${100 - aggrPct}% sell aggression; watch for exhaustion once the burst stops accelerating.`,
      };

    default:
      return {
        headline: "No tape signal",
        detail: "Trade flow is not materially directional right now.",
      };
  }
}


// ---------------------------------------------------------------------------
// BOOKMAP TAB — Dedicated Bookmap intel with CVD chart, walls table, wall heatmap
// ---------------------------------------------------------------------------

function BookmapTab({ API, currentPrice: dashboardPrice, selectedAsset }) {
  const [bmData, setBmData] = useState(null);
  const [cvdHistory, setCvdHistory] = useState([]);
  const [instrument, setInstrument] = useState(null);
  const [spotCvd, setSpotCvd] = useState(null);
  const [absorptionData, setAbsorptionData] = useState(null);
  const [tapeData, setTapeData] = useState(null);
  const [mmData, setMmData] = useState(null);
  const [wallSnapshot, setWallSnapshot] = useState(null);
  const isMobile = useIsMobile();

  // Poll spot CVD every 10s
  useEffect(() => {
    if (!selectedAsset) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/orderflow/spot/" + selectedAsset, { signal: controller.signal });
        if (r.ok) setSpotCvd(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] spot:", e); }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, selectedAsset]);

  // Poll /bookmap/intel every 10s
  useEffect(() => {
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await fetch(API + "/bookmap/intel", { signal: controller.signal });
        if (r.ok) {
          const d = await r.json();
          setBmData(d);
          // Auto-select first instrument if none selected
          const insts = d.instruments || {};
          const keys = Object.keys(insts);
          if (keys.length > 0) {
            setInstrument(prev => prev && insts[prev] ? prev : keys[0]);
          }
        }
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] poll:", e); }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API]);

  // Re-select instrument when selectedAsset changes (Bug 3: bind Bookmap to asset selector)
  useEffect(() => {
    const insts = bmData?.instruments || {};
    const keys = Object.keys(insts);
    const assetKey = selectedAsset?.toUpperCase();
    if (!assetKey) return;

    const matchesAsset = (key) => {
      const ku = key.toUpperCase();
      return ku.startsWith(assetKey + "-") ||
             ku.startsWith(assetKey + "_") ||
             ku.startsWith(assetKey + ":") ||
             ku === assetKey;
    };

    const matching = keys.filter(matchesAsset);
    setInstrument(prev => (prev && matching.includes(prev) ? prev : (matching[0] || null)));
  }, [bmData, selectedAsset]);

  // Fetch CVD history when instrument changes (or every 30s)
  useEffect(() => {
    if (!instrument) return;
    const controller = new AbortController();
    const fetchHistory = async () => {
      try {
        const r = await fetch(API + "/bookmap/cvd-history/" + encodeURIComponent(instrument), { signal: controller.signal });
        if (r.ok) {
          const d = await r.json();
          setCvdHistory(d.points || []);
        }
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] cvd-history:", e); }
    };
    fetchHistory();
    const t = setInterval(fetchHistory, 30000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, instrument]);

  // Poll absorption data every 10s
  useEffect(() => {
    if (!selectedAsset) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/bookmap/absorptions/" + selectedAsset, { signal: controller.signal });
        if (r.ok) setAbsorptionData(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] absorptions:", e); }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, selectedAsset]);

  // Poll tape data every 1s
  useEffect(() => {
    if (!selectedAsset) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/bookmap/tape/" + selectedAsset, { signal: controller.signal });
        if (r.ok) setTapeData(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] tape:", e); }
    };
    poll();
    const t = setInterval(poll, 1000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, selectedAsset]);

  // Poll MM corridor data every 1s
  useEffect(() => {
    if (!selectedAsset) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/bookmap/mm/" + selectedAsset, { signal: controller.signal });
        if (r.ok) setMmData(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] mm:", e); }
    };
    poll();
    const t = setInterval(poll, 1000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, selectedAsset]);

  // Poll wall snapshot every 10s
  useEffect(() => {
    if (!selectedAsset) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const r = await authFetch(API + "/bookmap/wall_snapshot?asset=" + selectedAsset, { signal: controller.signal });
        if (r.ok) setWallSnapshot(await r.json());
      } catch (e) { if (e.name !== "AbortError") console.error("[BookmapTab] wall_snapshot:", e); }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API, selectedAsset]);

  const instruments = bmData?.instruments || {};
  const instKeys = Object.keys(instruments);
  const inst = instrument ? instruments[instrument] : null;
  const composites = bmData?.composites || {};

  // Classify instruments by type for the selected asset
  const instByType = useMemo(() => {
    const result = {};
    for (const [key, val] of Object.entries(instruments)) {
      const ku = key.toUpperCase();
      let itype = "perp";
      if (ku.includes(".CME")) itype = "cme";
      else if (ku.includes(":SP@") || ku.endsWith(":SP")) itype = "spot";
      result[itype] = { key, ...val };
    }
    return result;
  }, [instruments]);

  // Parse data age
  const now = Date.now() / 1000;
  const age = inst?.server_received ? Math.round(now - inst.server_received) : null;
  const ageColor = age == null ? "#4a5568" : age < 30 ? "#4dff91" : age < 60 ? "#f6ad55" : "#ff6b6b";
  const dotColor = age == null ? "#ff6b6b" : age < 30 ? "#4dff91" : age < 60 ? "#f6ad55" : "#ff6b6b";

  // Parse walls string: "side size @ price; side size @ price; ..."
  const walls = useMemo(() => {
    if (!inst?.walls || typeof inst.walls !== "string") return [];
    return inst.walls.split(";").map(s => s.trim()).filter(Boolean).map(seg => {
      const m = seg.match(/^(bid|ask)\s+([\d,.]+)\s+@\s+([\d,.]+)$/i);
      if (!m) return null;
      return { side: m[1].toLowerCase(), size: parseFloat(m[2].replace(/,/g, "")), price: parseFloat(m[3].replace(/,/g, "")) };
    }).filter(Boolean).sort((a, b) => b.size - a.size);
  }, [inst?.walls]);

  // Current CVD
  const cvdVal = typeof inst?.cvd === "number" ? inst.cvd : null;
  const cvdColor = cvdVal == null ? "#4a5568" : cvdVal > 0 ? "#4dff91" : cvdVal < 0 ? "#ff6b6b" : "#718096";

  // Wall count
  const wallCountFromSummary = inst?.summary?.match(/([\d]+)\s+walls/)?.[1] || walls.length;

  // CVD chart data
  const chartData = useMemo(() => {
    return cvdHistory.map(p => ({
      time: p.ts,
      cvd: p.cvd,
    }));
  }, [cvdHistory]);

  // Wall heatmap: group walls by price level buckets
  const wallBuckets = useMemo(() => {
    if (!walls.length) return { bids: [], asks: [], maxSize: 1 };
    const bids = walls.filter(w => w.side === "bid").sort((a, b) => b.price - a.price);
    const asks = walls.filter(w => w.side === "ask").sort((a, b) => a.price - b.price);
    const maxSize = Math.max(...walls.map(w => w.size), 1);
    return { bids, asks, maxSize };
  }, [walls]);

  // Compute mid-price from best bid/ask or innermost walls
  const midPrice = useMemo(() => {
    if (typeof inst?.best_bid === "number" && typeof inst?.best_ask === "number") {
      return (inst.best_bid + inst.best_ask) / 2;
    }
    if (wallBuckets.bids.length && wallBuckets.asks.length) {
      return (wallBuckets.bids[0].price + wallBuckets.asks[0].price) / 2;
    }
    return walls[0]?.price ?? null;
  }, [inst?.best_bid, inst?.best_ask, wallBuckets, walls]);

  const sectionStyle = { background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: 16, marginBottom: 16 };
  const headerStyle = { fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 };
  const fmtChartTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // --- Orderflow state derivation ---
  const instrumentMatchesAsset = instrument && selectedAsset &&
    instrument.toLowerCase().startsWith(selectedAsset.toLowerCase());

  const orderflowState = useMemo(() => {
    const cp = (instrumentMatchesAsset && dashboardPrice) || midPrice;
    const history = cvdHistory || [];
    const nowMs = Date.now();

    // CVD deltas from history (fixed 60s tolerance)
    let cvdDelta1m = null, cvdDelta5m = null;
    if (history.length >= 2 && cvdVal != null) {
      const find = (agoMs) => {
        const target = nowMs - agoMs;
        let best = null;
        for (let i = history.length - 1; i >= 0; i--) {
          const diff = Math.abs(history[i].ts - target);
          if (!best || diff < best.diff) best = { diff, cvd: history[i].cvd };
          if (history[i].ts < target - 30000) break;
        }
        return best && best.diff < 60000 ? best.cvd : null;  // 60s max tolerance
      };
      const cvd1mAgo = find(60000);
      const cvd5mAgo = find(300000);
      if (cvd1mAgo != null) cvdDelta1m = cvdVal - cvd1mAgo;
      if (cvd5mAgo != null) cvdDelta5m = cvdVal - cvd5mAgo;
    }

    // Compute scale from recent CVD tick-to-tick deltas (normalizes thresholds across instruments)
    const recentDeltas = [];
    for (let i = 1; i < history.length; i++) {
      recentDeltas.push(Math.abs(history[i].cvd - history[i - 1].cvd));
    }
    const sortedDeltas = recentDeltas.sort((a, b) => a - b);
    const medianDelta = sortedDeltas.length > 0 ? sortedDeltas[Math.floor(sortedDeltas.length / 2)] : 10;
    const scale = Math.max(medianDelta * 2, 1);

    // Two-layer regime: structure (walls) + impulse (CVD slope)
    // Layer 2 impulse from CVD slope
    let impulse = "FLAT", impulseColor = "#718096";
    const d1 = cvdDelta1m, d5 = cvdDelta5m;
    if (d1 != null && d5 != null) {
      if (d1 > scale && d5 > scale * 2) { impulse = "BUYING"; impulseColor = "#4dff91"; }
      else if (d1 < -scale && d5 < -scale * 2) { impulse = "SELLING"; impulseColor = "#ff6b6b"; }
      else if ((d5 > scale * 4 && d1 < -scale) || (d5 < -scale * 4 && d1 > scale)) { impulse = "DIVERGENT"; impulseColor = "#f6ad55"; }
    } else if (cvdVal != null) {
      if (cvdVal > scale * 10) { impulse = "BUYING"; impulseColor = "#4dff91"; }
      else if (cvdVal < -scale * 10) { impulse = "SELLING"; impulseColor = "#ff6b6b"; }
    }

    // Wall analysis near spot
    const askWalls = walls.filter(w => w.side === "ask");
    const bidWalls = walls.filter(w => w.side === "bid");

    let nearestAsk = null, nearestBid = null;
    let askWithin2pct = { count: 0, totalSize: 0 };
    let bidWithin2pct = { count: 0, totalSize: 0 };

    if (cp && cp > 0) {
      // Nearest ask above
      const asksAbove = askWalls.filter(w => w.price > cp).sort((a, b) => a.price - b.price);
      if (asksAbove.length) {
        nearestAsk = { ...asksAbove[0], distPct: ((asksAbove[0].price - cp) / cp) * 100 };
      }
      // Nearest bid below
      const bidsBelow = bidWalls.filter(w => w.price < cp).sort((a, b) => b.price - a.price);
      if (bidsBelow.length) {
        nearestBid = { ...bidsBelow[0], distPct: ((cp - bidsBelow[0].price) / cp) * 100 };
      }
      // Dedup set for absorption fallback
      const wallPrices = new Set(walls.map(w => w.price.toFixed(2) + w.side));

      // Absorption fallback for nearest overhead/support (only if no wall at that price)
      if (!nearestAsk && absorptionData?.active_absorptions) {
        const askAbs = absorptionData.active_absorptions
          .filter(a => a.side === "ask" && a.price > cp && !wallPrices.has((a.price?.toFixed(2) || "0") + "ask"))
          .sort((a, b) => a.price - b.price);
        if (askAbs.length > 0) {
          nearestAsk = { price: askAbs[0].price, size: askAbs[0].current_size || askAbs[0].initial_size || 0, side: "ask", distPct: ((askAbs[0].price - cp) / cp) * 100 };
        }
      }
      if (!nearestBid && absorptionData?.active_absorptions) {
        const bidAbs = absorptionData.active_absorptions
          .filter(a => a.side === "bid" && a.price < cp && !wallPrices.has((a.price?.toFixed(2) || "0") + "bid"))
          .sort((a, b) => b.price - a.price);
        if (bidAbs.length > 0) {
          nearestBid = { price: bidAbs[0].price, size: bidAbs[0].current_size || bidAbs[0].initial_size || 0, side: "bid", distPct: ((cp - bidAbs[0].price) / cp) * 100 };
        }
      }
      // Within 2%
      for (const w of askWalls) {
        if (w.price > cp && (w.price - cp) / cp <= 0.02) {
          askWithin2pct.count++;
          askWithin2pct.totalSize += w.size;
        }
      }
      for (const w of bidWalls) {
        if (w.price < cp && (cp - w.price) / cp <= 0.02) {
          bidWithin2pct.count++;
          bidWithin2pct.totalSize += w.size;
        }
      }
      // Include active absorptions in within-2% totals — only levels not already in walls
      if (absorptionData?.active_absorptions) {
        absorptionData.active_absorptions.forEach(a => {
          const key = (a.price?.toFixed(2) || "0") + a.side;
          if (wallPrices.has(key)) return;
          const dist = Math.abs(a.price - cp) / cp;
          if (dist > 0.02) return;
          const sz = a.current_size || a.initial_size || 0;
          if (a.side === "ask" && a.price > cp) askWithin2pct.totalSize += sz;
          if (a.side === "bid" && a.price < cp) bidWithin2pct.totalSize += sz;
        });
      }
    }

    // Imbalance
    const askTotal = askWithin2pct.totalSize;
    const bidTotal = bidWithin2pct.totalSize;
    const imbalanceTotal = askTotal + bidTotal;
    let imbalanceLabel = "BALANCED", imbalanceColor = "#718096", imbalanceSub = "";
    if (imbalanceTotal > 0) {
      if (askTotal > 0 && bidTotal === 0) {
        imbalanceLabel = "OVERHEAD HEAVY";
        imbalanceColor = "#ff6b6b";
        imbalanceSub = "All ask-side";
      } else if (bidTotal > 0 && askTotal === 0) {
        imbalanceLabel = "SUPPORT HEAVY";
        imbalanceColor = "#4dff91";
        imbalanceSub = "All bid-side";
      } else {
        const ratio = askTotal > bidTotal ? askTotal / bidTotal : bidTotal / askTotal;
        const ratioStr = ratio > 99.9 ? "99.9" : ratio.toFixed(1);
        if (ratio > 1.25 && askTotal > bidTotal) {
          imbalanceLabel = "OVERHEAD HEAVY";
          imbalanceColor = "#ff6b6b";
          imbalanceSub = `Ask ${ratioStr}x bid`;
        } else if (ratio > 1.25 && bidTotal > askTotal) {
          imbalanceLabel = "SUPPORT HEAVY";
          imbalanceColor = "#4dff91";
          imbalanceSub = `Bid ${ratioStr}x ask`;
        } else {
          imbalanceSub = `Ratio ${ratioStr}x`;
        }
      }
    }

    // Layer 1 structure from wall imbalance (reuse computed imbalance)
    const structure = imbalanceLabel;  // "OVERHEAD HEAVY" | "SUPPORT HEAVY" | "BALANCED"
    const structureColor = imbalanceColor;

    // Deterministic sentence
    const sentenceParts = [];
    if (cvdVal != null) {
      let cvdState = cvdVal > 0 ? "Positive CVD" : cvdVal < 0 ? "Negative CVD" : "Neutral CVD";
      if (impulse === "BUYING") cvdState += " and strengthening";
      else if (impulse === "SELLING") cvdState += " and weakening";
      else if (impulse === "DIVERGENT") cvdState += " but reversing direction";
      sentenceParts.push(cvdState);
    }
    if (structure === "OVERHEAD HEAVY") {
      sentenceParts.push("asks dominate near spot");
      if (!nearestBid) sentenceParts.push("no visible bid support");
    } else if (structure === "SUPPORT HEAVY") {
      sentenceParts.push("bids dominate near spot");
      if (!nearestAsk) sentenceParts.push("no visible overhead resistance");
    } else {
      sentenceParts.push("wall pressure is balanced");
    }
    const sentence = sentenceParts.length > 0 ? sentenceParts.join(", ") + "." : "";

    return {
      structure, structureColor, impulse, impulseColor, sentence,
      cvdDelta1m, cvdDelta5m,
      nearestAsk, nearestBid, askWithin2pct, bidWithin2pct,
      imbalanceLabel, imbalanceColor, imbalanceSub, askTotal, bidTotal, imbalanceTotal,
    };
  }, [cvdVal, cvdHistory, instrumentMatchesAsset, dashboardPrice, midPrice, walls, absorptionData]);

  // Consistent reference price — only use dashboard price when instrument matches
  const refPrice = (instrumentMatchesAsset && dashboardPrice) || midPrice;

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 1 — Header Bar
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {isMobile && (
        <style>{`
          .bm-grid-3 { grid-template-columns: 1fr !important; }
          .bm-grid-2 { grid-template-columns: 1fr !important; }
          .bm-header-flex { flex-wrap: wrap !important; gap: 8px !important; }
          .bm-wall-grid { grid-template-columns: 40px 50px 1fr 55px !important; }
          .bm-wall-hdr { grid-template-columns: 40px 50px 1fr 55px !important; }
          .bm-event-grid { grid-template-columns: 55px 40px 40px 1fr 50px !important; }
          .bm-event-hdr { grid-template-columns: 55px 40px 40px 1fr 50px !important; }
          .bm-impulse-num { font-size: 24px !important; }
          .bm-imbalance-num { font-size: 20px !important; }
          .bm-section { padding: 10px !important; }
          .bm-regime-flex { flex-wrap: wrap !important; gap: 8px !important; }
        `}</style>
      )}

      <div className="bm-header-flex" style={{ ...sectionStyle, padding: 20, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        {/* Connection status dot */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor, boxShadow: `0 0 6px ${dotColor}`, animation: age != null && age < 30 ? "pulse 2s infinite" : "none" }} />
          <span style={{ fontSize: 10, color: dotColor, letterSpacing: 1, fontWeight: 700 }}>
            {age == null ? "NO DATA" : age < 30 ? "LIVE" : age < 60 ? "STALE" : "OFFLINE"}
          </span>
        </div>

        {/* Instrument selector */}
        {instKeys.length > 1 && (
          <div style={{ display: "flex", gap: 6 }}>
            {instKeys.map(k => (
              <button key={k} className="btn" onClick={() => setInstrument(k)} style={{
                background: instrument === k ? "#4da2ff20" : "transparent",
                border: `1px solid ${instrument === k ? "#4da2ff" : "#2d3f52"}`,
                color: instrument === k ? "#4da2ff" : "#a0aec0",
                padding: "3px 10px", borderRadius: 4, fontFamily: "monospace", fontSize: 10, fontWeight: 700,
              }}>{(instruments[k]?.alias || k).replace(/@.*$/, "")}</button>
            ))}
          </div>
        )}

        {/* Instrument name */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>
            {inst?.alias || instrument || "—"}
          </div>
        </div>

        {/* CVD value — large */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 2 }}>CVD</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: cvdColor, fontFamily: "'Space Mono', monospace" }}>
            {cvdVal != null ? (cvdVal > 0 ? "+" : "") + cvdVal.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
          </div>
        </div>

        {/* Wall count */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 2 }}>WALLS</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>{wallCountFromSummary}</div>
        </div>

        {/* Data age */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 2 }}>AGE</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: ageColor, fontFamily: "'Space Mono', monospace" }}>
            {age != null ? age + "s" : "—"}
          </div>
        </div>
      </div>


      {/* Multi-Instrument Panel */}
      {Object.keys(composites).length > 0 && (() => {
        const assetComp = composites[selectedAsset?.toUpperCase()] || composites[selectedAsset] || null;
        if (!assetComp) return null;
        const active = assetComp.instruments_active || [];
        const perInst = assetComp.per_instrument_cvd || {};
        const agreement = assetComp.cvd_agreement || 0;
        const total = assetComp.cvd_agreement_total || 0;
        const compCvd = assetComp.cvd_composite;
        const spDiv = assetComp.spot_perp_cvd_divergence;
        const cmePrem = assetComp.cme_premium_pct;
        return (
          <div className="bm-section" style={{ ...sectionStyle, padding: "10px 16px" }}>
            <div className="bm-header-flex" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>INSTRUMENTS</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {["perp", "spot", "cme"].map(t => (
                    <span key={t} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, fontWeight: 700, background: active.includes(t) ? "#4dff9120" : "#1a2030", color: active.includes(t) ? "#4dff91" : "#4a5568", border: `1px solid ${active.includes(t) ? "#4dff9140" : "#1a2030"}` }}>{t.toUpperCase()}</span>
                  ))}
                </div>
              </div>
              {compCvd != null && (
                <div>
                  <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>COMPOSITE CVD</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: compCvd > 0 ? "#4dff91" : compCvd < 0 ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace" }}>{compCvd > 0 ? "+" : ""}{compCvd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              )}
              {Object.keys(perInst).length > 1 && (
                <div>
                  <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>PER INSTRUMENT</div>
                  <div style={{ display: "flex", gap: 8, fontSize: 11, fontFamily: "'Space Mono', monospace" }}>
                    {Object.entries(perInst).map(([t, v]) => (
                      <span key={t} style={{ color: v > 0 ? "#4dff91" : v < 0 ? "#ff6b6b" : "#718096" }}>{t}: {v > 0 ? "+" : ""}{v.toFixed(0)}</span>
                    ))}
                  </div>
                </div>
              )}
              {total > 0 && (
                <div>
                  <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>AGREEMENT</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: agreement >= total ? "#4dff91" : agreement > 1 ? "#f6ad55" : "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>{agreement}/{total}</div>
                </div>
              )}
              {spDiv != null && spDiv !== 0 && (
                <div>
                  <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>SPOT-PERP DIV</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: spDiv > 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>{spDiv > 0 ? "+" : ""}{spDiv.toFixed(0)}</div>
                </div>
              )}
              {cmePrem != null && (
                <div>
                  <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>CME PREMIUM</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: cmePrem > 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>{cmePrem > 0 ? "+" : ""}{cmePrem.toFixed(3)}%</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Absorption Score Bars */}
      {absorptionData?.absorption_summary && (() => {
        const s = absorptionData.absorption_summary;
        const bidScore = s.bid_absorption_score || 0;
        const askScore = s.ask_absorption_score || 0;
        const bias = s.net_absorption_bias || "neutral";
        const held = s.walls_held_30m || 0;
        const swept = s.walls_swept_30m || 0;
        const pulled = s.walls_pulled_30m || 0;
        return (
          <div style={{ ...sectionStyle, padding: "10px 16px" }}>
            <div className="bm-header-flex" style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>BID ABSORPTION</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 10, background: "#1a2030", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: bidScore + "%", height: "100%", background: "#4dff91", borderRadius: 5, transition: "width 0.5s" }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#4dff91", fontFamily: "'Space Mono', monospace", minWidth: 32 }}>{bidScore}</span>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>ASK ABSORPTION</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 10, background: "#1a2030", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: askScore + "%", height: "100%", background: "#ff6b6b", borderRadius: 5, transition: "width 0.5s" }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#ff6b6b", fontFamily: "'Space Mono', monospace", minWidth: 32 }}>{askScore}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>BIAS</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: bias === "bid" ? "#4dff91" : bias === "ask" ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace", textTransform: "uppercase" }}>{bias}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>30M EVENTS</div>
                <div style={{ display: "flex", gap: 8, fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                  <span style={{ color: "#4dff91" }}>{held}H</span>
                  <span style={{ color: "#ff6b6b" }}>{swept}S</span>
                  <span style={{ color: "#f6ad55" }}>{pulled}P</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* PACE OF TAPE */}
      {(() => {
        const tape = tapeData?.latest;
        if (!tape) return null;
        const imp = tape.tape_impulse || 0;
        const st = tape.tape_state || "QUIET";
        const stColors = { QUIET: "#4a5568", BUY_BUILDING: "#4dff91", SELL_BUILDING: "#ff6b6b", BUY_EXHAUSTING: "#f6ad55", SELL_EXHAUSTING: "#f6ad55", BALANCED_ACTIVE: "#63b3ed", CASCADE_BUY: "#4dff91", CASCADE_SELL: "#ff6b6b" };
        const stColor = stColors[st] || "#4a5568";
        const isCascade = st.startsWith("CASCADE");
        const impColor = Math.abs(imp) < 0.5 ? "#718096" : imp > 0 ? "#4dff91" : "#ff6b6b";
        const aggr = tape.aggression_ratio ?? 0.5;
        const aggrPct = Math.round(aggr * 100);
        const hist = tapeData?.history || [];
        const chartPts = hist.slice(-60);

        return (
          <div style={sectionStyle}>
            <div style={headerStyle}>PACE OF TAPE</div>
            <div className="bm-grid-3" style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr", gap: 16, padding: "10px 16px" }}>
              {/* Left: Big impulse + state */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div className="bm-impulse-num" style={{ fontSize: 32, fontWeight: 700, color: impColor, fontFamily: "'Space Mono', monospace", animation: isCascade ? "pulse 1s infinite" : "none" }}>
                  {imp > 0 ? "+" : ""}{imp.toFixed(2)}
                </div>
                <div style={{ marginTop: 6, padding: "3px 10px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#0a0e14", background: stColor, animation: isCascade ? "pulse 1s infinite" : "none" }}>
                  {st.replace(/_/g, " ")}
                </div>
              </div>
              {/* Middle: Metrics */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                  <span style={{ color: "#718096" }}>INTENSITY Z</span>
                  <span style={{ color: (tape.signed_intensity_z || 0) > 0 ? "#4dff91" : (tape.signed_intensity_z || 0) < 0 ? "#ff6b6b" : "#718096", fontWeight: 700 }}>
                    {(tape.signed_intensity_z || 0).toFixed(2)}
                  </span>
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#718096", marginBottom: 2 }}>
                    <span>SELL</span><span>AGGRESSION</span><span>BUY</span>
                  </div>
                  <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "#1a2030" }}>
                    <div style={{ width: (100 - aggrPct) + "%", background: "#ff6b6b", transition: "width 0.3s" }} />
                    <div style={{ width: aggrPct + "%", background: "#4dff91", transition: "width 0.3s" }} />
                  </div>
                  <div style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: aggrPct > 55 ? "#4dff91" : aggrPct < 45 ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace", marginTop: 2 }}>{aggrPct}%</div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                  <span style={{ color: "#718096" }}>VOL ACCEL</span>
                  <span style={{ color: (tape.vol_accel || 0) > 0.1 ? "#4dff91" : (tape.vol_accel || 0) < -0.1 ? "#ff6b6b" : "#718096", fontWeight: 700 }}>
                    {(tape.vol_accel || 0) > 0 ? "\u25B2" : (tape.vol_accel || 0) < -0.05 ? "\u25BC" : "\u25C6"} {((tape.vol_accel || 0) * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "'Space Mono', monospace" }}>
                  <span style={{ color: "#718096" }}>TRADES/S</span>
                  <span style={{ color: "#a0aec0", fontWeight: 700 }}>{(tape.trade_rate_3s || 0).toFixed(1)}</span>
                </div>
              </div>
              {/* Right: 60s impulse mini-chart */}
              <div style={{ minHeight: 100 }}>
                {chartPts.length > 2 ? (
                  <ResponsiveContainer width="100%" height={110}>
                    <AreaChart data={chartPts} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="tapeGreen" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4dff91" stopOpacity={0.4} /><stop offset="100%" stopColor="#4dff91" stopOpacity={0.05} /></linearGradient>
                        <linearGradient id="tapeRed" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#ff6b6b" stopOpacity={0.4} /><stop offset="100%" stopColor="#ff6b6b" stopOpacity={0.05} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a203020" />
                      <YAxis domain={["auto", "auto"]} hide />
                      <ReferenceLine y={0} stroke="#4a5568" strokeWidth={1} />
                      <ReferenceLine y={1.5} stroke="#4dff9140" strokeDasharray="3 3" />
                      <ReferenceLine y={-1.5} stroke="#ff6b6b40" strokeDasharray="3 3" />
                      <Area type="monotone" dataKey="tape_impulse" stroke={imp >= 0 ? "#4dff91" : "#ff6b6b"} fill={imp >= 0 ? "url(#tapeGreen)" : "url(#tapeRed)"} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: "#4a5568", fontSize: 11, textAlign: "center", paddingTop: 40 }}>Waiting for tape data...</div>
                )}
              </div>
            </div>
            {/* Narrative */}
            {(() => {
              const narrative = buildTapeNarrative({
                tapeImpulse: tape.tape_impulse || 0,
                signedIntensityZ: tape.signed_intensity_z || 0,
                aggressionRatio: tape.aggression_ratio || 0.5,
                volAccel: tape.vol_accel || 0,
                tradeRate3s: tape.trade_rate_3s || 0,
                tapeState: tape.tape_state || "QUIET",
                bidAbsorptionScore: absorptionData?.absorption_summary?.bid_absorption_score || 0,
                askAbsorptionScore: absorptionData?.absorption_summary?.ask_absorption_score || 0,
                netAbsorptionBias: absorptionData?.absorption_summary?.net_absorption_bias || "neutral",
                structuralDirection: "NEUTRAL",
              });
              return (
                <div style={{ padding: "8px 16px", borderTop: "1px solid #1a2030" }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700,
                    color: st.includes("CASCADE") ? "#ff6b6b" : st.includes("BUILD") ? (imp > 0 ? "#4dff91" : "#ff6b6b") : st.includes("EXHAUST") ? "#f6ad55" : "#a0aec0",
                    fontFamily: "'Space Mono', monospace", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4,
                  }}>
                    {narrative.headline}
                  </div>
                  <div style={{ fontSize: 11, color: "#718096", fontStyle: "italic", lineHeight: 1.5 }}>
                    {narrative.detail}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* MM CORRIDOR LIQUIDITY */}
      {(() => {
        const mm = mmData?.latest;
        if (!mm) return null;
        const mmSt = mm.state || "BALANCED";
        const mmStColors = { BALANCED: "#4a5568", BID_HEAVY: "#4dff91", ASK_HEAVY: "#ff6b6b", BID_PULL: "#ff6b6b", ASK_PULL: "#4dff91", TWO_SIDED_PULL: "#f6ad55" };
        const mmStColor = mmStColors[mmSt] || "#4a5568";
        const imbPct = Math.round((mm.imbalance_3s || 0) * 100);
        const imbColor = imbPct > 0 ? "#4dff91" : imbPct < 0 ? "#ff6b6b" : "#718096";
        const bidPull = Math.round((mm.bid_pull || 0) * 100);
        const askPull = Math.round((mm.ask_pull || 0) * 100);
        const maxLiq = Math.max(mm.bid_total || 1, mm.ask_total || 1, 1);
        const narratives = {
          BALANCED: "MM liquidity is balanced across both corridors. No directional lean from market makers.",
          BID_HEAVY: `Market makers are showing ${Math.abs(imbPct)}% more bid liquidity \u2014 leaning long. Watch for confirmation from tape.`,
          ASK_HEAVY: `Market makers are showing ${Math.abs(imbPct)}% more ask liquidity \u2014 leaning short.`,
          BID_PULL: `Bid-side MM liquidity pulled \u2014 support withdrawn. Bearish tell if confirmed by tape.`,
          ASK_PULL: `Ask-side MM liquidity pulled \u2014 overhead removed. Bullish tell if confirmed by tape.`,
          TWO_SIDED_PULL: "Both sides pulling liquidity \u2014 volatility expansion expected. MMs stepping aside.",
        };
        return (
          <div style={sectionStyle}>
            <div style={headerStyle}>MARKET MAKER LIQUIDITY</div>
            <div className="bm-grid-3" style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 16, padding: "10px 16px" }}>
              {/* Left: State + imbalance */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ padding: "3px 10px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#0a0e14", background: mmStColor, marginBottom: 8 }}>
                  {mmSt.replace(/_/g, " ")}
                </div>
                <div className="bm-imbalance-num" style={{ fontSize: 24, fontWeight: 700, color: imbColor, fontFamily: "'Space Mono', monospace" }}>
                  {imbPct > 0 ? "+" : ""}{imbPct}%
                </div>
                <div style={{ fontSize: 9, color: "#4a5568", marginTop: 2 }}>IMBALANCE</div>
              </div>
              {/* Middle: Bid/Ask bars */}
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1 }}>BID CORRIDOR</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, height: 14, background: "#1a2030", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min((mm.bid_total || 0) / maxLiq * 100, 100)}%`, height: "100%", background: "#4dff91", borderRadius: 4, transition: "width 0.3s" }} />
                  </div>
                  <span style={{ fontSize: 10, color: "#4dff91", fontWeight: 700, fontFamily: "'Space Mono', monospace", minWidth: 40 }}>{(mm.bid_total || 0).toFixed(0)}</span>
                </div>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1 }}>ASK CORRIDOR</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, height: 14, background: "#1a2030", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min((mm.ask_total || 0) / maxLiq * 100, 100)}%`, height: "100%", background: "#ff6b6b", borderRadius: 4, transition: "width 0.3s" }} />
                  </div>
                  <span style={{ fontSize: 10, color: "#ff6b6b", fontWeight: 700, fontFamily: "'Space Mono', monospace", minWidth: 40 }}>{(mm.ask_total || 0).toFixed(0)}</span>
                </div>
                <div style={{ fontSize: 10, color: "#4a5568", fontFamily: "'Space Mono', monospace" }}>{mm.bid_count || 0} bids | {mm.ask_count || 0} asks</div>
              </div>
              {/* Right: Pull indicators */}
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 3 }}>BID PULL</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ flex: 1, height: 10, background: "#1a2030", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ width: bidPull + "%", height: "100%", background: bidPull >= 60 ? "#ff6b6b" : "#ff6b6b60", borderRadius: 5, transition: "width 0.3s", animation: bidPull >= 60 ? "pulse 1s infinite" : "none" }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: bidPull >= 60 ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace", minWidth: 28 }}>{bidPull}%</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 3 }}>ASK PULL</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ flex: 1, height: 10, background: "#1a2030", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ width: askPull + "%", height: "100%", background: askPull >= 60 ? "#4dff91" : "#4dff9160", borderRadius: 5, transition: "width 0.3s", animation: askPull >= 60 ? "pulse 1s infinite" : "none" }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: askPull >= 60 ? "#4dff91" : "#718096", fontFamily: "'Space Mono', monospace", minWidth: 28 }}>{askPull}%</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Narrative */}
            <div style={{ padding: "8px 16px", borderTop: "1px solid #1a2030" }}>
              <div style={{ fontSize: 11, color: "#718096", fontStyle: "italic", lineHeight: 1.5 }}>
                {narratives[mmSt] || narratives.BALANCED}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Spot CVD — Multi-Exchange Aggregate */}
      {spotCvd && spotCvd.exchanges_connected && spotCvd.exchanges_connected.length > 0 && (
        <div style={{ ...sectionStyle, padding: "10px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 4 }}>SPOT CVD (AGGREGATE)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: spotCvd.aggregate_cvd > 0 ? "#4dff91" : spotCvd.aggregate_cvd < 0 ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace" }}>
                {spotCvd.aggregate_cvd > 0 ? "+" : ""}{spotCvd.aggregate_cvd?.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>1m / 5m</div>
              <div style={{ fontSize: 12, fontFamily: "'Space Mono', monospace" }}>
                <span style={{ color: spotCvd.cvd_1m > 0 ? "#4dff91" : spotCvd.cvd_1m < 0 ? "#ff6b6b" : "#718096" }}>{spotCvd.cvd_1m > 0 ? "+" : ""}{spotCvd.cvd_1m?.toFixed(2)}</span>
                {" / "}
                <span style={{ color: spotCvd.cvd_5m > 0 ? "#4dff91" : spotCvd.cvd_5m < 0 ? "#ff6b6b" : "#718096" }}>{spotCvd.cvd_5m > 0 ? "+" : ""}{spotCvd.cvd_5m?.toFixed(2)}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>EXCHANGES</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {["bybit", "binance", "coinbase", "kraken", "okx", "crypto_com", "bitstamp", "huobi"].map(ex => {
                  const connected = spotCvd.exchanges_connected?.includes(ex);
                  const cvdVal = spotCvd.per_exchange?.[ex];
                  return (
                    <span key={ex} title={connected ? ex + ": " + (cvdVal?.toFixed(2) || "0") : ex + ": offline"} style={{ fontSize: 8, padding: "1px 4px", borderRadius: 2, background: connected ? "#4dff9120" : "#1a2030", color: connected ? "#4dff91" : "#4a5568", border: "1px solid " + (connected ? "#4dff9130" : "#1a2030") }}>{ex.replace("_", "").substring(0, 4).toUpperCase()}</span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orderflow State Strip */}
      {inst && (
        <div style={{ ...sectionStyle, padding: "12px 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>

            {/* Regime (two-layer: structure + impulse) */}
            <div style={{ gridColumn: "span 2", minWidth: 300 }}>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>ORDERFLOW REGIME</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: orderflowState.structureColor, fontFamily: "'Space Mono', monospace" }}>
                  {orderflowState.structure}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: orderflowState.impulseColor, fontFamily: "'Space Mono', monospace" }}>
                  {orderflowState.impulse}
                </span>
              </div>
              {orderflowState.sentence && (
                <div style={{ fontSize: 11, color: "#a0aec0", lineHeight: 1.4 }}>{orderflowState.sentence}</div>
              )}
            </div>

            {/* CVD Momentum */}
            <div>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>CVD MOMENTUM</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: cvdColor, fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>
                {cvdVal != null ? (cvdVal > 0 ? "+" : "") + cvdVal.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
              </div>
              <div style={{ fontSize: 10, color: "#4a5568" }}>
                {orderflowState.impulse !== "FLAT" ? orderflowState.impulse.charAt(0) + orderflowState.impulse.slice(1).toLowerCase() : "Flat"}
                {orderflowState.cvdDelta1m != null && ` (1m: ${orderflowState.cvdDelta1m > 0 ? "+" : ""}${orderflowState.cvdDelta1m.toFixed(0)})`}
              </div>
            </div>

            {/* Nearest Overhead */}
            <div>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>NEAREST OVERHEAD</div>
              {orderflowState.nearestAsk ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#ff6b6b", fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>
                    ${orderflowState.nearestAsk.price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span style={{ fontSize: 10, color: "#4a5568", fontWeight: 400, marginLeft: 6 }}>{orderflowState.nearestAsk.distPct.toFixed(2)}%</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#4a5568" }}>
                    Size: {orderflowState.nearestAsk.size.toFixed(1)} | {orderflowState.askWithin2pct.count} walls within 2% ({orderflowState.askWithin2pct.totalSize.toFixed(0)})
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#4a5568" }}>No overhead liquidity</div>
              )}
            </div>

            {/* Nearest Support */}
            <div>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>NEAREST SUPPORT</div>
              {orderflowState.nearestBid ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#4dff91", fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>
                    ${orderflowState.nearestBid.price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span style={{ fontSize: 10, color: "#4a5568", fontWeight: 400, marginLeft: 6 }}>{orderflowState.nearestBid.distPct.toFixed(2)}%</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#4a5568" }}>
                    Size: {orderflowState.nearestBid.size.toFixed(1)} | {orderflowState.bidWithin2pct.count} walls within 2% ({orderflowState.bidWithin2pct.totalSize.toFixed(0)})
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#f6ad55" }}>No bid support</div>
              )}
            </div>

            {/* Imbalance */}
            <div>
              <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>WALL IMBALANCE</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: orderflowState.imbalanceColor, fontFamily: "'Space Mono', monospace", marginBottom: 4 }}>
                {orderflowState.imbalanceLabel}
              </div>
              {orderflowState.imbalanceTotal > 0 ? (
                <>
                  <div style={{ fontSize: 10, color: "#4a5568", marginBottom: 4 }}>{orderflowState.imbalanceSub}</div>
                  <div style={{ display: "flex", height: 8, borderRadius: 3, overflow: "hidden", border: "1px solid #1a2030" }}>
                    <div style={{ width: `${orderflowState.imbalanceTotal > 0 ? (orderflowState.bidTotal / orderflowState.imbalanceTotal * 100) : 50}%`, background: "#4dff9140", minWidth: 2 }} />
                    <div style={{ flex: 1, background: "#ff6b6b40" }} />
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 10, color: "#4a5568" }}>No walls near spot</div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTION 2 — CVD Chart
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={{ ...sectionStyle, padding: 20 }}>
        <div style={headerStyle}>
          CVD TIME SERIES
          <span style={{ fontSize: 9, color: "#4a5568", fontWeight: 400, letterSpacing: 0 }}>Last 2 hours â€¢ 10s intervals</span>
        </div>
        {chartData.length < 3 ? (
          <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 60 }}>
            {!instrument ? "Waiting for Bookmap data..." : `Accumulating CVD history... (${chartData.length} points)`}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={chartData} margin={{ top: 10, right: 60, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="cvdGradUp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4dff91" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4dff91" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cvdGradDown" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff6b6b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
              <XAxis dataKey="time" tickFormatter={fmtChartTime} stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} minTickGap={80} />
              <YAxis stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={v => v > 0 ? "+" + v.toLocaleString() : v.toLocaleString()} />
              <Tooltip
                contentStyle={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 4, fontSize: 11, fontFamily: "'Space Mono', monospace" }}
                labelStyle={{ color: "#718096", fontSize: 10 }}
                labelFormatter={fmtChartTime}
                formatter={(v) => [(v > 0 ? "+" : "") + v.toLocaleString(undefined, { maximumFractionDigits: 2 }), "CVD"]}
              />
              <ReferenceLine y={0} stroke="#4a556880" strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="cvd"
                stroke={cvdVal != null && cvdVal >= 0 ? "#4dff91" : "#ff6b6b"}
                fill={cvdVal != null && cvdVal >= 0 ? "url(#cvdGradUp)" : "url(#cvdGradDown)"}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {/* Current CVD label */}
        {cvdVal != null && chartData.length >= 3 && (
          <div style={{ textAlign: "right", marginTop: -30, marginRight: 10, position: "relative", zIndex: 1 }}>
            <span style={{ background: cvdColor + "20", color: cvdColor, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700, fontFamily: "'Space Mono', monospace", border: `1px solid ${cvdColor}40` }}>
              {cvdVal > 0 ? "+" : ""}{cvdVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          SECTIONS 3 & 4 — Walls Table + Wall Heatmap (side by side)
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div className="bm-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* SECTION 3 — Order Book Walls Table (with absorption data) */}
        <div style={sectionStyle}>
          <div style={headerStyle}>ORDER BOOK WALLS</div>
          {(() => {
            // Merge wall data with active absorption state
            const activeAbs = absorptionData?.active_absorptions || [];
            const absMap = {};
            activeAbs.forEach(a => { if (a.price) absMap[a.price.toFixed(2) + a.side] = a; });
            const stateColors = { ABSORBING: "#63b3ed", HELD: "#4dff91", SWEPT: "#ff6b6b", PULLED: "#f6ad55", TESTED: "#a78bfa" };

            return walls.length === 0 ? (
              <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 30 }}>No wall data</div>
            ) : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                <div className="bm-wall-hdr" style={{ display: "grid", gridTemplateColumns: "40px 60px 1fr 55px 70px 40px 35px", padding: "6px 10px", borderBottom: "1px solid #1a2030", fontSize: 9, color: "#718096", letterSpacing: 2, position: "sticky", top: 0, background: "#0a0e14", zIndex: 1 }}>
                  <span>SIDE</span><span>SIZE</span><span>PRICE</span><span>DIST</span>{!isMobile && <><span>STATE</span><span>VOL</span><span>RF</span></>}
                </div>
                {(refPrice > 0 ? [...walls].sort((a, b) => Math.abs(a.price - refPrice) - Math.abs(b.price - refPrice)) : walls).slice(0, 30).map((w, i) => {
                  const dist = refPrice ? ((w.price - refPrice) / refPrice * 100) : null;
                  const isMajor = w.size >= 100;
                  const sideColor = w.side === "bid" ? "#4dff91" : "#ff6b6b";
                  const absInfo = absMap[w.price.toFixed(2) + w.side];
                  const wState = absInfo?.state || "";
                  const stColor = stateColors[wState] || "#4a5568";
                  const rowBg = wState === "ABSORBING" ? "#63b3ed10" : isMajor ? (sideColor + "08") : (i % 2 === 0 ? "#080c12" : "transparent");
                  return (
                    <div className="bm-wall-grid" key={i} style={{
                      display: "grid", gridTemplateColumns: "40px 60px 1fr 55px 70px 40px 35px",
                      padding: "5px 10px", fontSize: 11, fontFamily: "'Space Mono', monospace",
                      borderBottom: "1px solid #060a0f",
                      background: rowBg,
                      borderLeft: wState ? `2px solid ${stColor}` : isMajor ? `2px solid ${sideColor}` : "2px solid transparent",
                    }}>
                      <span style={{ color: sideColor, fontWeight: 700, fontSize: 10, textTransform: "uppercase" }}>{w.side}</span>
                      <span style={{ color: isMajor ? "#e2e8f0" : "#a0aec0", fontWeight: isMajor ? 700 : 400 }}>{w.size.toFixed(1)}</span>
                      <span style={{ color: "#a0aec0" }}>${w.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ color: dist != null ? (dist > 0 ? "#ff6b6b80" : "#4dff9180") : "#4a5568", fontSize: 10 }}>
                        {dist != null ? (dist > 0 ? "+" : "") + dist.toFixed(2) + "%" : "\u2014"}
                      </span>
                      {!isMobile && <span style={{ color: stColor, fontSize: 9, fontWeight: 700 }}>{wState || "\u2014"}</span>}
                      {!isMobile && <span style={{ color: "#a0aec0", fontSize: 10 }}>{absInfo ? absInfo.volume_absorbed?.toFixed(0) : "\u2014"}</span>}
                      {!isMobile && <span style={{ color: "#a0aec0", fontSize: 10 }}>{absInfo ? absInfo.refill_count : "\u2014"}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* SECTION 4 — Wall Heatmap (horizontal bar chart) */}
        <div style={sectionStyle}>
          <div style={headerStyle}>WALL DENSITY MAP</div>
          {walls.length === 0 ? (
            <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 30 }}>No wall data</div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {/* Bids on left (green), Asks on right (red), price in center */}
              {(() => {
                // Merge bids + asks sorted by price descending
                const all = [...wallBuckets.asks.slice().reverse(), ...wallBuckets.bids];
                const sorted = [...new Map(all.map(w => [w.price, w])).values()].sort((a, b) => b.price - a.price);
                const maxSz = wallBuckets.maxSize;
                return sorted.slice(0, 40).map((w, i) => {
                  const pct = Math.min(w.size / maxSz, 1);
                  const isBid = w.side === "bid";
                  const barColor = isBid ? "#4dff91" : "#ff6b6b";
                  const isMajor = w.size >= 100;
                  return (
                    <div key={i} style={{
                      display: "grid", gridTemplateColumns: "1fr 90px 1fr",
                      padding: "3px 8px", fontSize: 10, fontFamily: "'Space Mono', monospace",
                      alignItems: "center", gap: 4,
                      borderBottom: "1px solid #060a0f",
                      background: isMajor ? (barColor + "08") : "transparent",
                    }}>
                      {/* Left bar (bid) */}
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
                        {isBid && <span style={{ fontSize: 9, color: "#4dff9180" }}>{w.size.toFixed(0)}</span>}
                        <div style={{ height: 12, borderRadius: 2, background: isBid ? "#4dff91" + (isMajor ? "80" : "40") : "transparent", width: isBid ? `${pct * 100}%` : 0, minWidth: isBid ? 2 : 0, transition: "width 0.3s" }} />
                      </div>
                      {/* Center price */}
                      <div style={{ textAlign: "center", color: isMajor ? "#e2e8f0" : "#718096", fontWeight: isMajor ? 700 : 400, fontSize: 10 }}>
                        ${w.price.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      {/* Right bar (ask) */}
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ height: 12, borderRadius: 2, background: !isBid ? "#ff6b6b" + (isMajor ? "80" : "40") : "transparent", width: !isBid ? `${pct * 100}%` : 0, minWidth: !isBid ? 2 : 0, transition: "width 0.3s" }} />
                        {!isBid && <span style={{ fontSize: 9, color: "#ff6b6b80" }}>{w.size.toFixed(0)}</span>}
                      </div>
                    </div>
                  );
                });
              })()}
              {/* Mid price marker */}
              {refPrice && (
                <div style={{ textAlign: "center", padding: "6px 0", fontSize: 9, color: "#4a5568", letterSpacing: 1, borderTop: "1px dashed #2d3f52" }}>
                  REF ${refPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SECTION 5 — Wall Event Feed */}
      {absorptionData?.recent_events?.length > 0 && (
        <div style={sectionStyle}>
          <div style={headerStyle}>WALL EVENTS</div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            <div className="bm-event-hdr" style={{ display: "grid", gridTemplateColumns: "55px 40px 40px 1fr 50px 40px 35px 35px", padding: "6px 10px", borderBottom: "1px solid #1a2030", fontSize: 9, color: "#718096", letterSpacing: 2, position: "sticky", top: 0, background: "#0a0e14", zIndex: 1 }}>
              <span>TIME</span><span>TYPE</span><span>SIDE</span><span>PRICE</span><span>VOL</span>{!isMobile && <><span>DUR</span><span>TC</span><span>SC</span></>}
            </div>
            {absorptionData.recent_events.slice(0, 20).map((ev, i) => {
              const typeColors = { held: "#4dff91", swept: "#ff6b6b", pulled: "#f6ad55", absorbing: "#63b3ed", tested: "#a78bfa", appeared: "#718096" };
              const tc = typeColors[ev.type] || "#4a5568";
              const t = ev.ts ? new Date(ev.ts * 1000) : null;
              const timeStr = t ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "\u2014";
              return (
                <div className="bm-event-grid" key={i} style={{
                  display: "grid", gridTemplateColumns: "55px 40px 40px 1fr 50px 40px 35px 35px",
                  padding: "4px 10px", fontSize: 10, fontFamily: "'Space Mono', monospace",
                  borderBottom: "1px solid #060a0f",
                  borderLeft: `2px solid ${tc}`,
                  background: ev.type === "held" ? "#4dff9108" : ev.type === "swept" ? "#ff6b6b08" : "transparent",
                }}>
                  <span style={{ color: "#718096" }}>{timeStr}</span>
                  <span style={{ color: tc, fontWeight: 700, fontSize: 9, textTransform: "uppercase" }}>{ev.type}</span>
                  <span style={{ color: ev.side === "bid" ? "#4dff91" : "#ff6b6b", fontSize: 9, textTransform: "uppercase" }}>{ev.side || "\u2014"}</span>
                  <span style={{ color: "#a0aec0" }}>${ev.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "\u2014"}</span>
                  <span style={{ color: "#a0aec0" }}>{ev.volume_absorbed?.toFixed(0) || "\u2014"}</span>
                  {!isMobile && <span style={{ color: "#718096" }}>{ev.duration_s ? ev.duration_s.toFixed(0) + "s" : "\u2014"}</span>}
                  {!isMobile && <span style={{ color: "#a0aec0" }}>{ev.touch_count ?? "\u2014"}</span>}
                  {!isMobile && <span style={{ color: ev.score >= 50 ? "#4dff91" : ev.score >= 25 ? "#f6ad55" : "#718096" }}>{ev.score?.toFixed(0) || "\u2014"}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 6 — Deep Structural Walls (tiered snapshot) */}
      {(() => {
        const ws = wallSnapshot?.wall_snapshot;
        if (!ws) return null;
        const isStale = ws.stale;
        const formatPersistence = (s) => {
          if (s == null) return "\u2014";
          if (s < 60) return Math.round(s) + "s";
          if (s < 3600) return Math.round(s / 60) + "m";
          if (s < 86400) return Math.round(s / 3600) + "h";
          return Math.round(s / 86400) + "d";
        };
        const tiers = [
          { label: "NEAR (0\u20131%)", data: ws.near, opacity: 1 },
          { label: "MID (1\u20135%)", data: ws.mid, opacity: 0.85 },
          { label: "FAR (5\u201320%)", data: ws.far, opacity: 0.7 },
        ];
        const hasTiers = tiers.some(t => t.data && t.data.length > 0);
        if (!hasTiers) return null;
        return (
          <div style={{ ...sectionStyle, opacity: isStale ? 0.6 : 1 }}>
            <div style={{ ...headerStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>DEEP STRUCTURAL WALLS</span>
              <span style={{ fontSize: 9, color: isStale ? "#f6ad55" : "#4dff91" }}>
                {isStale ? "\u25cb Stale" : "\u25cf Live"}
              </span>
            </div>
            {tiers.map(tier => {
              if (!tier.data || tier.data.length === 0) return null;
              return (
                <div key={tier.label} style={{ opacity: tier.opacity, marginBottom: 2 }}>
                  <div style={{ padding: "4px 12px", fontSize: 9, color: "#718096", letterSpacing: 2, borderBottom: "1px solid #1a2030" }}>
                    {tier.label}
                  </div>
                  {tier.data.map((w, i) => (
                    <div key={i} style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "35px 50px 1fr 50px 35px" : "35px 60px 1fr 55px 40px 55px",
                      padding: "3px 12px",
                      fontSize: 10,
                      fontFamily: "'Space Mono', monospace",
                      borderBottom: "1px solid #060a0f",
                      background: tier.label.startsWith("FAR") ? "#0d111a" : "transparent",
                    }}>
                      <span style={{ color: w.side === "bid" ? "#4dff91" : "#ff6b6b", fontWeight: 700, fontSize: 9, textTransform: "uppercase" }}>{w.side}</span>
                      <span style={{ color: "#a0aec0" }}>{w.size?.toFixed?.(1) ?? w.size}</span>
                      <span style={{ color: "#cbd5e0" }}>${w.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ color: w.dist_pct < 0 || w.side === "bid" ? "#4dff91" : "#ff6b6b", fontSize: 9 }}>
                        {w.side === "bid" ? "\u2212" : "+"}{Math.abs(w.dist_pct)?.toFixed(2)}%
                      </span>
                      <span style={{ color: "#718096", fontSize: 9 }}>{formatPersistence(w.persistence_s)}</span>
                      {!isMobile && <span style={{ color: "#4a5568", fontSize: 9 }}>pk {w.size_peak?.toFixed?.(1) ?? w.size_peak}</span>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Summary bar */}
      {inst?.summary && (
        <div style={{ ...sectionStyle, padding: "10px 20px", fontSize: 11, color: "#a0aec0", fontFamily: "'Space Mono', monospace" }}>
          {inst.summary}
        </div>
      )}
    </div>
  );
}


// ORDER FLOW TAB — Bookmap Intel + OI Delta, Funding charts
// ---------------------------------------------------------------------------
const MAX_HISTORY = 360; // 30 min of 5s polls
const EXCHANGE_COLORS = { binance: "#f0b90b", bybit: "#4dff91", bybit_inverse: "#4da2ff" };
const OIChartTooltipStyle = { contentStyle: { background: "#0d1117", border: "1px solid #1a2030", borderRadius: 4, fontSize: 11, fontFamily: "'Space Mono', monospace" }, labelStyle: { color: "#718096", fontSize: 10 }, itemStyle: { padding: "1px 0" } };

// Build a flat history point from OI + funding data only.
function buildOIHistoryPoint(apiData, now) {
  const pt = { time: now };
  try {
    const oi = apiData.open_interest && typeof apiData.open_interest === "object" ? apiData.open_interest : {};
    let oiDelta5m = 0;
    for (const src of Object.keys(oi)) {
      const oiNode = oi[src] && typeof oi[src] === "object" ? oi[src] : {};
      const d5 = typeof oiNode.oi_delta_5m === "number" ? oiNode.oi_delta_5m : 0;
      pt["oi_delta5m_" + src] = d5;
      oiDelta5m += d5;
      if (typeof oiNode.funding_rate === "number") {
        pt["fr_" + src] = oiNode.funding_rate;
      }
    }
    pt.oiDelta = oiDelta5m;
  } catch (err) {
    console.error("[buildOIHistoryPoint] error:", err);
  }
  return pt;
}

function OrderFlowTab({ assetColor, API }) {
  const [ofAsset, setOfAsset] = useState("BTC");
  const [btcData, setBtcData] = useState(null);
  const [ethData, setEthData] = useState(null);
  const [bookmapData, setBookmapData] = useState(null);

  // Time-series history accumulated from 5s polls (keyed by asset) — OI + funding only
  const historyRef = useRef({ BTC: [], ETH: [] });
  const [historyTick, setHistoryTick] = useState(0);

  // Polling effect — fetches Bookmap intel (no auth) + OI data (auth)
  const inFlightRef = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    const poll = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const [bmResp, bResp, eResp] = await Promise.all([
          fetch(API + "/bookmap/intel", { signal: controller.signal }).catch(() => null),
          authFetch(API + "/orderflow/summary/BTC?minutes=15", { signal: controller.signal }),
          authFetch(API + "/orderflow/summary/ETH?minutes=15", { signal: controller.signal }),
        ]);
        if (bResp.status === 401 || eResp.status === 401) { handle401(); return; }
        const [bm, b, e] = await Promise.all([
          bmResp && bmResp.ok ? bmResp.json().catch(() => null) : null,
          bResp.ok ? bResp.json().catch(() => null) : null,
          eResp.ok ? eResp.json().catch(() => null) : null,
        ]);
        if (!controller.signal.aborted) {
          if (bm) setBookmapData(bm);
          if (b) setBtcData(b);
          if (e) setEthData(e);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[OrderFlowTab] poll error:", err);
      } finally {
        inFlightRef.current = false;
      }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { controller.abort(); clearInterval(t); };
  }, [API]);

  // History accumulation effect — OI + funding only
  useEffect(() => {
    if (!btcData && !ethData) return;
    const now = Date.now();
    const next = { ...historyRef.current };
    if (btcData) {
      const snap = buildOIHistoryPoint(btcData, now);
      const h = [...next.BTC, snap];
      next.BTC = h.length > MAX_HISTORY ? h.slice(-MAX_HISTORY) : h;
    }
    if (ethData) {
      const snap = buildOIHistoryPoint(ethData, now);
      const h = [...next.ETH, snap];
      next.ETH = h.length > MAX_HISTORY ? h.slice(-MAX_HISTORY) : h;
    }
    historyRef.current = next;
    setHistoryTick(t => t + 1);
  }, [btcData, ethData]);

  const data = ofAsset === "BTC" ? btcData : ethData;
  // eslint-disable-next-line no-unused-vars
  const _tick = historyTick; // trigger re-render on history update
  const history = historyRef.current[ofAsset] || [];

  const fmtOI = (n) => {
    if (!n || isNaN(n)) return "$0";
    if (Math.abs(n) >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
    if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
    if (Math.abs(n) >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
    return "$" + n.toFixed(0);
  };
  const fmtDelta = (n) => {
    if (!n || isNaN(n)) return "$0";
    const sign = n > 0 ? "+" : "";
    if (Math.abs(n) >= 1e9) return sign + "$" + (n / 1e9).toFixed(2) + "B";
    if (Math.abs(n) >= 1e6) return sign + "$" + (n / 1e6).toFixed(2) + "M";
    if (Math.abs(n) >= 1e3) return sign + "$" + (n / 1e3).toFixed(1) + "K";
    return sign + "$" + n.toFixed(0);
  };
  const deltaColor = (n) => !n ? "#4a5568" : n > 0 ? "#4dff91" : "#ff6b6b";
  const fmtFunding = (r) => {
    if (r == null) return "\u2014";
    return (r * 100).toFixed(4) + "%";
  };
  const fmtChartTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const fmtAxisVal = (v) => { if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(1) + "M"; if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(0) + "K"; return v.toFixed(0); };

  // OI / funding sources from selected asset
  const oiSources = data?.open_interest || {};
  const oiKeys = Object.keys(oiSources);

  const fundingValues = oiKeys.map(k => oiSources[k]?.funding_rate).filter(v => typeof v === "number" && Math.abs(v) > 1e-6);
  const hasFundingDivergence = fundingValues.length >= 2 && fundingValues.some(v => v > 0) && fundingValues.some(v => v < 0);
  const sharpOI = (src) => {
    if (!src?.oi_delta_5m) return false;
    const prevOI = (src.current_oi || 0) - src.oi_delta_5m;
    if (prevOI <= 0) return false;
    return Math.abs(src.oi_delta_5m / prevOI) > 0.005;
  };

  // Parse bookmap instruments — handle both {instruments: {...}} and raw dict formats
  const bookmapInstruments = useMemo(() => {
    if (!bookmapData) return [];
    const src = bookmapData.instruments || bookmapData;
    if (typeof src !== "object" || Array.isArray(src)) return [];
    return Object.entries(src).map(([key, val]) => ({ key, ...val }));
  }, [bookmapData]);

  const sectionStyle = { background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: 16, marginBottom: 16 };
  const headerStyle = { fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 };
  const subHeaderStyle = { fontSize: 9, color: "#4a5568", letterSpacing: 1, marginBottom: 8, marginTop: 16, display: "flex", alignItems: "center", gap: 6 };

  // Chart data keys from latest history point
  const last = history.length > 0 ? history[history.length - 1] : {};
  const chartOiKeys = Object.keys(last).filter(k => k.startsWith("oi_delta5m_"));
  const chartFundingKeys = Object.keys(last).filter(k => k.startsWith("fr_"));
  const hasChartData = history.length >= 3;

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      {/* Asset toggle (for OI data) */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2 }}>OI ASSET</div>
        {["BTC", "ETH"].map(a => (
          <button key={a} className="btn" onClick={() => setOfAsset(a)} style={{
            background: ofAsset === a ? (a === "BTC" ? "#f7931a" : "#627eea") + "20" : "transparent",
            border: `1px solid ${ofAsset === a ? (a === "BTC" ? "#f7931a" : "#627eea") : "#2d3f52"}`,
            color: ofAsset === a ? (a === "BTC" ? "#f7931a" : "#627eea") : "#a0aec0",
            padding: "5px 16px", borderRadius: 4, fontFamily: "monospace", fontSize: 12, fontWeight: 700,
          }}>{a}</button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4dff91", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, color: "#4dff91", letterSpacing: 1 }}>LIVE 5s</span>
        </div>
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          BOOKMAP INTEL — data from Bookmap API addon
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={{ ...sectionStyle, padding: 20 }}>
        <div style={headerStyle}>
          BOOKMAP INTEL
          <span style={{ background: "#4da2ff20", color: "#4da2ff", padding: "2px 8px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 1, border: "1px solid #4da2ff40" }}>LIVE</span>
        </div>

        {bookmapInstruments.length === 0 ? (
          <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 40 }}>Waiting for Bookmap data...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(280px, 1fr))`, gap: 12 }}>
            {bookmapInstruments.map(inst => {
              const cvdVal = typeof inst.cvd === "number" ? inst.cvd : null;
              const cvdColor = cvdVal == null ? "#4a5568" : cvdVal > 0 ? "#4dff91" : cvdVal < 0 ? "#ff6b6b" : "#718096";
              const updated = inst.server_received ? new Date(inst.server_received * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
              return (
                <div key={inst.key} style={{ background: "#080c12", borderRadius: 6, padding: 14, border: "1px solid #1a2030" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 700, letterSpacing: 1, fontFamily: "'Space Mono', monospace" }}>{inst.alias || inst.key}</span>
                    {updated && <span style={{ fontSize: 9, color: "#4a5568" }}>{updated}</span>}
                  </div>
                  {cvdVal != null && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 9, color: "#4a5568", letterSpacing: 1 }}>CVD </span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: cvdColor, fontFamily: "'Space Mono', monospace" }}>{cvdVal > 0 ? "+" : ""}{typeof cvdVal === "number" ? cvdVal.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "\u2014"}</span>
                    </div>
                  )}
                  {inst.walls && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 3 }}>WALLS</div>
                      <div style={{ fontSize: 11, color: "#a0aec0", fontFamily: "'Space Mono', monospace", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{inst.walls}</div>
                    </div>
                  )}
                  {inst.summary && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 3 }}>SUMMARY</div>
                      <div style={{ fontSize: 11, color: "#a0aec0", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{inst.summary}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          OI TRACKER — per-exchange open interest cards
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={sectionStyle}>
        <div style={headerStyle}>OPEN INTEREST TRACKER</div>
        {!data ? (
          <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 20 }}>Loading OI data...</div>
        ) : oiKeys.length === 0 ? (
          <div style={{ color: "#4a5568", fontSize: 12, padding: 12 }}>No OI data yet</div>
        ) : (
          oiKeys.map(source => {
            const oi = oiSources[source];
            const label = source.replace("_", " ").toUpperCase();
            const isSharp = sharpOI(oi);
            return (
              <div key={source} style={{ background: "#080c12", borderRadius: 6, padding: 12, marginBottom: 8, border: isSharp ? "1px solid #ff990060" : "1px solid #1a2030" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>{label}</span>
                    {isSharp && (
                      <span style={{ background: "#ff990020", color: "#ff9900", padding: "1px 6px", borderRadius: 3, fontSize: 8, fontWeight: 700, letterSpacing: 1 }}>SHARP MOVE</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 9, color: "#4a5568" }}>FR</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: oi?.funding_rate > 0 ? "#4dff91" : oi?.funding_rate < 0 ? "#ff6b6b" : "#718096", fontFamily: "'Space Mono', monospace" }}>{fmtFunding(oi?.funding_rate)}</span>
                  </div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace", marginBottom: 6 }}>{fmtOI(oi?.current_oi)}</div>
                <div style={{ display: "flex", gap: 14 }}>
                  {[["1m", "oi_delta_1m"], ["5m", "oi_delta_5m"], ["15m", "oi_delta_15m"]].map(([lbl, key]) => (
                    <div key={key}>
                      <span style={{ fontSize: 9, color: "#4a5568" }}>{lbl} </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(oi?.[key]), fontFamily: "'Space Mono', monospace" }}>{fmtDelta(oi?.[key])}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          OI DELTA + FUNDING RATE CHARTS
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={{ ...sectionStyle, padding: 20 }}>
        <div style={headerStyle}>OI & FUNDING CHARTS</div>

        {!hasChartData ? (
          <div style={{ color: "#4a5568", fontSize: 12, textAlign: "center", padding: 40 }}>Accumulating data... ({history.length} samples, need ~15s)</div>
        ) : (
          <>
            {/* â"€â"€ OI Delta (AreaChart) â"€â"€ */}
            <div style={subHeaderStyle}>
              OI DELTA {"\u0394"}5m
              <span style={{ color: "#4a556880" }}>|</span>
              <span style={{ fontWeight: 400, letterSpacing: 0 }}>Rising OI + rising price = new longs | Rising OI + falling price = new shorts</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={history} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
                <XAxis dataKey="time" tickFormatter={fmtChartTime} stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} minTickGap={60} />
                <YAxis stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={fmtAxisVal} />
                <Tooltip contentStyle={OIChartTooltipStyle.contentStyle} labelStyle={OIChartTooltipStyle.labelStyle} labelFormatter={fmtChartTime} formatter={(v, name) => [fmtDelta(v), name]} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} />
                <ReferenceLine y={0} stroke="#4a5568" strokeDasharray="3 3" />
                {chartOiKeys.map(k => {
                  const src = k.replace("oi_delta5m_", "");
                  const color = EXCHANGE_COLORS[src] || "#a78bfa";
                  return <Area key={k} type="monotone" dataKey={k} name={src.replace("_", " ").toUpperCase() + " OI \u03945m"} stroke={color} fill={color} fillOpacity={0.15} strokeWidth={1.5} dot={false} isAnimationActive={false} />;
                })}
              </AreaChart>
            </ResponsiveContainer>

            {/* â"€â"€ Funding Rate (LineChart) â"€â"€ */}
            <div style={subHeaderStyle}>
              FUNDING RATE
              {hasFundingDivergence && (
                <span style={{ background: "#ff990020", color: "#ff9900", padding: "1px 6px", borderRadius: 3, fontSize: 8, fontWeight: 700, letterSpacing: 1, border: "1px solid #ff990040" }}>DIVERGENCE</span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={history} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
                <XAxis dataKey="time" tickFormatter={fmtChartTime} stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} minTickGap={60} />
                <YAxis stroke="#4a5568" tick={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} tickFormatter={v => typeof v === "number" ? (v * 100).toFixed(4) + "%" : "\u2014"} domain={["auto", "auto"]} />
                <Tooltip contentStyle={OIChartTooltipStyle.contentStyle} labelStyle={OIChartTooltipStyle.labelStyle} labelFormatter={fmtChartTime} formatter={(v, name) => [typeof v === "number" ? (v * 100).toFixed(4) + "%" : "\u2014", name]} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'Space Mono', monospace" }} />
                <ReferenceLine y={0} stroke="#4a556880" strokeDasharray="3 3" label={{ value: "neutral", fill: "#4a5568", fontSize: 9, position: "left" }} />
                {chartFundingKeys.map(k => {
                  const src = k.replace("fr_", "");
                  const color = EXCHANGE_COLORS[src] || "#a78bfa";
                  return <Line key={k} type="stepAfter" dataKey={k} name={src.replace("_", " ").toUpperCase() + " FR"} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />;
                })}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          FUNDING RATE COMPARISON CARDS
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div style={sectionStyle}>
        <div style={headerStyle}>
          FUNDING RATE COMPARISON
          {hasFundingDivergence && (
            <span style={{ background: "#ff990020", color: "#ff9900", padding: "2px 8px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 1, border: "1px solid #ff990040" }}>DIVERGENCE</span>
          )}
        </div>
        {oiKeys.length === 0 && <div style={{ color: "#4a5568", fontSize: 12, padding: 12 }}>No funding data yet</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {oiKeys.map(source => {
            const oi = oiSources[source];
            const label = source.replace("_", " ").toUpperCase();
            const fr = oi?.funding_rate;
            const frColor = fr > 0 ? "#4dff91" : fr < 0 ? "#ff6b6b" : "#718096";
            return (
              <div key={source} style={{ background: "#080c12", borderRadius: 6, padding: 14, border: "1px solid #1a2030", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 8 }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: frColor, fontFamily: "'Space Mono', monospace" }}>{fmtFunding(fr)}</div>
                <div style={{ fontSize: 10, color: "#4a5568", marginTop: 6 }}>{fr > 0 ? "Longs pay shorts" : fr < 0 ? "Shorts pay longs" : "Neutral"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function AlertBell({ alerts, onClick }) {
  const unread = alerts.filter(a => a.severity === "CRITICAL" || a.severity === "IMPORTANT").length;
  return (
    <button onClick={onClick} style={{ background: "transparent", border: "none", cursor: "pointer", position: "relative", padding: "4px 8px", fontSize: 18, color: unread > 0 ? "#d29922" : "#484f58" }} title={`${alerts.length} alerts`}>
      {"\uD83D\uDD14"}
      {unread > 0 && (
        <span style={{ position: "absolute", top: 0, right: 2, background: "#f85149", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 8, padding: "1px 4px", minWidth: 14, textAlign: "center" }}>{unread > 99 ? "99+" : unread}</span>
      )}
    </button>
  );
}

function AlertDrawer({ alerts, open, onClose }) {
  const mob = useIsMobile();
  if (!open) return null;
  const sc = { CRITICAL: "#f85149", IMPORTANT: "#d29922", INFO: "#484f58" };
  const si = { CRITICAL: "\uD83D\uDD34", IMPORTANT: "\uD83D\uDFE1", INFO: "\u2139\uFE0F" };
  return (
    <div style={{ position: "fixed", top: 0, right: 0, width: mob ? "100%" : 380, height: "100vh", background: "#0d1117", borderLeft: mob ? "none" : "1px solid #1a2030", zIndex: 1000, display: "flex", flexDirection: "column", boxShadow: "-4px 0 20px rgba(0,0,0,0.5)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #1a2030" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#c9d1d9" }}>Alerts</span>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#718096", cursor: "pointer", fontSize: 16 }}>{"\u2715"}</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
        {alerts.length === 0 && <div style={{ color: "#484f58", fontSize: 11, textAlign: "center", marginTop: 40 }}>No alerts yet</div>}
        {alerts.map((a, i) => (
          <div key={a.id || i} style={{ padding: "8px 10px", marginBottom: 4, borderRadius: 4, background: "#161b22", borderLeft: `3px solid ${sc[a.severity] || "#484f58"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: sc[a.severity] || "#718096" }}>{si[a.severity] || ""} {a.category}</span>
              <span style={{ fontSize: 9, color: "#484f58" }}>{a.ts ? new Date(a.ts * 1000).toLocaleTimeString() : ""}</span>
            </div>
            <div style={{ fontSize: 11, color: "#c9d1d9", fontWeight: 500, marginBottom: 2 }}>{a.title}</div>
            <div style={{ fontSize: 10, color: "#8b949e" }}>{a.message}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertToastLayer({ toasts, onDismiss }) {
  if (!toasts || toasts.length === 0) return null;
  const sc = { CRITICAL: "#f85149", IMPORTANT: "#d29922", INFO: "#484f58" };
  return (
    <div style={{ position: "fixed", top: 60, right: 16, zIndex: 1001, display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
      {toasts.map((a, i) => (
        <div key={a.id || i} style={{ padding: "10px 14px", borderRadius: 6, background: "#161b22", border: `1px solid ${sc[a.severity] || "#1a2030"}`, borderLeft: `3px solid ${sc[a.severity] || "#484f58"}`, boxShadow: "0 4px 12px rgba(0,0,0,0.4)", animation: "fadeIn 0.3s ease", cursor: "pointer" }} onClick={() => onDismiss && onDismiss(a.id)}>
          <div style={{ fontSize: 10, fontWeight: 600, color: sc[a.severity] || "#718096", marginBottom: 2 }}>{a.severity} {"\u2014"} {a.asset}</div>
          <div style={{ fontSize: 11, color: "#c9d1d9" }}>{a.title}</div>
          <div style={{ fontSize: 9, color: "#8b949e" }}>{a.message?.slice(0, 100)}</div>
        </div>
      ))}
    </div>
  );
}

const MM_ROSTER_SECTIONS = [
  { id: "high", label: "HIGH", states: ["ACTIVE_MM_T1", "ACTIVE_MM_T2"] },
  { id: "mid", label: "MID", states: ["PROBATION_MM"] },
  { id: "decay", label: "DECAY", states: ["WATCHLIST_DECAY"] },
];

const MM_STATE_LABELS = {
  ACTIVE_MM_T1: "T1",
  ACTIVE_MM_T2: "T2",
  PROBATION_MM: "PROBATION",
  WATCHLIST_DECAY: "DECAY",
};

function shortAddress(addr) {
  if (!addr) return "\u2014";
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function fmtDecimal(n, digits) {
  if (n == null || isNaN(Number(n))) return "\u2014";
  return Number(n).toFixed(digits);
}

function fmtLoadedAt(d) {
  return d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "\u2014";
}

function MMRosterSortHeader({ label, sortKey, sort, onSort, align = "left" }) {
  const active = sort.key === sortKey;
  return (
    <button
      onClick={() => onSort(sortKey)}
      style={{
        background: "transparent",
        border: "none",
        color: active ? "#e2e8f0" : "#718096",
        cursor: "pointer",
        fontFamily: "monospace",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.5,
        textAlign: align,
        padding: 0,
      }}
      title={`Sort by ${label}`}
    >
      {label}{active ? (sort.dir === "asc" ? " \u2191" : " \u2193") : ""}
    </button>
  );
}

function MMRosterSection({ label, rows, defaultOpen = true }) {
  const mob = useIsMobile();
  const [open, setOpen] = useState(defaultOpen);
  const [sort, setSort] = useState({ key: "delta_neutrality", dir: "asc" });
  const lowDneut = rows.filter(r => r.delta_neutrality != null && Number(r.delta_neutrality) < 0.10).length;

  const onSort = useCallback((key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "address" || key === "state" || key === "delta_neutrality" ? "asc" : "desc" });
  }, []);

  const sortedRows = useMemo(() => {
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" || typeof bv === "number") {
        return (Number(av) - Number(bv)) * direction;
      }
      return String(av).localeCompare(String(bv)) * direction;
    });
  }, [rows, sort]);

  return (
    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", marginBottom: 14 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width: "100%", background: "#080c12", border: "none", borderBottom: open ? "1px solid #1a2030" : "none", padding: mob ? "10px 12px" : "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "monospace" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 800, letterSpacing: 2 }}>{open ? "\u25be" : "\u25b8"} {label}</span>
          <span style={{ color: "#718096", fontSize: 11 }}>{rows.length.toLocaleString()} wallets</span>
          {lowDneut > 0 && <span style={{ color: "#ff6b6b", background: "#ff6b6b18", border: "1px solid #ff6b6b44", borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 700 }}>! {lowDneut} LOW DNEUT</span>}
        </div>
      </button>
      {open && (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ display: "grid", gridTemplateColumns: "140px 88px 90px 120px 100px 80px 120px", minWidth: 780, padding: "10px 14px", borderBottom: "1px solid #1a2030", alignItems: "center" }}>
            <MMRosterSortHeader label="ADDRESS" sortKey="address" sort={sort} onSort={onSort} />
            <MMRosterSortHeader label="STATE" sortKey="state" sort={sort} onSort={onSort} />
            <MMRosterSortHeader label="EFFECTIVE" sortKey="effective" sort={sort} onSort={onSort} align="right" />
            <MMRosterSortHeader label="DNEUT" sortKey="delta_neutrality" sort={sort} onSort={onSort} align="right" />
            <MMRosterSortHeader label="CONF" sortKey="confidence" sort={sort} onSort={onSort} align="right" />
            <MMRosterSortHeader label="TWP" sortKey="twp" sort={sort} onSort={onSort} align="right" />
            <MMRosterSortHeader label="BTC POS" sortKey="btc_notional" sort={sort} onSort={onSort} align="right" />
          </div>
          {sortedRows.length === 0 && (
            <div style={{ minWidth: 780, padding: "18px 14px", color: "#4a5568", fontSize: 11 }}>No wallets</div>
          )}
          {sortedRows.map((row, i) => {
            const dneut = row.delta_neutrality == null ? null : Number(row.delta_neutrality);
            const dneutFlag = dneut != null && dneut < 0.10;
            const dneutWarn = dneut != null && dneut >= 0.10 && dneut < 0.20;
            const btcSide = row.btc_side || "";
            const btcColor = btcSide === "long" ? "#4dff91" : btcSide === "short" ? "#ff6b6b" : "#718096";
            return (
              <div key={`${row.state}:${row.address}`} style={{ display: "grid", gridTemplateColumns: "140px 88px 90px 120px 100px 80px 120px", minWidth: 780, padding: "9px 14px", borderBottom: "1px solid #060a0f", alignItems: "center", background: i % 2 === 0 ? "#080c12" : "transparent", fontSize: 12 }}>
                <WalletLink address={row.address} display={shortAddress(row.address)} />
                <span style={{ color: row.state === "ACTIVE_MM_T1" ? "#4dff91" : row.state === "ACTIVE_MM_T2" ? "#63b3ed" : row.state === "PROBATION_MM" ? "#f6ad55" : "#a0aec0", fontWeight: 700 }}>{MM_STATE_LABELS[row.state] || row.state}</span>
                <span style={{ color: "#e2e8f0", textAlign: "right", fontFamily: "'Space Mono', monospace" }}>{fmtDecimal(row.effective, 4)}</span>
                <span style={{ textAlign: "right" }}>
                  <span style={{ display: "inline-block", minWidth: 70, padding: "3px 8px", borderRadius: 4, background: dneutFlag ? "#ff6b6b22" : dneutWarn ? "#f6ad5522" : "transparent", border: dneutFlag ? "1px solid #ff6b6b66" : dneutWarn ? "1px solid #f6ad5566" : "1px solid transparent", color: dneutFlag ? "#ff6b6b" : dneutWarn ? "#f6ad55" : "#a0aec0", fontWeight: dneutFlag || dneutWarn ? 800 : 500, fontFamily: "'Space Mono', monospace" }}>
                    {dneutFlag ? "! " : ""}{fmtDecimal(row.delta_neutrality, 3)}
                  </span>
                </span>
                <span style={{ color: "#a0aec0", textAlign: "right", fontFamily: "'Space Mono', monospace" }}>{fmtDecimal(row.confidence, 4)}</span>
                <span style={{ color: "#718096", textAlign: "right", fontFamily: "'Space Mono', monospace" }}>{fmtDecimal(row.twp, 2)}</span>
                <span style={{ color: btcColor, textAlign: "right", fontFamily: "'Space Mono', monospace", textTransform: "uppercase" }}>
                  {row.btc_notional != null ? `${btcSide || "pos"} ${fmt(row.btc_notional)}` : "\u2014"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MMRosterTab({ API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const loadRoster = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const res = await authFetch(API + "/api/mm_roster");
      if (res.status === 401) { handle401(); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      setData(payload);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const rowsBySection = useMemo(() => {
    const tiers = data?.tiers || {};
    return MM_ROSTER_SECTIONS.map(section => ({
      ...section,
      rows: section.states.flatMap(state => tiers[state] || []),
    }));
  }, [data]);

  const allRows = rowsBySection.flatMap(section => section.rows);
  const total = allRows.length;
  const lowDneut = allRows.filter(r => r.delta_neutrality != null && Number(r.delta_neutrality) < 0.10).length;

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>MM ROSTER</div>
          <div style={{ fontSize: 22, color: "#e2e8f0", fontWeight: 800, fontFamily: "'Space Mono', monospace" }}>{total.toLocaleString()} wallets</div>
        </div>
        <button
          onClick={loadRoster}
          disabled={refreshing}
          className="btn"
          style={{ background: "#1a2030", color: "#e2e8f0", border: "1px solid #2d3f52", padding: "8px 14px", borderRadius: 4, cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.65 : 1, fontFamily: "monospace", fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
          title="Refresh MM roster"
        >
          {"\u21bb"} {refreshing ? "REFRESHING" : "REFRESH"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 16 }}>
        <StatCard label="CYCLE" value={data?.as_of ? new Date(data.as_of).toLocaleString() : "\u2014"} />
        <StatCard label="LOADED" value={fmtLoadedAt(loadedAt)} />
        <StatCard label="LOW DNEUT" value={lowDneut.toLocaleString()} accent={lowDneut > 0 ? "#ff6b6b" : "#4dff91"} />
        <StatCard label="SCORES FILE" value={data?.scores_file || "\u2014"} />
      </div>

      {loading && <div style={{ color: "#718096", fontSize: 12, padding: 24, textAlign: "center" }}>Loading roster...</div>}
      {error && <div style={{ color: "#ff6b6b", fontSize: 12, padding: 14, background: "#ff6b6b12", border: "1px solid #ff6b6b33", borderRadius: 6, marginBottom: 14 }}>{String(error.message || error)}</div>}
      {!loading && rowsBySection.map(section => (
        <MMRosterSection key={section.id} label={section.label} rows={section.rows} defaultOpen={section.id !== "decay"} />
      ))}
    </div>
  );
}

function HLDashboardInner() {
  const mob = useIsMobile();
  const [asset, setAsset] = useState("BTC");
  const [tab, setTab] = useState("overview");
  const [liqModal, setLiqModal] = useState(null); // {asset, target_price, pct_change, positions}
  const [scenario, setScenario] = useState(null);
  const [assets, setAssets] = useState(DEFAULT_ASSETS);
  const [alerts, setAlerts] = useState([]);
  const [alertToasts, setAlertToasts] = useState([]);
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const alertLastTs = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const qs = new URLSearchParams({ limit: "50" });
        if (alertLastTs.current) qs.set("since_ts", String(alertLastTs.current));
        const res = await authFetch(API + "/alerts/recent?" + qs.toString());
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.alerts && data.alerts.length) {
          const incoming = data.alerts;
          setAlerts(prev => {
            const existing = new Set(prev.map(a => a.id));
            const fresh = incoming.filter(a => !existing.has(a.id));
            if (fresh.length > 0) {
              setAlertToasts(tp => [...fresh.slice(0, 3), ...tp].slice(0, 5));
            }
            return [...incoming, ...prev.filter(a => !incoming.find(n => n.id === a.id))].slice(0, 100);
          });
          alertLastTs.current = Math.max(...incoming.map(a => a.ts));
        }
      } catch (e) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (alertToasts.length === 0) return;
    const timer = setTimeout(() => setAlertToasts(prev => prev.slice(0, -1)), 6000);
    return () => clearTimeout(timer);
  }, [alertToasts]);

  const assetColor = ASSET_COLORS[asset] || "#4da2ff";

  // Fetch overview to get dynamic asset list
  const { data: overview } = useApi(API + "/overview", 30000);
  useEffect(() => {
    if (overview && overview.coins && overview.coins.length > 0) {
      const top = overview.coins.slice(0, 15).map(c => c.coin);
      setAssets(top);
    }
  }, [overview]);

  // Fetch data for selected asset
  const { data: scenarioData, loading: scenarioLoading } = useApi(API + "/scenario/" + asset + "/all", 30000);
  const { data: whaleData } = useApi(API + "/whales/" + asset + "?limit=30", 30000);
  const { data: heatmapData } = useApi(API + "/heatmap/" + asset, 30000);
  const { data: fundingData } = useApi(API + "/funding/" + asset, 30000);
  const { data: posData } = useApi(API + "/positions/" + asset + "?limit=5", 30000);
  const { data: exposureData } = useApi(API + "/exposure/" + asset, 30000);
  const { data: imbalanceData } = useApi(API + "/imbalance/" + asset, 30000);
  const { data: imbalanceAnalysis } = useApi(API + "/analysis/" + asset + "/imbalance", 30000);
  const { data: msData } = useApi(tab === "overview" ? API + "/market_structure/" + asset : null, 30000);
  const { data: ms4hData } = useApi(tab === "overview" ? API + "/market_structure_4h/" + asset : null, 60000);
  const { data: aggOiData } = useApi(tab === "overview" ? API + "/aggregated/oi/" + asset : null, 10000);
  const { data: aggLsData } = useApi(tab === "overview" ? API + "/aggregated/ls_ratio/" + asset : null, 15000);
  const { data: aggLiqData } = useApi(tab === "overview" ? API + "/aggregated/liquidations/" + asset : null, 5000);
  const { data: bookmapTriggerData } = useApi(tab === "overview" ? API + "/bookmap/trigger/" + asset : null, 5000);
  const { data: modelAData } = useApi(tab === "overview" ? API + "/model_a/" + asset : null, 15000);
  const { data: mmBiasData } = useApi(tab === "overview" ? API + "/mm/bias/" + asset : null, 60000);
  const { data: whaleBiasData } = useApi(tab === "overview" ? API + "/mm/whale_bias/" + asset : null, 60000);

  const currentPrice = scenarioData?.current_price || 0;
  const targetPrice = scenario ? currentPrice * (1 + scenario.pct) : null;

  // Find scenario result from pre-computed scenarios
  const scenarioResult = useMemo(() => {
    if (!scenario || !scenarioData?.scenarios) return null;
    const pctStr = (scenario.pct > 0 ? "+" : "") + Math.round(scenario.pct * 100) + "%";
    return scenarioData.scenarios[pctStr] || null;
  }, [scenario, scenarioData]);

  // Overview stats for selected asset
  const assetOverview = useMemo(() => {
    if (!overview?.coins) return null;
    return overview.coins.find(c => c.coin === asset);
  }, [overview, asset]);

  const tabs = [
    // Trade
    { id: "overview", label: "OVERVIEW" },
    { id: "proximity", label: "PROXIMITY" },
    { id: "bookmap", label: "BOOKMAP", hidden: true },
    { id: "orderflow", label: "ORDER FLOW" },
    { id: "_sep1" },
    // Research
    { id: "scenario", label: "SCENARIOS" },
    { id: "heatmap", label: "HEATMAP", hidden: true },
    { id: "whales", label: "WHALES" },
    { id: "mmroster", label: "MM ROSTER" },
    { id: "cascade", label: "CASCADE" },
    { id: "_sep2" },
    // Ops
    { id: "engine", label: "ENGINE" },
    { id: "paper", label: "PAPER TRADER" },
    { id: "markets", label: "ALL MARKETS" },
  ];

  return (
    <div style={{ background: "#060a0f", minHeight: "100vh", width: "100%", overflowX: "hidden", color: "#e2e8f0", fontFamily: "monospace" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { background: #060a0f; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0d1117; }
        ::-webkit-scrollbar-thumb { background: #1e2530; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        .btn { cursor: pointer; transition: opacity 0.15s; }
        .btn:hover { opacity: 0.85; }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: "1px solid #1a2030", padding: mob ? "10px 12px" : "14px clamp(12px, 3vw, 28px)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#080c12", flexWrap: "wrap", gap: mob ? 8 : 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: mob ? 10 : 20, flexWrap: "wrap", minWidth: 0 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: mob ? 16 : 20, fontWeight: 800, letterSpacing: -0.5, flexShrink: 0 }}>
            <span style={{ color: assetColor }}>HL</span><span style={{ color: "#e2e8f0" }}>RISK</span>
          </div>
          {!mob && <div style={{ width: 1, height: 24, background: "#1a2030" }} />}
          <div style={{ display: "flex", gap: 4, flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: mob ? "calc(100vw - 120px)" : undefined }}>
            {assets.map((a) => (
              <button key={a} className="btn" onClick={() => { setAsset(a); setScenario(null); }} style={{
                background: asset === a ? (ASSET_COLORS[a] || "#4da2ff") + "20" : "transparent",
                border: `1px solid ${asset === a ? (ASSET_COLORS[a] || "#4da2ff") : "#2d3f52"}`,
                color: asset === a ? (ASSET_COLORS[a] || "#4da2ff") : "#a0aec0",
                padding: "3px 10px", borderRadius: 4, fontFamily: "monospace", fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>{a}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: mob ? 10 : 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 70 }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>PRICE</div>
            <div style={{ fontSize: mob ? 15 : 18, fontWeight: 700, color: assetColor, fontFamily: "'Space Mono', monospace" }}>
              ${currentPrice ? currentPrice.toLocaleString(undefined, { maximumFractionDigits: currentPrice > 100 ? 0 : 2 }) : "..."}
            </div>
          </div>
          {!mob && <div style={{ minWidth: 60 }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>POSITIONS</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
              {assetOverview ? (assetOverview.long_count + assetOverview.short_count).toLocaleString() : "..."}
            </div>
          </div>}
          {!mob && <div style={{ minWidth: 80 }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>LONG / SHORT</div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>
              <span style={{ color: "#4dff91" }}>{assetOverview?.long_count?.toLocaleString() || "..."}</span>
              <span style={{ color: "#718096" }}> / </span>
              <span style={{ color: "#ff6b6b" }}>{assetOverview?.short_count?.toLocaleString() || "..."}</span>
            </div>
          </div>}
          {!mob && <div style={{ minWidth: 60 }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>TOTAL OI</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
              {assetOverview ? fmt(assetOverview.total_notional) : "..."}
            </div>
          </div>}
          {!mob && <div style={{ minWidth: 60 }}>
            <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>FUNDING</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: fundingData?.funding_rate > 0 ? "#4dff91" : "#ff6b6b", fontFamily: "'Space Mono', monospace" }}>
              {fundingData ? (fundingData.funding_rate * 100).toFixed(4) + "%" : "..."}
            </div>
          </div>}
          <AlertBell alerts={alerts} onClick={() => setAlertDrawerOpen(!alertDrawerOpen)} />
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#4dff91", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 11, color: "#4dff91", letterSpacing: 1 }}>LIVE</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid #1a2030", padding: mob ? "0 8px" : "0 clamp(12px, 3vw, 28px)", display: "flex", gap: 0, background: "#080c12", overflowX: "auto", whiteSpace: "nowrap", WebkitOverflowScrolling: "touch" }}>
        {tabs.filter((t) => !t.hidden).map((t) => t.id.startsWith("_sep") ? (
          mob ? null : <div key={t.id} style={{ width: 1, height: 16, background: "#30363d", margin: "auto 4px", flexShrink: 0 }} />
        ) : (
          <button key={t.id} className="btn" onClick={() => setTab(t.id)} style={{
            background: "transparent", border: "none",
            borderBottom: tab === t.id ? `2px solid ${assetColor}` : "2px solid transparent",
            color: tab === t.id ? "#e2e8f0" : "#8a9bb0",
            padding: mob ? "10px 10px" : "12px 18px", fontFamily: "monospace", fontSize: mob ? 10 : 11, letterSpacing: mob ? 1 : 2, flexShrink: 0,
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: mob ? "12px 8px" : "16px clamp(12px, 3vw, 28px)", maxWidth: "100%", overflowX: "hidden" }}>

        {/* ═══ OVERVIEW TAB V2 — 6-panel grid ═══ */}
        {tab === "overview" && (
          <OverviewTabV2 ms={msData} ms4h={ms4hData} imb={imbalanceAnalysis} scenario={scenarioData} aggOi={aggOiData} aggLs={aggLsData} aggLiq={aggLiqData} bt={bookmapTriggerData} modelA={modelAData} mmBias={mmBiasData} whaleBias={whaleBiasData} currentPrice={currentPrice} asset={asset} />
        )}

        {/* ═══ PROXIMITY TAB — primary signal full view ═══ */}
        {tab === "proximity" && <TabErrorBoundary><ProximityTab asset={asset} /></TabErrorBoundary>}

        {/* OLD OVERVIEW — commented out for V2 redesign */}
        {false && tab === "overview" && assetOverview && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeIn 0.3s ease" }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <StatCard label="Total Positions" value={(assetOverview.long_count + assetOverview.short_count).toLocaleString()} sub={`${assetOverview.long_count} longs · ${assetOverview.short_count} shorts`} accent="#e2e8f0" />
              <StatCard label="Total Notional" value={fmt(assetOverview.total_notional)} sub={`Long ${fmt(assetOverview.long_notional)} / Short ${fmt(assetOverview.short_notional)}`} accent={assetColor} />
              <StatCard label="Funding Rate" value={fundingData ? (fundingData.funding_rate * 100).toFixed(4) + "%" : "..."} sub={fundingData?.funding_rate > 0 ? "Longs pay shorts" : "Shorts pay longs"} accent={fundingData?.funding_rate > 0 ? "#4dff91" : "#ff6b6b"} />
              <StatCard label="Mark Price" value={"$" + (currentPrice ? currentPrice.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "...")} sub={fundingData ? "Oracle: $" + fundingData.oracle_price?.toLocaleString(undefined, { maximumFractionDigits: 2 }) : ""} accent={assetColor} />
            </div>
            <OIBar longNtl={assetOverview.long_notional} shortNtl={assetOverview.short_notional} accent={assetColor} />

            {["BTC", "ETH"].includes(asset) && <OverviewFlowStrip asset={asset} />}

            {/* Squeeze Analysis — composite gauge + 4 sub-metrics */}
            {imbalanceAnalysis && (() => {
              const d = imbalanceAnalysis;
              const score = d.composite_score;
              // Color based on direction: green for short squeeze, red for long squeeze, blue for balanced
              const dirColor = score <= -10 ? "#ff6b6b" : score >= 10 ? "#4dff91" : "#4da2ff";
              // Gauge position: map -100..+100 to 0..100%
              const gaugePct = ((score + 100) / 200) * 100;
              // Sub-score mini bar helper
              const miniBar = (val) => {
                const pct = ((val + 100) / 200) * 100;
                const c = val <= -10 ? "#ff6b6b" : val >= 10 ? "#4dff91" : "#4da2ff";
                return (
                  <div style={{ position: "relative", height: 6, borderRadius: 3, background: "#1a2030", marginTop: 6, overflow: "visible" }}>
                    <div style={{ position: "absolute", left: "50%", top: 0, width: 1, height: 6, background: "#2d3f52" }} />
                    <div style={{ position: "absolute", left: `${pct}%`, top: -2, width: 10, height: 10, borderRadius: "50%", background: c, transform: "translateX(-5px)", border: "2px solid #0a0e14" }} />
                  </div>
                );
              };
              return (
                <div>
                  {/* Composite Score Banner */}
                  <div style={{ background: "#0a0e14", border: `1px solid ${dirColor}30`, borderRadius: 8, padding: "20px 24px", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>SQUEEZE ANALYSIS</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 28, fontWeight: 800, color: dirColor, fontFamily: "'Space Mono', monospace" }}>
                            {score > 0 ? "+" : ""}{score}
                          </span>
                          <span style={{ padding: "4px 14px", borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                            background: dirColor + "18", color: dirColor, border: `1px solid ${dirColor}40`
                          }}>{d.direction}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#4a5568", maxWidth: 340 }}>{d.interpretation}</div>
                        <div style={{ fontSize: 10, color: "#718096", marginTop: 4 }}>
                          {d.positions_with_liqpx.toLocaleString()} positions with liqPx / {d.total_positions.toLocaleString()} total ({d.filtered_dust.toLocaleString()} dust filtered)
                        </div>
                      </div>
                    </div>
                    {/* Full-width gauge bar */}
                    <div style={{ position: "relative", height: 10, borderRadius: 5, overflow: "visible" }}>
                      <div style={{ position: "absolute", inset: 0, borderRadius: 5, background: "linear-gradient(to right, #ff6b6b, #ff6b6b40 35%, #1a2030 45%, #1a2030 55%, #4dff9140 65%, #4dff91)" }} />
                      <div style={{ position: "absolute", left: "50%", top: -2, width: 2, height: 14, background: "#2d3f52", borderRadius: 1 }} />
                      <div style={{ position: "absolute", left: `${gaugePct}%`, top: -4, width: 18, height: 18, borderRadius: "50%",
                        background: dirColor, border: "3px solid #0a0e14", transform: "translateX(-9px)",
                        boxShadow: `0 0 8px ${dirColor}60`
                      }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                      <span style={{ fontSize: 9, color: "#ff6b6b", letterSpacing: 1 }}>LONG SQUEEZE</span>
                      <span style={{ fontSize: 9, color: "#718096" }}>BALANCED</span>
                      <span style={{ fontSize: 9, color: "#4dff91", letterSpacing: 1 }}>SHORT SQUEEZE</span>
                    </div>
                  </div>

                  {/* Signal Bar */}
                  {d.signal && (() => {
                    const sig = d.signal;
                    const alertColors = { NONE: "#4a5568", WATCH: "#f6ad55", ALERT: "#fc8181", STRONG: "#ff6b6b" };
                    const alertBg = { NONE: "rgba(74,85,104,0.1)", WATCH: "rgba(246,173,85,0.12)", ALERT: "rgba(252,129,129,0.12)", STRONG: "rgba(255,107,107,0.15)" };
                    const ac = alertColors[sig.alert_level] || "#4a5568";
                    return (
                      <div style={{ background: "#0a0e14", border: `1px solid ${ac}40`, borderRadius: 6, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>SIGNAL</span>
                          <span style={{ padding: "3px 10px", borderRadius: 3, fontSize: 11, fontWeight: 700, letterSpacing: 1,
                            background: alertBg[sig.alert_level], color: ac, border: `1px solid ${ac}40`
                          }}>{sig.direction.replace(/_/g, " ")}</span>
                          <span style={{ padding: "3px 8px", borderRadius: 3, fontSize: 10, fontWeight: 600, letterSpacing: 1,
                            background: alertBg[sig.alert_level], color: ac
                          }}>{sig.alert_level}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 9, color: "#718096", marginBottom: 2 }}>CONFIDENCE</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: ac, fontFamily: "'Space Mono', monospace" }}>{sig.confidence}%</div>
                          </div>
                          <div style={{ width: 80, height: 6, borderRadius: 3, background: "#1a2030", overflow: "hidden" }}>
                            <div style={{ width: `${sig.confidence}%`, height: "100%", borderRadius: 3, background: ac, transition: "width 0.3s" }} />
                          </div>
                          <div style={{ fontSize: 9, color: "#4a5568" }}>
                            mag:{sig.magnitude_conf} agr:{sig.agreement_conf}{sig.conc_penalty > 0 ? ` pen:-${sig.conc_penalty}` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 4 Sub-Metric Cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
                    {/* PROXIMITY (30%) */}
                    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>PROXIMITY</div>
                        <span style={{ fontSize: 9, color: "#4a5568" }}>{d.dynamic_weights ? Math.round(d.dynamic_weights.proximity * 100) + "%" : "30%"}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: d.proximity.score <= -10 ? "#ff6b6b" : d.proximity.score >= 10 ? "#4dff91" : "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                        {d.proximity.score > 0 ? "+" : ""}{d.proximity.score}
                      </div>
                      {miniBar(d.proximity.score)}
                      <div style={{ marginTop: 10, fontSize: 10, color: "#4a5568", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 8px" }}>
                        <span>0-1%: {fmt(d.proximity.long_ntl_0_1pct)}</span><span style={{ textAlign: "right" }}>{fmt(d.proximity.short_ntl_0_1pct)}</span>
                        <span>1-3%: {fmt(d.proximity.long_ntl_1_3pct)}</span><span style={{ textAlign: "right" }}>{fmt(d.proximity.short_ntl_1_3pct)}</span>
                        <span>3-5%: {fmt(d.proximity.long_ntl_3_5pct)}</span><span style={{ textAlign: "right" }}>{fmt(d.proximity.short_ntl_3_5pct)}</span>
                        <span>5-10%: {fmt(d.proximity.long_ntl_5_10pct)}</span><span style={{ textAlign: "right" }}>{fmt(d.proximity.short_ntl_5_10pct)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#718096", marginTop: 4 }}>
                        <span style={{ color: "#4dff91" }}>LONG</span><span style={{ color: "#ff6b6b" }}>SHORT</span>
                      </div>
                      {d.proximity.concentration_warning && (
                        <div style={{ marginTop: 6, padding: "3px 6px", background: "rgba(246,173,85,0.15)", border: "1px solid rgba(246,173,85,0.3)", borderRadius: 3, fontSize: 9, color: "#f6ad55", textAlign: "center", letterSpacing: 1 }}>
                          CONCENTRATED
                        </div>
                      )}
                      {d.proximity.concentration && (
                        <div style={{ marginTop: 4, fontSize: 9, color: "#718096" }}>
                          Top whale: {d.proximity.concentration.long.top_1_pct}% of longs / {d.proximity.concentration.short.top_1_pct}% of shorts
                        </div>
                      )}
                    </div>

                    {/* CASCADE RISK (30%) */}
                    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>CASCADE RISK</div>
                        <span style={{ fontSize: 9, color: "#4a5568" }}>{d.cascade.cascade_relevance < 1 ? `rel:${d.cascade.cascade_relevance} · ` : ""}{d.dynamic_weights ? Math.round(d.dynamic_weights.cascade * 100) + "%" : "30%"}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: d.cascade.score <= -10 ? "#ff6b6b" : d.cascade.score >= 10 ? "#4dff91" : "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                        {d.cascade.score > 0 ? "+" : ""}{d.cascade.score}
                      </div>
                      {miniBar(d.cascade.score)}
                      <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                        <div>
                          <div style={{ fontSize: 9, color: "#4dff91", marginBottom: 2 }}>LONG CASCADE</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", fontFamily: "monospace" }}>{d.cascade.long_cascade_multiplier}x <span style={{ fontSize: 11, color: "#718096" }}>eff:{d.cascade.long_effective}</span></div>
                          <div style={{ fontSize: 10, color: "#4a5568" }}>{fmt(d.cascade.long_cascade_ntl)} @ {d.cascade.long_nearest_cluster_pct}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, color: "#ff6b6b", marginBottom: 2 }}>SHORT CASCADE</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", fontFamily: "monospace" }}>{d.cascade.short_cascade_multiplier}x <span style={{ fontSize: 11, color: "#718096" }}>eff:{d.cascade.short_effective}</span></div>
                          <div style={{ fontSize: 10, color: "#4a5568" }}>{fmt(d.cascade.short_cascade_ntl)} @ {d.cascade.short_nearest_cluster_pct}%</div>
                        </div>
                      </div>
                    </div>

                    {/* OI DELTA (20%) */}
                    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>OI DELTA</div>
                        <span style={{ fontSize: 9, color: "#4a5568" }}>{d.dynamic_weights ? Math.round(d.dynamic_weights.oi * 100) + "%" : "20%"}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: (d.oi_delta?.score || 0) <= -10 ? "#ff6b6b" : (d.oi_delta?.score || 0) >= 10 ? "#4dff91" : "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                        {(d.oi_delta?.score || 0) > 0 ? "+" : ""}{d.oi_delta?.score ?? 0}
                      </div>
                      {miniBar(d.oi_delta?.score || 0)}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4 }}>
                          <span style={{ color: "#718096" }}>Current OI</span>
                          <span style={{ color: "#e2e8f0" }}>{fmt(d.oi_delta?.current_oi)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4 }}>
                          <span style={{ color: "#718096" }}>Delta blend</span>
                          <span style={{ color: "#e2e8f0" }}>{d.oi_delta?.delta_blend?.toLocaleString() ?? "0"}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#718096", marginTop: 4 }}>
                          Exchanges: {d.oi_delta?.exchanges ?? 0} · ATR: {d.oi_delta?.atr_pct ?? 0}%
                        </div>
                      </div>
                    </div>

                    {/* MARGIN (15%) */}
                    <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, padding: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: "#718096", letterSpacing: 2 }}>MARGIN HEALTH</div>
                        <span style={{ fontSize: 9, color: "#4a5568" }}>{d.dynamic_weights ? Math.round(d.dynamic_weights.margin * 100) + "%" : "15%"}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: d.margin_imbalance.score <= -10 ? "#ff6b6b" : d.margin_imbalance.score >= 10 ? "#4dff91" : "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                        {d.margin_imbalance.score > 0 ? "+" : ""}{d.margin_imbalance.score}
                      </div>
                      {miniBar(d.margin_imbalance.score)}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 6 }}>
                          <span style={{ color: "#4dff91" }}>Long avg liq distance</span>
                          <span style={{ color: "#e2e8f0" }}>{d.margin_imbalance.long_avg_liq_distance_pct}%</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 8 }}>
                          <span style={{ color: "#ff6b6b" }}>Short avg liq distance</span>
                          <span style={{ color: "#e2e8f0" }}>{d.margin_imbalance.short_avg_liq_distance_pct}%</span>
                        </div>
                        {[["long", "#4dff91"], ["short", "#ff6b6b"]].map(([side, color]) => {
                          const c = d.margin_imbalance[side + "_cross"];
                          const i = d.margin_imbalance[side + "_isolated"];
                          return (
                            <div key={side} style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 9, color: color, letterSpacing: 1, marginBottom: 2 }}>{side.toUpperCase()}</div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#a0aec0" }}>
                                <span>Cross: {c.count} / {fmt(c.notional)} / {c.avg_liq_distance_pct}%</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#a0aec0" }}>
                                <span>Iso: {i.count} / {fmt(i.notional)} / {i.avg_liq_distance_pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ fontSize: 9, color: "#718096", marginTop: 2 }}>
                          Smaller distance = closer to liquidation
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

        {/* 4H Market Structure panel — higher timeframe context first */}
        {ms4hData && (() => {
          const m = ms4hData;
          const green = "#4dff91", red = "#ff6b6b", gray = "#718096", mgray = "#4a5568";
          const d4 = m.ms_direction || "NO_BOS";
          const dc = d4 === "UP" ? green : d4 === "DOWN" ? red : gray;
          return (
            <div style={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 8, padding: "10px 16px", marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: "#718096", letterSpacing: 2 }}>4H STRUCTURE</span>
                  <span style={{ color: dc, fontWeight: 700, fontSize: 12 }}>{d4 === "UP" ? "\u2191 UP" : d4 === "DOWN" ? "\u2193 DOWN" : "NO BOS"}</span>
                  {m.structure_event && m.structure_event !== "NONE" && <span style={{ fontSize: 9, color: mgray }}>{m.structure_event.replace("_", " ")}</span>}
                  {m.bos_price && <span style={{ fontSize: 9, color: mgray, fontFamily: "'Space Mono', monospace" }}>@ ${m.bos_price?.toLocaleString(undefined,{maximumFractionDigits:2})}</span>}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {m.confirmed_protected_price != null && <span style={{ fontSize: 9, color: mgray, fontFamily: "'Space Mono', monospace" }}>prot ${m.confirmed_protected_price?.toLocaleString(undefined,{maximumFractionDigits:2})}</span>}
                  {msData && (() => {
                    const d30 = msData.ms_direction;
                    if (d30 === "NO_BOS" || d4 === "NO_BOS" || !d30 || !d4) return null;
                    const aligned = d30 === d4;
                    return <span style={{ fontSize: 9, fontWeight: 700, color: aligned ? "#4dff91" : "#f6ad55" }}>
                      {aligned ? "ALIGNED \u2713" : "CONFLICTING \u26a1"}
                    </span>;
                  })()}
                </div>
              </div>
              {m.structure_4h_internal && (() => {
                const s4i = m.structure_4h_internal;
                const fmtLvl = (v) => "$" + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                return (
                  <div style={{ marginTop: 4, paddingLeft: 20, fontSize: 9, display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ color: "#718096" }}>4H INT:</span>
                    <span style={{ color: s4i.ms_direction === "UP" ? green : s4i.ms_direction === "DOWN" ? red : gray, fontWeight: 600 }}>
                      {s4i.ms_direction === "UP" ? "\u2191 UP" : s4i.ms_direction === "DOWN" ? "\u2193 DOWN" : "NEUTRAL"}
                    </span>
                    {s4i.confirmed_protected_price != null && <span style={{ color: "#718096", fontFamily: "'Space Mono', monospace" }}>prot {fmtLvl(s4i.confirmed_protected_price)}</span>}
                    {s4i.last_structural_extreme_price != null && <span style={{ color: "#4a5568", fontFamily: "'Space Mono', monospace" }}>ext {fmtLvl(s4i.last_structural_extreme_price)}</span>}
                    {s4i.against_4h_external && <span style={{ color: "#f6ad55", fontSize: 8 }}>{"\u26a0"} AGAINST EXT</span>}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Market Structure panel */}
        {(() => {
          const ms = msData;
          if (!ms) return null;
          const green = "#4dff91", red = "#ff6b6b", yellow = "#f6ad55", gray = "#718096", mgray = "#4a5568";
          const dir = ms.ms_direction || (ms.ms_bias === "bullish" ? "UP" : ms.ms_bias === "bearish" ? "DOWN" : "NO_BOS");
          const biasColor = dir === "UP" ? green : dir === "DOWN" ? red : gray;
          const phase = (() => {
            if (ms.otf_broken) return { text: ms.otf_direction === "up" ? "BROKEN UP" : "BROKEN DOWN", color: ms.entry_signal ? green : red };
            if (ms.otf_active) return { text: `FORMING ${(ms.otf_active_direction||"").toUpperCase()} (${ms.otf_active_candle_count}c)`, color: yellow };
            return { text: "NONE", color: gray };
          })();
          const sweep = (() => {
            if (ms.otf_broken) {
              if (ms.otf_sweep_validated) return { text: `SWEPT @ $${ms.otf_sweep_price?.toLocaleString(undefined,{maximumFractionDigits:2})||"\u2014"}`, color: green };
              return { text: "FAILED", color: red };
            }
            if (ms.otf_active) {
              if (ms.otf_active_internal_sweep) return { text: `SWEPT @ $${ms.otf_active_sweep_price?.toLocaleString(undefined,{maximumFractionDigits:2})||"\u2014"}`, color: green };
              const sc = ms.otf_active_internal_swings?.length || 0;
              if (sc > 0) return { text: `${sc} swings tracked`, color: yellow };
              return { text: "\u2014", color: gray };
            }
            return { text: "\u2014", color: gray };
          })();
          const gate = (() => {
            if (ms.entry_signal) return { text: "READY", color: green };
            if (ms.otf_broken && ms.entry_blocked_reason) return { text: "BLOCKED", color: red };
            if (ms.otf_active) return { text: "WAITING", color: yellow };
            return { text: "\u2014", color: gray };
          })();
          const swings = ms.otf_active ? ms.otf_active_internal_swings : ms.otf_internal_swings;
          const nextLevel = ms.otf_next_sweep_level;
          const steps = [
            { label: "30m BOS", lit: dir !== "NO_BOS" },
            { label: "5m OTF", lit: ms.otf_broken || ms.otf_active },
            { label: "Sweep", lit: ms.otf_sweep_validated || ms.otf_active_internal_sweep },
            { label: "Entry", lit: !!ms.entry_signal },
          ];
          const blockedText = { no_internal_sweep: "OTF broke without sweeping an internal swing", otf_too_short: "OTF lasted fewer than 4 candles" };
          return (
            <div style={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: "#a0aec0", letterSpacing: 2 }}>MARKET STRUCTURE</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {ms.entry_signal && <span style={{ fontSize: 12, fontWeight: 700, color: ms.entry_signal === "LONG" ? green : red }}>{ms.entry_signal}</span>}
                  {ms.entry_signal && <span style={{ fontSize: 10, color: "#a0aec0" }}>conf {ms.confidence_score}</span>}
                  {!ms.entry_signal && <span style={{ fontSize: 10, color: gray }}>{"\u2014"}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 10 }}>
                {steps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <div style={{ width: 16, height: 1, background: s.lit ? green : "#1a2030" }} />}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.lit ? green : "#1a2030", border: `1px solid ${s.lit ? green : "#2d3748"}` }} />
                      <span style={{ fontSize: 8, color: s.lit ? "#e2e8f0" : mgray, letterSpacing: 1 }}>{s.label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, fontSize: 10 }}>
                <div>
                  <div style={{ color: gray, fontSize: 9, marginBottom: 2 }}>30m Structure</div>
                  <div style={{ color: biasColor, fontWeight: 700 }}>{dir === "UP" ? "\u2191 UP" : dir === "DOWN" ? "\u2193 DOWN" : "NO BOS"}</div>
                  {ms.structure_event && ms.structure_event !== "NONE" && <div style={{ color: mgray, fontSize: 9 }}>{ms.structure_event.replace("_", " ")}</div>}
                  {ms.bos_price && <div style={{ color: mgray, fontSize: 9 }}>BOS @ ${ms.bos_price?.toLocaleString(undefined,{maximumFractionDigits:2})}</div>}
                </div>
                <div>
                  <div style={{ color: gray, fontSize: 9, marginBottom: 2 }}>5m Phase</div>
                  <div style={{ color: phase.color, fontWeight: 600 }}>{phase.text}</div>
                </div>
                <div>
                  <div style={{ color: gray, fontSize: 9, marginBottom: 2 }}>Internal Sweep</div>
                  <div style={{ color: sweep.color, fontWeight: 600, fontFamily: "'Space Mono', monospace" }}>{sweep.text}</div>
                </div>
                <div>
                  <div style={{ color: gray, fontSize: 9, marginBottom: 2 }}>Gate</div>
                  <div style={{ color: gate.color, fontWeight: 700 }}>{gate.text}</div>
                </div>
              </div>
              {ms.confirmed_protected_price != null && (() => {
                const protPrice = ms.confirmed_protected_price;
                const protType = ms.confirmed_protected_type;
                const pendPrice = ms.pending_origin_price;
                const pendType = ms.pending_origin_type;
                const extreme = ms.last_structural_extreme_price;
                const isUp = dir === "UP";
                const fmtLvl = (v) => "$" + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                const s30i = ms.structure_30m_internal;
                return (
                  <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 4, background: "#0a0e14", border: "1px solid #1a2030" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 9 }}>
                      <span>
                        <span style={{ color: gray }}>Protected: </span>
                        <span style={{ color: "#e2e8f0", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{fmtLvl(protPrice)}</span>
                        <span style={{ color: mgray }}> ({protType === "origin_low" ? "origin low" : "origin high"})</span>
                      </span>
                      <span style={{ color: isUp ? red : green, fontSize: 8 }}>
                        BOS flip {"\u2192"} {isUp ? "DOWN" : "UP"} {isUp ? "below" : "above"} {fmtLvl(protPrice)}
                      </span>
                    </div>
                    {pendPrice != null && (
                      <div style={{ fontSize: 9, marginTop: 2 }}>
                        <span style={{ color: gray }}>Pending: </span>
                        <span style={{ color: yellow, fontFamily: "'Space Mono', monospace" }}>{fmtLvl(pendPrice)}</span>
                        <span style={{ color: mgray }}> ({pendType === "candidate_low" ? "candidate low" : "candidate high"})</span>
                      </div>
                    )}
                    {s30i && (
                      <div style={{ marginTop: 4, paddingLeft: 20, fontSize: 9, display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: "#718096" }}>30m INT:</span>
                        <span style={{ color: s30i.ms_direction === "UP" ? green : s30i.ms_direction === "DOWN" ? red : gray, fontWeight: 600 }}>
                          {s30i.ms_direction === "UP" ? "\u2191 UP" : s30i.ms_direction === "DOWN" ? "\u2193 DOWN" : "NEUTRAL"}
                        </span>
                        {s30i.confirmed_protected_price != null && <span style={{ color: "#718096", fontFamily: "'Space Mono', monospace" }}>prot {fmtLvl(s30i.confirmed_protected_price)}</span>}
                        {s30i.last_structural_extreme_price != null && <span style={{ color: "#4a5568", fontFamily: "'Space Mono', monospace" }}>ext {fmtLvl(s30i.last_structural_extreme_price)}</span>}
                        {s30i.against_30m_external && <span style={{ color: yellow, fontSize: 8 }}>{"\u26a0"} AGAINST EXT</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
              {(swings?.length > 0 || ms.entry_blocked_reason) && (
                <div style={{ marginTop: 8 }}>
                  {swings?.length > 0 && (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ fontSize: 8, color: gray, letterSpacing: 1, marginBottom: 3 }}>
                        INTERNAL SWINGS ({swings.filter(s => typeof s === "object" ? s.swept : false).length}/{swings.length} swept)
                      </div>
                      {swings.map((s, i) => {
                        const isObj = typeof s === "object" && s !== null;
                        const price = isObj ? s.price : s;
                        const swept = isObj ? s.swept : false;
                        const isNext = nextLevel != null && price === nextLevel && !swept;
                        const ts = isObj ? s.ts : null;
                        const fmtTs = ts ? new Date(typeof ts === "number" && ts > 1e12 ? ts : (typeof ts === "number" ? ts * 1000 : parseInt(ts))).toLocaleString("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hour12: false }) : "";
                        const color = swept ? green : isNext ? yellow : "#a0aec0";
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, fontFamily: "'Space Mono', monospace", marginBottom: 1 }}>
                            <span style={{ color, fontWeight: isNext ? 700 : 400 }}>
                              ${price?.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                            {swept && <span style={{ color: green, fontSize: 8 }}>{"\u2713"}</span>}
                            {isNext && <span style={{ color: yellow, fontSize: 8 }}>{"\u2190"} next</span>}
                            {fmtTs && <span style={{ color: mgray, fontSize: 8 }}>@ {fmtTs}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {ms.entry_blocked_reason && <span style={{ fontSize: 9, color: red }}>{blockedText[ms.entry_blocked_reason] || ms.entry_blocked_reason}</span>}
                  {ms.otf_broken && !ms.entry_blocked_reason && ms.otf_sweep_validated && (
                    <div style={{ fontSize: 9, color: green }}>Internal sweep: YES at ${ms.otf_sweep_price?.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                  )}
                  {ms.otf_broken && !ms.otf_sweep_validated && !ms.entry_blocked_reason && (
                    <div style={{ fontSize: 9, color: red }}>Internal sweep: FAILED — no internal swing was swept</div>
                  )}
                </div>
              )}
              {ms.sfp_detected && (
                <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 4, background: "#f6ad5515", borderLeft: "3px solid #f6ad55" }}>
                  <span style={{ color: "#f6ad55", fontWeight: 700, fontSize: 11 }}>
                    {"\u26a0"} {ms.sfp_type?.replace("_", " ")} @ ${ms.sfp_level?.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </span>
                </div>
              )}
              {ms.structure_5m && (() => {
                const s5 = ms.structure_5m;
                const s5Dir = s5.ms_direction;
                const s5Color = s5Dir === "UP" ? green : s5Dir === "DOWN" ? red : gray;
                const s5Prot = s5.confirmed_protected_price;
                const s5Ext = s5.last_structural_extreme_price;
                const s5Pend = s5.pending_origin_price;
                const s5Int = s5.internal;
                const against = s5.against_30m;
                const fmtLvl = (v) => "$" + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                return (
                  <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 4, background: "#0a0e14", border: `1px solid ${against ? "#744210" : "#1a2030"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 9, color: "#a0aec0", letterSpacing: 1 }}>5m Structure</span>
                        <span style={{ fontSize: 10, color: s5Color, fontWeight: 700 }}>{s5Dir === "UP" ? "\u2191 UP" : s5Dir === "DOWN" ? "\u2193 DOWN" : "NEUTRAL"}</span>
                      </div>
                      {against ? <span style={{ fontSize: 8, color: yellow }}>{"\u26a0"} against 30m</span> : <span style={{ fontSize: 8, color: green }}>aligned {"\u2713"}</span>}
                    </div>
                    <div style={{ fontSize: 9, fontFamily: "'Space Mono', monospace" }}>
                      {s5Prot != null && (
                        <div>
                          <span style={{ color: gray }}>Protected: </span>
                          <span style={{ color: "#63b3ed", fontWeight: 600 }}>{fmtLvl(s5Prot)}</span>
                          <span style={{ color: mgray }}> ({s5.confirmed_protected_type === "origin_low" ? "origin low" : "origin high"})</span>
                        </div>
                      )}
                      {s5Ext != null && (
                        <div>
                          <span style={{ color: gray }}>Extreme: </span>
                          <span style={{ color: mgray }}>{fmtLvl(s5Ext)}</span>
                        </div>
                      )}
                      {s5Pend != null && (
                        <div>
                          <span style={{ color: gray }}>Pending: </span>
                          <span style={{ color: yellow }}>{fmtLvl(s5Pend)}</span>
                        </div>
                      )}
                    </div>
                    {s5Int && (
                      <div style={{ marginTop: 4, fontSize: 9, display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: gray }}>5m Internal:</span>
                        <span style={{ color: s5Int.ms_direction === "UP" ? green : s5Int.ms_direction === "DOWN" ? red : gray, fontWeight: 600 }}>
                          {s5Int.ms_direction === "UP" ? "\u2191 UP" : s5Int.ms_direction === "DOWN" ? "\u2193 DOWN" : "NEUTRAL"}
                        </span>
                        {s5Int.confirmed_protected_price != null && (
                          <span style={{ color: mgray, fontFamily: "'Space Mono', monospace" }}>protected {fmtLvl(s5Int.confirmed_protected_price)}</span>
                        )}
                      </div>
                    )}
                    {s5.choch && s5.choch.state !== "none" && (() => {
                      const ch = s5.choch;
                      const cur = ch.current;
                      const lf = ch.last_fired;
                      const fmtChTs = (ms) => { if (!ms) return ""; const d = new Date(ms); return d.toLocaleTimeString("en-US", {hour:"2-digit",minute:"2-digit",hour12:false}); };
                      return (
                        <>
                          <div style={{ marginTop: 4, fontSize: 9, display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ color: gray }}>CHOCH:</span>
                            {ch.state === "watching" && cur && (
                              <>
                                <span style={{ color: yellow }}>watching {cur.direction === "CHOCH_DOWN" ? "\u2193" : "\u2191"}</span>
                                <span style={{ color: yellow, fontFamily: "'Space Mono', monospace" }}>{fmtLvl(cur.level)}</span>
                              </>
                            )}
                            {ch.state === "detected" && cur && (
                              <>
                                <span style={{ color: cur.direction === "CHOCH_UP" ? green : red, fontWeight: 700 }}>
                                  {cur.direction === "CHOCH_UP" ? "\u2191 CHOCH_UP" : "\u2193 CHOCH_DOWN"}
                                </span>
                                <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>@ {fmtLvl(cur.level)}</span>
                                <span style={{ color: mgray, fontSize: 8 }}>({cur.sweep_type}{cur.closed_through ? " + closed" : ""})</span>
                              </>
                            )}
                          </div>
                          {lf && lf !== cur && (
                            <div style={{ fontSize: 8, color: mgray, marginLeft: 50 }}>
                              last: {lf.direction === "CHOCH_UP" ? "\u2191" : "\u2193"} @ {fmtLvl(lf.level)} {fmtChTs(lf.sweep_ts)}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                );
              })()}
              {(() => {
                const levels = [];
                const fmtLvl = (v) => "$" + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                if (ms.confirmed_protected_price != null) levels.push({ price: ms.confirmed_protected_price, label: "30m protected", type: ms.confirmed_protected_type === "origin_low" ? "strong low" : "strong high", color: "#e2e8f0", bold: true });
                if (ms.pending_origin_price != null) levels.push({ price: ms.pending_origin_price, label: "30m pending", type: ms.pending_origin_type === "candidate_low" ? "candidate low" : "candidate high", color: yellow, bold: false });
                if (ms.last_structural_extreme_price != null) levels.push({ price: ms.last_structural_extreme_price, label: "30m extreme", type: dir === "UP" ? "weak high" : "weak low", color: mgray, bold: false });
                const s5 = ms.structure_5m;
                const s30i = ms.structure_30m_internal;
                if (s30i?.confirmed_protected_price != null) levels.push({ price: s30i.confirmed_protected_price, label: "30m int prot", type: s30i.confirmed_protected_type === "origin_low" ? "strong low" : "strong high", color: "#a0aec0", bold: false });
                if (s5?.confirmed_protected_price != null) levels.push({ price: s5.confirmed_protected_price, label: "5m protected", type: s5.confirmed_protected_type === "origin_low" ? "strong low" : "strong high", color: "#63b3ed", bold: false });
                if (s5?.last_structural_extreme_price != null) levels.push({ price: s5.last_structural_extreme_price, label: "5m extreme", type: s5.ms_direction === "UP" ? "weak high" : "weak low", color: "#4a5568", bold: false });
                if (levels.length === 0) return null;
                levels.sort((a, b) => b.price - a.price);
                return (
                  <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 4, background: "#0a0e14", border: "1px solid #1a2030" }}>
                    <div style={{ fontSize: 8, color: gray, letterSpacing: 1, marginBottom: 4 }}>STRUCTURAL LEVELS</div>
                    {levels.map((l, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 9, fontFamily: "'Space Mono', monospace", marginBottom: 1 }}>
                        <span style={{ color: l.color, fontWeight: l.bold ? 700 : 400, minWidth: 90, textAlign: "right" }}>{fmtLvl(l.price)}</span>
                        <span style={{ color: l.color, fontWeight: l.bold ? 600 : 400, fontSize: 8 }}>{l.label}</span>
                        <span style={{ color: mgray, fontSize: 8 }}>({l.type})</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Entry Confidence panel */}
        {(() => {
          const ec = msData?.entry_confidence;
          if (!ec) return null;
          const vColors = { GO: { bg: "#0d2818", border: "#4dff91", text: "#4dff91" }, CAUTION: { bg: "#2d1f0d", border: "#EF9F27", text: "#EF9F27" }, NO_GO: { bg: "#0d1117", border: "#4a5568", text: "#888780" } };
          const vc = vColors[ec.verdict] || vColors.NO_GO;
          const pathColors = { CLEAR: "#4dff91", PARTIALLY_BLOCKED: "#EF9F27", BLOCKED: "#E24B4A", UNKNOWN: "#888780" };
          const evColor = ec.ev_pct > 0.2 ? "#4dff91" : ec.ev_pct > 0 ? "#EF9F27" : "#ff6b6b";
          return (
            <div style={{ background: vc.bg, border: `1px solid ${vc.border}`, borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: vc.text, letterSpacing: 2 }}>{ec.verdict.replace("_", " ")}</span>
                  {ec.ev_pct != null && <span style={{ fontSize: 13, fontWeight: 700, color: evColor, fontFamily: "'Space Mono', monospace" }}>EV {ec.ev_pct > 0 ? "+" : ""}{ec.ev_pct.toFixed(2)}%</span>}
                  {ec.rr != null && <span style={{ fontSize: 11, color: "#a0aec0" }}>RR {ec.rr.toFixed(1)}:1</span>}
                </div>
                <span style={{ fontSize: 9, color: "#4a5568", letterSpacing: 2 }}>ENTRY CONFIDENCE</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 9, color: "#718096", marginBottom: 6, fontFamily: "'Space Mono', monospace" }}>
                {ec.p_touch != null && <span>P(touch) {(ec.p_touch * 100).toFixed(0)}%</span>}
                {ec.p_follow != null && <span>P(follow) {(ec.p_follow * 100).toFixed(0)}%</span>}
                {ec.target_depth != null && <span>target {ec.target_depth.toFixed(1)}%</span>}
                {ec.stop_pct != null && <span>stop {ec.stop_pct.toFixed(1)}%</span>}
                {ec.is_top_setup && <span style={{ color: "#4dff91", fontWeight: 700 }}>TOP SETUP</span>}
              </div>
              <div style={{ display: "flex", gap: 10, fontSize: 9, color: "#718096", marginBottom: 4, fontFamily: "'Space Mono', monospace", flexWrap: "wrap" }}>
                {ec.oi_regime && ec.oi_regime !== "NEUTRAL" && <span style={{ padding: "1px 4px", borderRadius: 3, background: "#21262d", color: ec.oi_regime === "BUILD_LONGS" || ec.oi_regime === "SHORT_COVER" ? "#3fb950" : ec.oi_regime === "BUILD_SHORTS" || ec.oi_regime === "LONG_FLUSH" ? "#f85149" : "#d29922" }}>{ec.oi_regime.replace(/_/g, " ")}</span>}
                {ec.oi_regime_mod != null && ec.oi_regime_mod !== 1.0 && <span>OI {ec.oi_regime_mod.toFixed(2)}x</span>}
                {ec.liq_touch_mod != null && ec.liq_touch_mod !== 1.0 && <span>liq-t {ec.liq_touch_mod.toFixed(2)}x</span>}
                {ec.liq_follow_mod != null && ec.liq_follow_mod !== 1.0 && <span>liq-f {ec.liq_follow_mod.toFixed(2)}x</span>}
                {ec.crowding_mod != null && ec.crowding_mod !== 1.0 && <span>crowd {ec.crowding_mod.toFixed(2)}x</span>}
                {ec.crowding_mod === 1.0 && ec.crowding_mod_raw != null && ec.crowding_mod_raw !== 1.0 && <span style={{ color: "#484f58" }}>crowd {ec.crowding_mod_raw.toFixed(2)}x (off)</span>}
                {ec.retail_long != null && <span>retail {(ec.retail_long * 100).toFixed(0)}%L</span>}
                {ec.whale_position_long != null && <span>whale {(ec.whale_position_long * 100).toFixed(0)}%L</span>}
                {ec.funding_spread_alert && <span style={{ color: "#f0883e" }}>{"\u26a0"} funding spread</span>}
              </div>
              <div style={{ fontSize: 9, color: "#a0aec0", marginBottom: 6, fontFamily: "'Space Mono', monospace" }}>{ec.reason}</div>
              <div style={{ display: "flex", gap: 12, fontSize: 9, color: "#718096" }}>
                <span>path: <span style={{ color: pathColors[ec.path_status] || "#888" }}>{ec.path_status}</span></span>
                <span>{ec.direction_aligned ? `aligned ${ec.direction_30m}` : `30m=${ec.direction_30m} 5m=${ec.direction_5m}`}</span>
                {ec.direction_4h && ec.direction_4h !== "NO_BOS" && <span style={{ color: ec.direction_4h === ec.direction_30m ? "#4dff91" : "#E24B4A" }}>4H {ec.direction_4h === ec.direction_30m ? "confirms" : "OPPOSED"}</span>}
                {ec.structural_walls_in_path?.length > 0 && <span>{ec.structural_walls_in_path.length} walls (${(ec.total_wall_notional / 1e6).toFixed(1)}M)</span>}
              </div>
            </div>
          );
        })()}

        {/* Structural-Proximity Linkage panel */}
        {(() => {
          const sp = msData?.structural_proximity;
          if (!sp || sp.length === 0) return null;
          const confColors = { EXTREME: "#E24B4A", HIGH: "#EF9F27", MODERATE: "#F0997B", LOW: "#888780" };
          const fmtLvl = (v) => v != null ? "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";
          return (
            <div style={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: "#a0aec0", letterSpacing: 2 }}>STRUCTURAL-PROXIMITY</span>
                <span style={{ fontSize: 9, color: "#4a5568" }}>
                  {sp.length} levels linked
                  {sp[0]?.scenario_snapshot_age_s != null && (
                    <span style={{ fontSize: 8, color: '#4a5568', marginLeft: 4 }}>
                      data {Math.round(sp[0].scenario_snapshot_age_s)}s ago
                    </span>
                  )}
                </span>
              </div>
              {sp.map((r, i) => {
                const cc = confColors[r.confluence] || confColors.LOW;
                const appColors = { IMPULSE: "#E24B4A", ACCELERATING: "#EF9F27", APPROACHING: "#F0997B", DRIFTING: "#888780", RETREATING: "#4a5568", NEUTRAL: "#888780" };
                const ac = appColors[r.approach_state] || appColors.DRIFTING;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4, fontSize: 10, fontFamily: "'Space Mono', monospace", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: `${cc}20`, color: cc, fontWeight: 600, minWidth: 56, textAlign: "center" }}>{r.confluence}</span>
                    <span style={{ color: "#a0aec0", minWidth: 90, fontSize: 9 }}>{r.name.replace(/_/g, " ")}</span>
                    <span style={{ color: "#e2e8f0", minWidth: 78, textAlign: "right" }}>{fmtLvl(r.price)}</span>
                    <span style={{ color: r.distance_pct >= 0 ? "#4dff91" : "#ff6b6b", minWidth: 48, textAlign: "right", fontSize: 9 }}>{r.distance_pct >= 0 ? "+" : ""}{r.distance_pct.toFixed(2)}%</span>
                    <span style={{ color: "#718096", minWidth: 55, textAlign: "right", fontSize: 9 }}>+${(r.incremental_liq_notional / 1e6).toFixed(1)}M</span>
                    {r.approach_state && (
                      <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 3, background: `${ac}18`, color: ac, fontWeight: 600 }}>{r.approach_state}</span>
                    )}
                    {r.time_to_level_min != null && (
                      <span style={{ fontSize: 8, color: r.time_to_level_min < 30 ? "#EF9F27" : "#4a5568" }}>~{r.time_to_level_min < 60 ? Math.round(r.time_to_level_min) + "m" : Math.round(r.time_to_level_min / 60) + "h"}</span>
                    )}
                    <span style={{ fontSize: 8, color: "#4a5568" }}>{r.alignment === "LONG_LIQ" ? "\u2193 long liq" : "\u2191 short liq"}</span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* CVD Divergence — multi-pivot panel */}
        {(() => {
          const divNew = msData?.cvd_divergence_new;
          if (!divNew || Object.keys(divNew).length === 0) return null;
          const bullColor = "#4dff91", bearColor = "#ff6b6b", devColor = "#EF9F27", unreliColor = "#888780", noneColor = "#4a5568";
          const toUTC7 = (ts) => { if (!ts) return ""; const d = new Date(typeof ts === "number" && ts > 1e12 ? ts : ts * 1000); return d.toLocaleString("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hour12: false, month: "2-digit", day: "2-digit" }); };
          const fmtP = (v) => v != null ? "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";
          const divColor = (type) => type === "bullish" ? bullColor : type === "bearish" ? bearColor : noneColor;
          const statusColor = (s) => s === "CONFIRMED" ? bullColor : s === "DEVELOPING" ? devColor : s === "UNRELIABLE" ? unreliColor : noneColor;
          return (
            <div style={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: "#a0aec0", letterSpacing: 2 }}>CVD DIVERGENCE</span>
              </div>
              {["5m", "30m", "4h"].map(tf => {
                const state = divNew[tf];
                if (!state) return null;
                const strongest = state.strongest;
                const live = state.live || {};
                const divs = state.confirmed_divergences || [];
                const pivotCount = state.internal_pivot_count || 0;
                const hasSignal = strongest || live.status === "DEVELOPING";
                return (
                  <div key={tf} style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 4, background: hasSignal ? "#0a0e14" : "transparent", border: hasSignal ? "1px solid #1a2030" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#a0aec0", minWidth: 28 }}>{tf.toUpperCase()}</span>
                      {strongest ? (
                        <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: `${statusColor(strongest.status)}18`, color: statusColor(strongest.status), fontWeight: 600 }}>
                          {strongest.status} {strongest.type?.toUpperCase()}
                        </span>
                      ) : (
                        <span style={{ fontSize: 8, color: noneColor }}>no divergence</span>
                      )}
                      {strongest?.cvd_delta != null && <span style={{ fontSize: 9, color: divColor(strongest.type), fontFamily: "'Space Mono', monospace" }}>{"\u0394"}CVD {strongest.cvd_delta > 0 ? "+" : ""}{strongest.cvd_delta.toFixed(1)}</span>}
                      <span style={{ fontSize: 7, color: "#4a5568", marginLeft: "auto" }}>{pivotCount} pivots</span>
                    </div>
                    {live.status === "DEVELOPING" && (
                      <div style={{ fontSize: 9, fontFamily: "'Space Mono', monospace", color: devColor, marginBottom: 2, paddingLeft: 36 }}>
                        DEVELOPING {live.direction} — vs {fmtP(live.comparing_to?.price)} @ {toUTC7(live.comparing_to?.ts)} | {"\u0394"}CVD {live.cvd_delta > 0 ? "+" : ""}{live.cvd_delta?.toFixed(1)}
                      </div>
                    )}
                    {divs.length > 0 && divs.map((dv, i) => (
                      <div key={i} style={{ fontSize: 8, fontFamily: "'Space Mono', monospace", color: statusColor(dv.status), paddingLeft: 36, marginBottom: 1 }}>
                        {fmtP(dv.pivot_a?.price)} {"\u2192"} {fmtP(dv.pivot_b?.price)} | {"\u0394"}CVD {dv.cvd_delta > 0 ? "+" : ""}{dv.cvd_delta?.toFixed(1)} | {dv.status}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* CVD Divergence 30m panel — OLD, hidden */}
        {false && (() => {
          const div30m = msData?.cvd_divergence_30m;
          if (!div30m) return null;
          const summary = div30m.summary || {};
          const sType = summary.structural_type;
          const strength = summary.strength || "none";
          const green = "#4dff91", red = "#ff6b6b", yellow = "#f6ad55", gray = "#718096", mgray = "#4a5568", white = "#e2e8f0";
          const isBull = sType === "bullish";
          const strengthColors = { strong: isBull ? green : red, medium: isBull ? "#68d391" : "#fc8181", weak: gray };
          const strengthColor = strengthColors[strength] || gray;
          const allSources = [
            { key: "combined", label: "Combined", data: div30m.combined || {} },
            { key: "spot", label: "Spot", data: div30m.spot || {} },
            { key: "perp", label: "Perp", data: div30m.perp || {} },
          ];
          const allDisabled = allSources.every(s => s.data.status === "disabled");
          const allInsufficient = allSources.every(s => s.data.status === "insufficient_data" || s.data.status === "disabled");
          const allSuppressed = allSources.every(s => s.data.status === "suppressed_range");
          const fmtP = (v) => v != null ? "$" + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "\u2014";
          const fmtC = (v) => v != null ? v.toLocaleString(undefined, {maximumFractionDigits: 0}) : "\u2014";
          const fmtTs = (ms) => { if (!ms) return "\u2014"; const d = new Date(typeof ms === "number" && ms > 1e12 ? ms : ms * 1000); return d.toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }); };
          const fmtDir = (d) => d ? d.replace(/_/g, " ") : "\u2014";
          const fmtReason = (r) => r === "low_excursion" ? "excursion too small" : r === "expired" ? "expired" : r || "";
          const expiresIn = (() => { for (const s of allSources) { if (s.data.expires_at) return Math.max(0, Math.round((s.data.expires_at - Date.now()) / 60000)); } return null; })();
          return (
            <div style={{ background: "#0d1117", border: `1px solid ${strength !== "none" ? (isBull ? "#22543d" : "#742a2a") : "#1a2030"}`, borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: "#a0aec0", letterSpacing: 2 }}>CVD DIVERGENCE</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {strength !== "none" && <span style={{ fontSize: 12, fontWeight: 700, color: strengthColor }}>{sType?.toUpperCase()} {isBull ? "\u2197" : "\u2198"}</span>}
                  {strength !== "none" && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: `${strengthColor}15`, color: strengthColor, fontWeight: 600 }}>{strength}</span>}
                  {strength === "none" && allDisabled && <span style={{ fontSize: 10, color: mgray }}>detection disabled</span>}
                  {strength === "none" && !allDisabled && allInsufficient && <span style={{ fontSize: 10, color: mgray }}>building history (~1.5h)</span>}
                  {strength === "none" && !allDisabled && !allInsufficient && allSuppressed && <span style={{ fontSize: 10, color: "#f6ad55" }}>suppressed — market is ranging</span>}
                  {strength === "none" && !allDisabled && !allInsufficient && !allSuppressed && <span style={{ fontSize: 10, color: mgray }}>no divergence</span>}
                  {expiresIn != null && <span style={{ fontSize: 9, color: mgray }}>expires: {expiresIn}m</span>}
                </div>
              </div>
              {allSources.map(({ key, label, data }) => {
                const hasSwings = data.swing_1_price != null;
                const detected = data.status === "detected";
                const filtered = data.is_divergent && !detected;
                const noData = data.status === "insufficient_data" || data.status === "disabled" || data.status === "suppressed_range";
                const srcColor = detected ? (data.type === "bullish" ? green : red) : filtered ? yellow : mgray;
                const statusLabel = detected ? data.type?.toUpperCase() : filtered ? "FILTERED" : noData ? data.status : "NONE";
                return (
                  <div key={key} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #1a2030" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: "#a0aec0", fontWeight: 600, minWidth: 65 }}>{label}:</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: srcColor }}>{statusLabel}</span>
                      {filtered && data.filter_reason && <span style={{ fontSize: 8, color: yellow }}>({fmtReason(data.filter_reason)})</span>}
                    </div>
                    {hasSwings && (
                      <div style={{ fontSize: 9, fontFamily: "'Space Mono', monospace", marginLeft: 4 }}>
                        <div style={{ display: "flex", gap: 8, color: "#a0aec0", marginBottom: 2 }}>
                          <span style={{ color: mgray, minWidth: 50 }}>Swing 1:</span>
                          <span style={{ color: data.swing_1_price > (data.swing_2_price || 0) ? red : green }}>{fmtP(data.swing_1_price)}</span>
                          <span style={{ color: mgray }}>({data.swing_1_label || "?"})</span>
                          <span style={{ color: mgray }}>@ {fmtTs(data.swing_1_ts)}</span>
                          <span style={{ color: mgray }}>|</span>
                          <span style={{ color: white }}>CVD: {fmtC(data.swing_1_cvd)}</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, color: "#a0aec0", marginBottom: 3 }}>
                          <span style={{ color: mgray, minWidth: 50 }}>Swing 2:</span>
                          <span style={{ color: data.swing_2_price > (data.swing_1_price || 0) ? green : red }}>{fmtP(data.swing_2_price)}</span>
                          <span style={{ color: mgray }}>({data.swing_2_label || "?"})</span>
                          <span style={{ color: mgray }}>@ {fmtTs(data.swing_2_ts)}</span>
                          <span style={{ color: mgray }}>|</span>
                          <span style={{ color: white }}>CVD: {fmtC(data.swing_2_cvd)}</span>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ color: "#a0aec0" }}>Price: <span style={{ color: white }}>{fmtDir(data.price_direction)}</span></span>
                          <span style={{ color: mgray }}>|</span>
                          <span style={{ color: "#a0aec0" }}>CVD: <span style={{ color: white }}>{fmtDir(data.cvd_direction)}</span></span>
                          <span style={{ color: mgray }}>|</span>
                          <span style={{ fontWeight: 700, color: data.is_divergent ? (detected ? (data.type === "bullish" ? green : red) : yellow) : mgray }}>
                            {data.is_divergent ? "DIVERGENCE" : "NO DIVERGENCE"}
                          </span>
                        </div>
                      </div>
                    )}
                    {!hasSwings && noData && (
                      <div style={{ fontSize: 9, color: data.status === "suppressed_range" ? "#f6ad55" : mgray, marginLeft: 4 }}>{data.status === "disabled" ? "detection disabled" : data.status === "suppressed_range" ? "suppressed \u2014 30m structure is ranging" : "insufficient CVD history"}</div>
                    )}
                    {!hasSwings && !noData && (
                      <div style={{ fontSize: 9, color: mgray, marginLeft: 4 }}>no valid swing pair</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Cascade Path Quality panel — hidden, replaced by Entry Confidence */}
        {false && (() => {
          const cpq = imbalanceAnalysis?.cascade_path_quality;
          if (!cpq || cpq.status === "no_signal") return null;
          const gateColors = { PASS: "#4dff91", CAUTION: "#f6ad55", BLOCK: "#ff6b6b" };
          const stateColors = { CLEAN: "#4dff91", PARTIALLY_BLOCKED: "#f6ad55", HEAVILY_BLOCKED: "#ff6b6b", OPEN_UNVALIDATED: "#63b3ed", UNKNOWN: "#718096", NO_SIGNAL: "#4a5568" };
          const gc = gateColors[cpq.execution_gate] || "#718096";
          const sc = stateColors[cpq.path_state] || "#718096";
          const fb = cpq.first_blocker;
          const sb = cpq.strongest_blocker;
          const tw = cpq.termination_wall;
          const ls = cpq.launch_support;
          const blockers = cpq.trusted_blockers || [];
          const pSpan = cpq.target_price && cpq.current_price ? Math.abs(cpq.target_price - cpq.current_price) : 0;
          const pctPos = (p) => pSpan > 0 ? Math.min(100, Math.max(0, Math.abs(p - cpq.current_price) / pSpan * 100)) : 50;
          const formatP = (s) => s == null ? "\u2014" : s < 60 ? Math.round(s) + "s" : s < 3600 ? Math.round(s/60) + "m" : s < 86400 ? Math.round(s/3600) + "h" : Math.round(s/86400) + "d";
          return (
            <div style={{ background: "#0d1117", border: "1px solid #1a2030", borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#a0aec0", letterSpacing: 2 }}>CASCADE PATH QUALITY</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: gc }}>{cpq.path_quality_score}</span>
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: gc + "22", color: gc, fontWeight: 700 }}>{cpq.execution_gate}</span>
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: sc + "22", color: sc }}>{cpq.path_state?.replace(/_/g, " ")}</span>
                </div>
                <div style={{ fontSize: 9, color: cpq.snapshot_stale ? "#f6ad55" : "#4dff91" }}>
                  {cpq.snapshot_stale ? "\u25cb Stale" : "\u25cf Live"} | {cpq.coverage_tier || "?"} tier
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10, fontSize: 10 }}>
                <div><span style={{ color: "#718096" }}>Direction</span><br/><span style={{ color: "#e2e8f0" }}>{cpq.direction || "\u2014"}</span></div>
                <div><span style={{ color: "#718096" }}>Target</span><br/><span style={{ color: "#e2e8f0" }}>${cpq.target_price?.toLocaleString(undefined,{maximumFractionDigits:2}) || "\u2014"}</span></div>
                <div><span style={{ color: "#718096" }}>Distance</span><br/><span style={{ color: "#e2e8f0" }}>{cpq.target_distance_pct?.toFixed(2)}%</span></div>
                <div><span style={{ color: "#718096" }}>Blocker Load</span><br/><span style={{ color: cpq.blocker_load > 50 ? "#ff6b6b" : cpq.blocker_load > 25 ? "#f6ad55" : "#4dff91" }}>{cpq.blocker_load}</span></div>
              </div>
              {pSpan > 0 && (
                <div style={{ position: "relative", height: 28, background: "#141a24", borderRadius: 4, margin: "8px 0", overflow: "hidden" }}>
                  <div style={{ position: "absolute", left: 0, top: 12, width: "100%", height: 2, background: "#1a2030" }} />
                  <div style={{ position: "absolute", left: 0, top: 6, fontSize: 8, color: "#718096" }}>${cpq.current_price?.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                  <div style={{ position: "absolute", right: 0, top: 6, fontSize: 8, color: "#718096" }}>${cpq.target_price?.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                  {ls && <div title={"Support $" + ls.price?.toLocaleString()} style={{ position: "absolute", left: pctPos(ls.price) + "%", top: 8, width: 8, height: 8, borderRadius: "50%", background: "#4dff91", border: "1px solid #0d1117", transform: "translateX(-50%)" }} />}
                  {blockers.map((b, i) => <div key={i} title={"Blocker $" + b.price?.toLocaleString() + " score=" + b.blocker_score} style={{ position: "absolute", left: pctPos(b.price) + "%", top: 8, width: 8, height: 8, background: b.blocker_score >= 50 ? "#ff6b6b" : "#f6ad55", borderRadius: 2, border: "1px solid #0d1117", transform: "translateX(-50%)" }} />)}
                  {tw && <div title={"Termination $" + tw.price?.toLocaleString()} style={{ position: "absolute", left: pctPos(tw.price) + "%", top: 6, width: 0, height: 12, borderLeft: "2px solid #63b3ed", transform: "translateX(-50%)" }} />}
                </div>
              )}
              {blockers.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 9, color: "#718096", letterSpacing: 1, marginBottom: 3 }}>TOP BLOCKERS</div>
                  {blockers.slice(0, 3).map((b, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "35px 1fr 40px 45px 40px 35px", fontSize: 10, fontFamily: "'Space Mono', monospace", padding: "2px 0", borderBottom: "1px solid #0a0e14" }}>
                      <span style={{ color: b.side === "bid" ? "#4dff91" : "#ff6b6b", fontSize: 9, textTransform: "uppercase" }}>{b.side}</span>
                      <span style={{ color: "#a0aec0" }}>${b.price?.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                      <span style={{ color: "#718096", fontSize: 9 }}>{b.tier}</span>
                      <span style={{ color: b.blocker_score >= 50 ? "#ff6b6b" : "#f6ad55" }}>sc:{b.blocker_score}</span>
                      <span style={{ color: "#718096" }}>tr:{b.trust_score}</span>
                      <span style={{ color: "#4a5568", fontSize: 9 }}>{formatP(b.persistence_s)}</span>
                    </div>
                  ))}
                </div>
              )}
              {(tw || ls) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6, fontSize: 10 }}>
                  {tw && <div style={{ background: "#141a24", borderRadius: 4, padding: "4px 8px" }}>
                    <span style={{ color: "#63b3ed", fontSize: 9 }}>TERMINATION</span> ${tw.price?.toLocaleString(undefined,{maximumFractionDigits:2})} <span style={{ color: "#718096" }}>trust:{tw.trust_score}</span>
                  </div>}
                  {ls && <div style={{ background: "#141a24", borderRadius: 4, padding: "4px 8px" }}>
                    <span style={{ color: "#4dff91", fontSize: 9 }}>LAUNCH SUPPORT</span> ${ls.price?.toLocaleString(undefined,{maximumFractionDigits:2})} <span style={{ color: "#718096" }}>trust:{ls.trust_score}</span>
                  </div>}
                </div>
              )}
              {cpq.path_notes?.length > 0 && (
                <div style={{ fontSize: 9, color: "#4a5568", marginTop: 6 }}>{cpq.path_notes.join(" \u2022 ")}</div>
              )}
            </div>
          );
        })()}
          </div>
        )}

        {/* â"€â"€ SCENARIOS TAB â"€â"€ */}
        {tab === "scenario" && (scenarioData?.bands?.length || scenarioData?.scenarios) && (
          <ScenarioTab
            asset={asset}
            currentPrice={currentPrice}
            scenarioData={scenarioData}
            exposureData={exposureData}
            setLiqModal={setLiqModal}
          />
        )}
        {/* â"€â"€ CASCADE TAB â"€â"€ */}
        {tab === "cascade" && <CascadeSimulation asset={asset} assetColor={assetColor} currentPrice={currentPrice} API={API} />}

        {/* â"€â"€ HEATMAP TAB â"€â"€ */}
        {tab === "heatmap" && <HeatmapChart asset={asset} assetColor={assetColor} currentPrice={currentPrice} heatmapData={heatmapData} API={API} />}

        {/* â"€â"€ WHALES TAB â"€â"€ */}
        {tab === "whales" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 20 }}>LARGEST POSITIONS — {asset} ({whaleData?.whales?.length || 0} shown)</div>
            <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{ display: "grid", gridTemplateColumns: "40px 60px 1fr 1fr 60px 1fr 1fr 90px 110px", minWidth: 720, padding: "10px 20px", borderBottom: "1px solid #1a2030", fontSize: 10, color: "#718096", letterSpacing: 2 }}>
                <span>#</span><span>SIDE</span><span>NOTIONAL</span><span>MARGIN</span><span>LEV</span><span>ENTRY</span><span>LIQ PRICE</span><span>PROXIMITY</span><span>ADDRESS</span>
              </div>
              {(whaleData?.whales || []).map((w, i) => {
                const proximity = w.liqPx && currentPrice ? Math.abs(currentPrice - w.liqPx) / currentPrice : 999;
                const proxColor = proximity < 0.03 ? "#ff0000" : proximity < 0.05 ? "#ff4444" : proximity < 0.10 ? "#ff9900" : "#4dff91";
                const addr = w.user || "";
                const shortAddr = addr;
                return (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 60px 1fr 1fr 60px 1fr 1fr 90px 110px", minWidth: 720, padding: "10px 20px", borderBottom: "1px solid #060a0f", fontSize: 12, alignItems: "center", background: i % 2 === 0 ? "#080c12" : "transparent" }}>
                    <span style={{ color: "#718096" }}>{i + 1}</span>
                    <span style={{ color: w.side === "long" ? "#4dff91" : "#ff6b6b", fontWeight: 700, textTransform: "uppercase" }}>{w.side}</span>
                    <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{fmt(w.notional)}</span>
                    <span style={{ color: "#a0aec0" }}>{fmt(w.margin)}</span>
                    <span style={{ color: assetColor }}>{w.leverage > 0 ? w.leverage + "×" : "cross"}</span>
                    <span style={{ color: "#718096" }}>${w.entryPx?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    <span style={{ color: w.side === "long" ? "#ff6b6b" : "#4dff91" }}>{w.liqPx > 0 ? "$" + w.liqPx.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "cross"}</span>
                    <span style={{ color: proxColor, fontWeight: 700 }}>{proximity < 900 ? (proximity * 100).toFixed(1) + "%" : "—"}</span>
                    <WalletLink address={w.address} display={shortAddr} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* â"€â"€ ORDER FLOW TAB â"€â"€ */}
        {tab === "mmroster" && <TabErrorBoundary><MMRosterTab API={API} /></TabErrorBoundary>}

        {tab === "orderflow" && <TabErrorBoundary><OrderFlowTab assetColor={assetColor} API={API} /></TabErrorBoundary>}

        {/* â"€â"€ BOOKMAP TAB â"€â"€ */}
        {tab === "bookmap" && <TabErrorBoundary><BookmapTab API={API} currentPrice={currentPrice} selectedAsset={asset} /></TabErrorBoundary>}

        {tab === "engine" && <TabErrorBoundary><ExecutionEngineTab API={API} asset={asset} /></TabErrorBoundary>}

        {/* â"€â"€ PAPER TRADER TAB â"€â"€ */}
        {tab === "paper" && <TabErrorBoundary><PaperTraderTab API={API} /></TabErrorBoundary>}

        {/* â"€â"€ ALL MARKETS TAB â"€â"€ */}
        {tab === "markets" && overview && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontSize: 10, color: "#718096", letterSpacing: 2, marginBottom: 6 }}>
              ALL MARKETS — {overview.total_positions?.toLocaleString()} TOTAL POSITIONS
            </div>
            <div style={{ fontSize: 10, color: "#4a5568", marginBottom: 20 }}>
              Snapshot: {overview.snapshot_time ? new Date(overview.snapshot_time).toLocaleString() : "..."}
            </div>
            <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 6, overflow: "hidden", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 80px 1fr 1fr 1fr", minWidth: 600, padding: "10px 20px", borderBottom: "1px solid #1a2030", fontSize: 10, color: "#718096", letterSpacing: 2 }}>
                <span>COIN</span><span>PRICE</span><span>LONGS</span><span>SHORTS</span><span>LONG OI</span><span>SHORT OI</span><span>TOTAL</span>
              </div>
              {overview.coins?.slice(0, 50).map((c, i) => (
                <div key={c.coin} onClick={() => { setAsset(c.coin); setTab("overview"); }} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 80px 1fr 1fr 1fr", minWidth: 600, padding: "10px 20px", borderBottom: "1px solid #060a0f", fontSize: 12, alignItems: "center", background: i % 2 === 0 ? "#080c12" : "transparent", cursor: "pointer" }} className="btn">
                  <span style={{ color: ASSET_COLORS[c.coin] || "#4da2ff", fontWeight: 700 }}>{c.coin}</span>
                  <span style={{ color: "#a0aec0" }}>${c.price?.toLocaleString(undefined, { maximumFractionDigits: c.price > 100 ? 0 : 4 })}</span>
                  <span style={{ color: "#4dff91" }}>{c.long_count?.toLocaleString()}</span>
                  <span style={{ color: "#ff6b6b" }}>{c.short_count?.toLocaleString()}</span>
                  <span style={{ color: "#4dff91" }}>{fmt(c.long_notional)}</span>
                  <span style={{ color: "#ff6b6b" }}>{fmt(c.short_notional)}</span>
                  <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{fmt(c.total_notional)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Liquidated Positions Modal */}
      {liqModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 40, overflowY: "auto" }} onClick={() => setLiqModal(null)}>
          <div style={{ background: "#0a0e14", border: "1px solid #1a2030", borderRadius: 8, width: mob ? "100%" : "90%", maxWidth: 1100, maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: mob ? "12px 16px" : "16px 24px", borderBottom: "1px solid #1a2030", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <span style={{ color: "#e2e8f0", fontSize: mob ? 12 : 14, fontWeight: 700 }}>Liquidated Positions — {asset} @ {liqModal.pct > 0 ? "+" : ""}{liqModal.pct}%</span>
                {!mob && <span style={{ color: "#4a5568", fontSize: 12, marginLeft: 12 }}>Target: ${liqModal.target?.toLocaleString(undefined, { maximumFractionDigits: 0 })} · {liqModal.positions.length} positions</span>}
              </div>
              <span onClick={() => setLiqModal(null)} style={{ color: "#ff6b6b", cursor: "pointer", fontSize: 18, fontWeight: 700 }}>✕</span>
            </div>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <div style={{ display: "grid", gridTemplateColumns: "40px 60px 100px 100px 60px 100px 100px 100px 1fr", minWidth: 760, padding: "8px 24px", borderBottom: "1px solid #1a2030", fontSize: 10, color: "#718096", letterSpacing: 2 }}>
              <span>#</span><span>SIDE</span><span>NOTIONAL</span><span>MARGIN</span><span>LEV</span><span>ENTRY</span><span>LIQ PRICE</span><span>SIZE</span><span>ADDRESS</span>
            </div>
            {liqModal.positions.map((p, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 60px 100px 100px 60px 100px 100px 100px 1fr", minWidth: 760, padding: "8px 24px", borderBottom: "1px solid #060a0f", fontSize: 12, alignItems: "center", background: i % 2 === 0 ? "#080c12" : "transparent" }}>
                <span style={{ color: "#718096" }}>{i + 1}</span>
                <span style={{ color: p.side === "long" ? "#4dff91" : "#ff6b6b", fontWeight: 700 }}>{p.side?.toUpperCase()}</span>
                <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{fmt(p.notional)}</span>
                <span style={{ color: "#a0aec0" }}>{fmt(p.margin)}</span>
                <span style={{ color: assetColor }}>{p.leverage > 0 ? p.leverage + "×" : "cross"}</span>
                <span style={{ color: "#718096" }}>${p.entryPx?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span style={{ color: p.side === "long" ? "#ff6b6b" : "#4dff91" }}>${p.liqPx?.toLocaleString(undefined, { maximumFractionDigits: 0 })}{p.estimated_liq && <span style={{ color: "#f6ad55", fontSize: 8, fontWeight: 700, marginLeft: 3, verticalAlign: "super" }}>EST</span>}</span>
                <span style={{ color: "#a0aec0" }}>{p.size?.toFixed(4)}</span>
                <WalletLink address={p.address} display={p.user} />
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ borderTop: "1px solid #0d1117", padding: mob ? "10px 12px" : "12px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: "#4a5568", letterSpacing: 1, flexWrap: "wrap", gap: 4 }}>
        <span>HLRISK — HYPERLIQUID POSITION TRACKER</span>
        {!mob && <span>DATA SOURCE: HL NODE STATE · {overview?.total_positions?.toLocaleString() || "..."} REAL POSITIONS</span>}
        <span>UPDATED: {overview?.snapshot_time ? new Date(overview.snapshot_time).toLocaleTimeString() : "..."}</span>
      </div>
      <AlertDrawer alerts={alerts} open={alertDrawerOpen} onClose={() => setAlertDrawerOpen(false)} />
      <AlertToastLayer toasts={alertToasts} onDismiss={(id) => setAlertToasts(prev => prev.filter(t => t.id !== id))} />
    </div>
  );
}

export default function HLDashboard() {
  const [authed, setAuthed] = useState(isTokenValid());
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return <HLDashboardInner />;
}
